import { useState, useMemo, useEffect, useRef, useCallback, memo } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../lib/ThemeContext.jsx";
import {row, rowBetween, subText, inputStyle} from "../lib/styles.js";
import { LS } from "../lib/storage.js";
import { Tooltip, Info } from "../components/ui.jsx";

function getStoredBrands() {
  try {
    const raw = localStorage.getItem("sf_brands");
    if (raw) { const p = JSON.parse(raw); if (Array.isArray(p) && p.length > 0) return p; }
  } catch {}
  return [{ key:"main", label:"Brand 1" }];
}
function writeMarketplaceToBrands(mkt, brandKeys) {
  const currentBrand = localStorage.getItem("sf_brand") || "main";
  brandKeys.forEach(bk => {
    try {
      const sk = `sf_data_${bk}_marketplaces`;
      const existing = JSON.parse(localStorage.getItem(sk) || "[]");
      const updated = existing.filter(m => m.id !== mkt.id);
      updated.push({ ...mkt, sharedFrom: currentBrand });
      localStorage.setItem(sk, JSON.stringify(updated));
    } catch {}
  });
}
import {Btn, Label, BottomSheet, SlidePanel, SaveBadge, ConfirmSheet, PromptSheet, StyledSelect} from "../components/ui.jsx";
import {FONT, RADIUS, SPRING} from "../lib/theme.js";
import {
  fmtFull, mkKey, MONTH_NAMES, currSym,
  getMonthVars, payoutForMkt, uid, sortMkts,
  isMktActiveForKey, realizedPayoutPerProduct,
} from "../lib/calculations.js";
import { FulfilmentSection } from "./expenses/FulfilmentSection.jsx";
import { PLATFORMS, defaultApiConnection, initiateAuth, triggerSync } from "../lib/marketplaceApi.js";
import { SkeletonBlock } from "../App.jsx";
import ProductCostEditor from "./marketplaces/ProductCostEditor.jsx";
import {
  SALES_WINDOWS, DEFAULT_SALES_WINDOW,
  recomputeMarketplaceShares, recomputeProductShares,
  isOverridden, applyOverride, resetField, getApiValue,
  decoupleProduct, createGroup, deleteGroup, renameGroup, getProductGroup,
  aggregateGroupStats,
} from "./marketplaces/marketplaceUtils.js";
import { productStockoutInfo, groupStockoutInfo, stockoutSeverity } from "./marketplaces/stockoutMath.js";
import { aggregateLeadTimes } from "./orders/orderUtils.js";
import FieldChangeMarker from "../components/FieldChangeMarker.jsx";
import { supabase } from "../hooks/useData.js";

// ─── StockoutStrip — informational 6-column strip for API-connected products ─
// Shows: Stock · Pace · Y'day · Period · OOS date · days-to-stockout
// Sized to match the other pill badges/buttons in the row (~22 px tall, pill
// shape, 11 px text). Renders em-dashes when data missing; renders 0 when the
// product IS connected but genuinely sold nothing (meaningful zero).
//
// Severity color on the days number:
//   • red (critical) when daysToOOS ≤ leadTime — reorder window blown.
//   • red (stale) when daysToOOS > 365 — pace so low the forecast isn't
//     trustworthy. Tooltip explains this case distinctly from critical.
//   • orange (warning) when within leadTime + 21 days.
//   • green (ok) otherwise.
function StockoutStrip({ info, T, windowDays, leadTime, title }) {
  if (!info || !info.connected) {
    return null; // non-API products: silent (no strip)
  }
  const sev = stockoutSeverity(info.daysToOOS, leadTime);
  const sevColor = (sev === "critical" || sev === "stale") ? T.red
                 : sev === "warning" ? T.orange
                 : sev === "ok"      ? T.green
                 : T.textSub;

  const dash = <span style={{ color:T.textMuted, fontVariantNumeric:"tabular-nums" }}>—</span>;
  const paceTxt = info.pace > 0 ? info.pace.toFixed(info.pace < 1 ? 2 : 1) : null;
  const winLbl = `${windowDays || 30}d`;

  const sevTooltip = sev === "critical"
    ? `⚠ Below lead time (${leadTime}d) — you can't reorder in time. Place an order now.`
    : sev === "stale"
    ? "Low-confidence forecast — sales pace is very low, so the days number isn't reliable."
    : sev === "warning"
    ? `⏳ Within reorder window (lead time ${leadTime}d + 21d buffer). Plan the next order soon.`
    : "";

  const Cell = ({ label, children, color }) => (
    <span style={{ display:"inline-flex", alignItems:"baseline", gap:4, whiteSpace:"nowrap" }}>
      <span style={{ color:T.textMuted, fontSize:10, fontWeight:600, textTransform:"uppercase", letterSpacing:0.3 }}>{label}</span>
      <strong style={{ color: color || T.text, fontSize:11, fontWeight:700 }}>{children}</strong>
    </span>
  );
  const Sep = () => <span style={{ color:T.border, fontSize:11 }}>·</span>;

  return (
    <Tooltip content={title || "Stockout forecast · calculated from recent sales. Informational only — not used in Cash Calendar yet."}>
    <div style={{
      display:"inline-flex", alignItems:"center", gap:8,
      // match the 22 px height of adjacent pill buttons ("FROM API", decouple
      // button, etc.) by giving the strip the same vertical padding.
      padding:"3px 10px", height:22, boxSizing:"border-box",
      borderRadius:RADIUS.pill,
      background:T.surface,
      border:`1px solid ${T.accent}50`,
      fontFamily:FONT, color:T.text, lineHeight:1,
      fontVariantNumeric:"tabular-nums",
    }}>
      <Cell label="Stock">{info.inStock != null ? Math.round(info.inStock).toLocaleString() : dash}</Cell>
      <Sep/>
      <Cell label="Pace">{paceTxt ? `${paceTxt}/d` : dash}</Cell>
      <Sep/>
      <Cell label={winLbl}>{info.soldWindow != null ? info.soldWindow : dash}</Cell>
      <Sep/>
      <Cell label="OOS" color={sev === "critical" || sev === "stale" ? T.red : undefined}>
        {info.oosDate ? info.oosDate : dash}
      </Cell>
      <Sep/>
      <Tooltip content={sevTooltip || "Days until stock runs out at the current sales pace."}>
      <span style={{ color:sevColor, fontWeight:800, fontSize:11 }}>
        {info.daysToOOS != null ? `${info.daysToOOS.toLocaleString()}d` : dash}
      </span>
      </Tooltip>
    </div>
    </Tooltip>
  );
}

const MKT_COLORS = [
  "#FF3B30","#FF9500","#FFCC00","#34C759","#00C7BE",
  "#8b5cf6","#5E5CE6","#BF5AF2","#FF2D55","#30D158",
  "#64D2FF","#FFD60A","#FF6961","#4CD964","#007AFF",
];

const SHOP_ICONS = [
  { id:"bag",    emoji:"\u{1F6CD}", label:"Shopping Bag" },
  { id:"store",  emoji:"\u{1F3EA}", label:"Store" },
  { id:"globe",  emoji:"\u{1F310}", label:"Global" },
  { id:"box",    emoji:"\u{1F4E6}", label:"Box" },
  { id:"rocket", emoji:"\u{1F680}", label:"Rocket" },
  { id:"star",   emoji:"\u2B50", label:"Star" },
  { id:"cart",   emoji:"\u{1F6D2}", label:"Cart" },
  { id:"tag",    emoji:"\u{1F3F7}", label:"Tag" },
  { id:"gem",    emoji:"\u{1F48E}", label:"Premium" },
  { id:"fire",   emoji:"\u{1F525}", label:"Hot" },
  { id:"leaf",   emoji:"\u{1F33F}", label:"Eco" },
  { id:"bolt",   emoji:"\u26A1", label:"Fast" },
  { id:"heart",  emoji:"\u2764\uFE0F", label:"Favourite" },
  { id:"crown",  emoji:"\u{1F451}", label:"Crown" },
  { id:"truck",  emoji:"\u{1F69A}", label:"Delivery" },
  { id:"map",    emoji:"\u{1F5FA}", label:"Map" },
  { id:"chain",  emoji:"\u{1F517}", label:"Chain" },
  { id:"flag",   emoji:"\u{1F3C1}", label:"Flag" },
];

function mktColor(idx) { return MKT_COLORS[idx % MKT_COLORS.length]; }
function mktIcon(mkt) {
  if (!mkt.icon) return SHOP_ICONS[1].emoji;
  // If icon is a SHOP_ICONS id (legacy), look it up
  const found = SHOP_ICONS.find(i => i.id === mkt.icon);
  if (found) return found.emoji;
  // Otherwise it's a raw emoji from the new picker — return as-is
  return mkt.icon;
}

// ── Persona-style marketplace card ────────────────────────────────────────────
function MktCard({ mkt, color, isSelected, position, onClick, T }) {
  // Responsive card width: 320px desktop, 220px mobile
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const CARD_W = isMobile ? 220 : 320;
  const CARD_H = isMobile ? 150 : 180;
  const abs = Math.abs(position);
  const scale   = isSelected ? 1.05 : abs === 1 ? 0.91 : abs === 2 ? 0.78 : 0.66;
  // Opacity fades faster past abs=1 so off-center tiles don't appear to
  // overlap the left/right action columns. Combined with the container's
  // edge-fade mask this leaves a clean gap between the carousel and the
  // button stacks on each side.
  const opacity = isSelected ? 1    : abs === 1 ? 0.72 : abs === 2 ? 0.28 : 0;
  const zIndex  = isSelected ? 20   : abs === 1 ? 12   : abs === 2 ? 6    : 2;
  const STEP = CARD_W * 0.65;
  const translateX = position * STEP;
  const pctWarn = mkt.products.length > 0 &&
    Math.abs(mkt.products.reduce((s,p)=>s+(p.pctOfMarket||0),0)-1) > 0.01;
  const netPayout = payoutForMkt(mkt);

  // Glow blob — same shape as card, slightly smaller, blurred, behind the card
  const GLOW_W = CARD_W * 0.9;
  const GLOW_H = CARD_H * 0.88;
  const VPAD = 0;

  return (
    <>
      {/* Card — no glow, no shadow, no border, pure float */}
      <div
        onClick={onClick}
        style={{
          position: "absolute",
          left: "50%",
          marginLeft: -CARD_W / 2,
          top: VPAD,
          width: CARD_W,
          height: CARD_H,
          cursor: "pointer",
          transition: `all 0.42s ${SPRING}`,
          borderRadius: RADIUS.lg,
          background: "transparent",
          border: "none",
          boxShadow: "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          padding: "12px 16px",
          userSelect: "none",
          overflow: "hidden",
          transform: `translateX(${translateX}px) scale(${scale})`,
          opacity,
          zIndex,
        }}
      >

      {/* Icon — static, no click */}
      <div style={{ position:"relative", zIndex:1 }}>
        {mkt.customImage
          ? <div style={{ width:64,height:64,borderRadius:RADIUS.lg,background:`url(${mkt.customImage}) center/cover`,backgroundSize:"cover" }}/>
          : <div style={{ fontSize:44, lineHeight:1, filter:isSelected?"drop-shadow(0 2px 10px rgba(0,0,0,0.2))":"none", transition:"filter 0.3s" }}>
              {mktIcon(mkt)}
            </div>
        }
        {pctWarn && (
          <div style={{ position:"absolute",top:-2,right:-6,width:11,height:11,borderRadius:"50%",background:"#ef4444",border:"2px solid white" }}/>
        )}
      </div>

      {/* Info */}
      <div style={{ textAlign: "center", zIndex: 1 }}>
        <div style={{
          fontSize: 15, fontWeight: 700,
          color: T.text,
          letterSpacing: -0.3, marginBottom: 4,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          maxWidth: CARD_W - 32,
        }}>{mkt.name}</div>

        <div style={{
          fontSize: 12, fontWeight: 600,
          color: T.textSub,
          marginBottom: 6,
        }}>
          {Math.round((mkt.pctOfTotal || 0) * 100)}% of sales{mkt.payoutDelay > 0 ? ` · ${mkt.payoutDelay}d delay` : ""}
        </div>

        {/* Stats row */}
        <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
          <div style={{ background: T.surface, borderRadius: RADIUS.sm, padding: "3px 8px" }}>
            <div style={{ fontSize: 9, color: T.textSub, fontWeight: 600, letterSpacing: 0.3, textTransform: "uppercase" }}>Payout</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.green }}>
              {currSym()}{netPayout.toFixed(0)}/u
            </div>
          </div>
          <div style={{ background: T.surface, borderRadius: RADIUS.sm, padding: "3px 8px" }}>
            <div style={{ fontSize: 9, color: T.textSub, fontWeight: 600, letterSpacing: 0.3, textTransform: "uppercase" }}>Products</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>
              {mkt.products.length}
            </div>
          </div>
          {(Number(mkt.reserveHeld) || Number(mkt.apiConnection?.reservesHeld) || 0) > 0 && (
            <div style={{ background: T.surface, borderRadius: RADIUS.sm, padding: "3px 8px" }}>
              <div style={{ fontSize: 9, color: T.textSub, fontWeight: 600, letterSpacing: 0.3, textTransform: "uppercase" }}>🔒 Reserve</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.orange }}>
                {currSym()}{Math.round(Number(mkt.reserveHeld) || Number(mkt.apiConnection?.reservesHeld) || 0).toLocaleString()}
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  );
}

// ── Add marketplace card — positioned inline after all cards ──────────────────
function AddCard({ onClick, T, position }) {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const CARD_W = isMobile ? 220 : 320;
  const STEP = CARD_W * 0.65;
  const abs = Math.abs(position);
  const scale   = abs === 0 ? 0.93 : abs === 1 ? 0.83 : 0.75;
  const opacity = abs === 0 ? 0.8  : abs === 1 ? 0.55 : 0.35;
  const translateX = position * STEP;
  return (
    <div
      onClick={onClick}
      style={{
        position: "absolute",
        left: 16, top: isMobile ? 16 : 40,
        width: CARD_W, height: isMobile ? 150 : 180,
        transition: `all 0.42s ${SPRING}`,
        borderRadius: RADIUS.xl,
        background: T.card,
        border: `2px dashed ${T.accent+"55"}`,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: 12, cursor: "pointer",
        transform: `translateX(${translateX}px) scale(${scale})`,
        opacity,
        zIndex: 1,
        boxShadow: T.shadow,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = T.accent;
        e.currentTarget.style.background = T.accent + "10";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = T.accent + "55";
        e.currentTarget.style.background = T.card;
      }}
    >
      <div style={{
        width: 52, height: 52, borderRadius: "50%",
        background: T.accent + "18", border: `2px solid ${T.accent+"44"}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 26, color: T.accent,
      }}>+</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.accent }}>Add</div>
    </div>
  );
}

function Seg({ value, options, onChange, color }) {
  const T = useTheme();
  return (
    <div style={{ display:"flex", background:T.surfaceHi, borderRadius:RADIUS.md, padding:2, gap:1 }}>
      {options.map(([v,l]) => (
        <button key={v} onClick={()=>onChange(v)} style={{
          padding:"4px 10px", borderRadius:RADIUS.sm, fontSize:11, fontFamily:FONT,
          cursor:"pointer", fontWeight:value===v?700:400, border:"none",
          background:value===v?(color||T.accent)+"20":"transparent",
          color:value===v?(color||T.accent):T.textSub,
          transition:"all 0.18s ease",
        }}>{l}</button>
      ))}
    </div>
  );
}

function IconPicker({ value, onChange, color }) {
  const T = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = SHOP_ICONS.find(i=>i.id===value) || SHOP_ICONS[1];
  useEffect(() => {
    if (!open) return;
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  return (
    <div ref={ref} style={{ position:"relative", display:"inline-block" }}>
      <button onClick={()=>setOpen(o=>!o)} style={{
        width:52, height:52, borderRadius:RADIUS.md, border:`2px solid ${open?color:T.border}`,
        background:T.surface, cursor:"pointer", fontSize:24,
        display:"flex", alignItems:"center", justifyContent:"center",
        transition:`all 0.2s ${SPRING}`,
        boxShadow: open ? `0 0 0 3px ${color+"30"}` : "none",
      }}>{current.emoji}</button>
      {open && (
        <div style={{
          position:"absolute", top:"calc(100% + 8px)", left:0, zIndex:400,
          background:T.card, border:`1px solid ${T.border}`,
          borderRadius:RADIUS.lg, padding:12, width:260,
          boxShadow:"0 20px 60px rgba(0,0,0,0.4)",
          display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:5,
          animation:"sfSpringIn 0.22s ease both",
        }}>
          {SHOP_ICONS.map(icon => (
            <Tooltip key={icon.id} content={icon.label}>
            <button onClick={()=>{ onChange(icon.id); setOpen(false); }}
              style={{
                width:36, height:36, borderRadius:RADIUS.sm, border:"none",
                background: icon.id===value ? color+"22" : "transparent",
                cursor:"pointer", fontSize:20,
                outline: icon.id===value ? `2px solid ${color}` : "none",
                transition:`all 0.15s ${SPRING}`,
              }}
              onMouseEnter={e=>{ if(icon.id!==value) e.currentTarget.style.background=T.surfaceHi; }}
              onMouseLeave={e=>{ if(icon.id!==value) e.currentTarget.style.background="transparent"; }}
            >{icon.emoji}</button>
            </Tooltip>
          ))}
        </div>
      )}
    </div>
  );
}

const ProductRow = memo(function ProductRow({ prod, mkt, isGlobal, mktOps, monthOps, settingsMonth, setLastEditedId, lastEditedId, saveStatus, onDelete, onDecouple, T, globalSimpleMode=true, isSelected, onToggleSelect, groupName, showConfirm, hasMonthOverrides, globalProd, salesWindow=30, leadTime=14, realizedPayout=null, primaryIdentity="name" }) {
  // Stockout info — informational only, computed per-row. Cheap: pure math.
  const stockoutInfo = productStockoutInfo(prod, salesWindow);
  const [expanded, setExpanded] = useState(false);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const advancedMode = !globalSimpleMode; // controlled by global toggle
  const cogs = prod.cogs || { productCost:0, shipping:0, customs:0, prep:0 };
  const totalCogs = (cogs.productCost||0)+(cogs.shipping||0)+(cogs.customs||0)+(cogs.prep||0);
  // Advertising cost — new fields with ppcRate fallback
  const adType = prod.adCostType || (prod.ppcType === "fixed" ? "fixed" : "pct");
  const adRate = Number(prod.adCostRate ?? prod.ppcRate) || 0;
  const adAmount = Number(prod.adCostAmount) || 0;
  const adCost = prod.payout > 0
    ? (adType === "pct" ? prod.payout * adRate : adAmount)
    : 0;
  const refundCost = prod.payout>0&&(prod.refundType||"pct")==="pct" ? prod.payout*(prod.refundRate||0) : (prod.refundAmount||0);
  const netCash = prod.payout - adCost - refundCost - totalCogs;
  // Track whether any API-synced field has been manually overridden
  const hasApiOverrides = isGlobal && prod._overridden && Object.values(prod._overridden).some(Boolean);

  // Month override: save to months[key].vars instead of global marketplace data.
  // In GLOBAL mode, also tracks API overrides: if a field has an _apiSnapshot
  // value AND the user is changing it for the first time, ask for confirm and
  // mark _overridden[field] = true so re-syncs preserve the manual value.
  async function update(fields) {
    setLastEditedId(`prod_${prod.id}`);
    if (isGlobal) {
      // Detect API-overrides for fields that came from sync
      let confirmed = true;
      const newOverridden = { ...(prod._overridden || {}) };
      for (const [field, value] of Object.entries(fields)) {
        // Only track scalar fields, skip cogs (handled by ProductCostEditor)
        if (field === "cogs" || field === "_overridden" || field === "_apiSnapshot" || field === "_decoupled") continue;
        const apiVal = (prod._apiSnapshot || {})[field];
        if (apiVal !== undefined && apiVal !== value && !prod._overridden?.[field]) {
          if (confirmed) {
            confirmed = await showConfirm({
              icon: "✏️", title: "Override API value?",
              message: "This value came from your API sync. If you override it, future re-syncs will preserve your manual value.\n\nA \"↺ Reset to API\" button will appear so you can revert at any time.",
              confirmLabel: "Override", danger: false,
            });
            if (!confirmed) return;
          }
          newOverridden[field] = true;
        }
      }
      mktOps.updateProduct(mkt.id, prod.id, { ...fields, _overridden: newOverridden });
    } else if (monthOps && settingsMonth) {
      // Map each field to the correct var key
      if (fields.payout !== undefined) monthOps.updateVarField(settingsMonth, `payout_${mkt.id}_${prod.id}`, fields.payout);
      if (fields.deliveryCost !== undefined) monthOps.updateVarField(settingsMonth, `deliv_${mkt.id}_${prod.id}`, fields.deliveryCost);
      if (fields.pctOfMarket !== undefined) monthOps.updateVarField(settingsMonth, `pct_${mkt.id}_${prod.id}`, fields.pctOfMarket);
      if (fields.name !== undefined) mktOps.updateProduct(mkt.id, prod.id, { name: fields.name }); // names are always global
      if (fields.ppcRate !== undefined) monthOps.updateVarField(settingsMonth, `ppc_${mkt.id}_${prod.id}`, fields.ppcRate);
      if (fields.adCostRate !== undefined) monthOps.updateVarField(settingsMonth, `adrate_${mkt.id}_${prod.id}`, fields.adCostRate);
      if (fields.adCostAmount !== undefined) monthOps.updateVarField(settingsMonth, `adamt_${mkt.id}_${prod.id}`, fields.adCostAmount);
      if (fields.adCostType !== undefined) mktOps.updateProduct(mkt.id, prod.id, { adCostType: fields.adCostType }); // type is always global
      if (fields.deductAdCostFromProjection !== undefined) mktOps.updateProduct(mkt.id, prod.id, { deductAdCostFromProjection: fields.deductAdCostFromProjection }); // always global
      if (fields.adSpendPayments !== undefined) mktOps.updateProduct(mkt.id, prod.id, { adSpendPayments: fields.adSpendPayments });
      if (fields.adSpendScheduleType !== undefined) mktOps.updateProduct(mkt.id, prod.id, { adSpendScheduleType: fields.adSpendScheduleType });
      if (fields.adSpendScheduleDay !== undefined) mktOps.updateProduct(mkt.id, prod.id, { adSpendScheduleDay: fields.adSpendScheduleDay });
      if (fields.currentStock !== undefined) mktOps.updateProduct(mkt.id, prod.id, { currentStock: fields.currentStock });
      if (fields.refundRate !== undefined) monthOps.updateVarField(settingsMonth, `refund_${mkt.id}_${prod.id}`, fields.refundRate);
      if (fields.refundAmount !== undefined) monthOps.updateVarField(settingsMonth, `refundamt_${mkt.id}_${prod.id}`, fields.refundAmount);
      if (fields.cogs !== undefined) {
        const c = fields.cogs;
        if (c.productCost !== undefined) monthOps.updateVarField(settingsMonth, `cogs_product_${mkt.id}_${prod.id}`, c.productCost);
        if (c.shipping !== undefined) monthOps.updateVarField(settingsMonth, `cogs_ship_${mkt.id}_${prod.id}`, c.shipping);
        if (c.customs !== undefined) monthOps.updateVarField(settingsMonth, `cogs_customs_${mkt.id}_${prod.id}`, c.customs);
        if (c.prep !== undefined) monthOps.updateVarField(settingsMonth, `cogs_prep_${mkt.id}_${prod.id}`, c.prep);
      }
    }
  }
  function updateCogs(field, val) { update({ cogs: { ...cogs, [field]: parseFloat(val)||0 } }); }

  // Inline "↺ Reset to API" button — only shown when this field is overridden
  // AND we have a cached _apiSnapshot value to revert to.
  function ResetBtn({ field }) {
    if (!isGlobal) return null;
    if (!prod._overridden?.[field]) return null;
    const apiVal = prod._apiSnapshot?.[field];
    if (apiVal === undefined) return null;
    return (
      <button
        onClick={() => {
          const newOver = { ...prod._overridden };
          delete newOver[field];
          mktOps.updateProduct(mkt.id, prod.id, { [field]: apiVal, _overridden: newOver });
        }}
        title={`Reset to API value: ${apiVal}`}
        style={{
          marginLeft: 6, padding: "1px 7px",
          fontSize: 9, fontWeight: 700,
          background: T.green + "15",
          border: `1px solid ${T.green}40`,
          borderRadius: RADIUS.pill,
          color: T.green, fontFamily: FONT, cursor: "pointer",
        }}
      >↺ Reset</button>
    );
  }

  const monthBadge = !isGlobal ? <span style={{ fontSize:9, color:T.purple, background:T.purple+"18", padding:"1px 6px", borderRadius:RADIUS.pill, marginLeft:4, fontWeight:700, letterSpacing:0.3, verticalAlign:"middle" }}>MONTH</span> : null;

  // When in month-override mode, inputs with a value that DIFFERS from the
  // global default get a bright purple border + a small "✏" indicator so the
  // user can tell at a glance which fields they've overridden for this month.
  // Fields still at the global fallback keep a dimmer purple border.
  // Month-override inputs use ORANGE (not purple) for overridden fields so
  // the user immediately spots "this value is different from the global default
  // for this specific month." Non-overridden fields keep a dim purple border.
  const ovColor = T.orange;
  const inp = (val, onChange, color, w, globalVal, fieldName) => {
    const isMonthOverridden = !isGlobal && globalVal !== undefined && Number(val||0) !== Number(globalVal||0);
    const isApiOverridden = isGlobal && fieldName && prod._overridden?.[fieldName];
    const borderColor = isMonthOverridden ? ovColor : isApiOverridden ? T.orange : !isGlobal ? T.purple+"44" : T.border;
    return (
      <div style={{ position:"relative", display:"inline-block", width:w||"100%" }}>
        <input type="number" value={val||""} placeholder="0" onChange={onChange}
          style={{
            background: isMonthOverridden ? ovColor+"08" : isApiOverridden ? T.orange+"08" : T.card,
            border: `1.5px solid ${borderColor}`,
            borderRadius:RADIUS.sm, color:color, fontSize:14, fontWeight:700,
            padding:"7px 10px", width:"100%", outline:"none", fontFamily:FONT,
            boxSizing:"border-box", transition:"border-color 0.15s",
          }}
          onFocus={e=>e.target.style.borderColor=color+"88"}
          onBlur={e=>e.target.style.borderColor=borderColor}/>
        {isMonthOverridden && (
          <span title={`Overridden for this month (global: ${globalVal})`}
            style={{ position:"absolute", top:-5, right:-5, fontSize:8, background:ovColor, color:"#fff", borderRadius:"50%", width:14, height:14, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900, boxShadow:`0 1px 4px ${ovColor}55` }}>✏</span>
        )}
        {isApiOverridden && !isMonthOverridden && (
          <span title="Manually overridden (API value available — click ↺ Reset to API to revert)"
            style={{ position:"absolute", top:-5, right:-5, fontSize:8, background:T.orange, color:"#fff", borderRadius:"50%", width:14, height:14, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900, boxShadow:`0 1px 4px ${T.orange}55` }}>✏</span>
        )}
      </div>
    );
  };
  return (
    <div style={{ borderRadius:RADIUS.md, overflow:"hidden", background:T.card, transition:`all 0.22s ${SPRING}`, animation:"sfFadeIn 0.22s ease both", border: prod._decoupled ? `2px solid ${T.red}` : isSelected ? `1.5px solid ${T.accent}` : hasApiOverrides ? `1.5px solid ${T.orange}` : hasMonthOverrides ? `1.5px solid ${T.orange}` : (!isGlobal?`1px solid ${T.purple}33`:"none"), opacity: prod._decoupled ? 0.6 : 1 }}>
      {/* Row container is no longer clickable — only the title text is. The
          old row-wide onClick made it impossible to use the checkbox without
          accidentally expanding/collapsing the row. Now: click the product
          title to expand, click the checkbox to select for bulk operations. */}
      <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px" }}>
        {isGlobal && onToggleSelect && (
          <input
            type="checkbox"
            checked={!!isSelected}
            onChange={e=>{ e.stopPropagation(); onToggleSelect(prod.id); }}
            onClick={e=>e.stopPropagation()}
            title="Select for bulk operations"
            style={{ cursor:"pointer", flexShrink:0, accentColor:T.accent }}
          />
        )}
        <div style={{ width:8, height:8, borderRadius:"50%", background:prod._decoupled?T.red:(!isGlobal?T.purple:T.green), flexShrink:0 }}/>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ ...row(8) }}>
            {(() => {
              // Primary line — either name or SKU based on the user toggle.
              // Falls back to name when the user picked SKU but the product
              // has none synced yet.
              const wantSku = primaryIdentity === "sku";
              const hasSku = !!prod.sku;
              const showSkuPrimary = wantSku && hasSku;
              const primaryText = showSkuPrimary ? prod.sku : (prod.name || "Unnamed");
              const primaryMono = showSkuPrimary;
              return (
                <Tooltip content={`${prod.name || "Unnamed"}${hasSku ? ` · SKU ${prod.sku}` : ""} — click to expand / collapse`}>
                  <span
                    onClick={()=>setExpanded(o=>!o)}
                    style={{ fontSize:14, fontWeight:600, color:T.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", cursor:"pointer", userSelect:"none", maxWidth:320, display:"inline-block", fontFamily: primaryMono ? "monospace" : FONT }}>
                    {primaryText}
                  </span>
                </Tooltip>
              );
            })()}
            {groupName && <span style={{ fontSize:9, color:T.accent, fontWeight:700, padding:"1px 6px", borderRadius:8, background:T.accent+"15", border:`1px solid ${T.accent}40` }}>👪 {groupName}</span>}
            {prod._decoupled && <span style={{ fontSize:9, color:T.red, fontWeight:700, padding:"1px 6px", borderRadius:8, background:T.red+"15", border:`1px solid ${T.red}40` }}>DECOUPLED</span>}
            {prod.shopifyId && <span style={{ fontSize:9, color:T.green, fontWeight:700, padding:"1px 6px", borderRadius:8, background:T.green+"12", border:`1px solid ${T.green}30` }}>FROM API</span>}
            {!isGlobal && <span style={{ fontSize:10, color:T.purple, fontWeight:600 }}>📅 {settingsMonth}</span>}
            <SaveBadge status={saveStatus} show={lastEditedId===`prod_${prod.id}`}/>
            {/* StockoutStrip moved to the end of the row (Stock / Pace /
                Stockout big chips, right before Decouple). Keeps the name
                line uncluttered. */}
          </div>
          <div style={{ display:"flex", gap:12, marginTop:2, alignItems:"center", flexWrap:"wrap" }}>
            <span style={{ fontSize:12, fontWeight:700, color:T.green }}>{currSym()}{prod.payout?.toFixed(2)||"0.00"}</span>
            {(() => {
              // Secondary identity chip — always shows the OPPOSITE field
              // from the primary. If primary is SKU we show the name here
              // (truncated), and vice versa. Skipped entirely when the
              // product has no SKU.
              const wantSku = primaryIdentity === "sku";
              const hasSku = !!prod.sku;
              const skuIsPrimary = wantSku && hasSku;
              if (skuIsPrimary) {
                return (
                  <Tooltip content={prod.name || "Unnamed"}>
                    <span style={{
                      fontSize: 11, color: T.textSub,
                      background: T.surface, border: `1px solid ${T.border}`,
                      borderRadius: RADIUS.sm, padding: "1px 8px",
                      maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis",
                      whiteSpace: "nowrap", cursor: "help",
                    }}>
                      {prod.name || "Unnamed"}
                    </span>
                  </Tooltip>
                );
              }
              if (hasSku) {
                return (
                  <Tooltip content="SKU from the marketplace API">
                    <span style={{
                      fontSize: 10, fontFamily: "monospace", color: T.textSub,
                      background: T.surface, border: `1px solid ${T.border}`,
                      borderRadius: RADIUS.sm, padding: "1px 6px",
                      whiteSpace: "nowrap", cursor: "help",
                    }}>
                      SKU {prod.sku}
                    </span>
                  </Tooltip>
                );
              }
              return null;
            })()}
            {netCash!==prod.payout&&<span style={{ fontSize:11, color:netCash>=0?T.green:T.red }}>net {currSym()}{netCash.toFixed(2)}</span>}
            {/* Realized / net-revenue pill removed from the per-product row.
                The value still drives every downstream KPI via
                realizedPayoutPerProduct; inspect it on the Marketplace
                Data Audit page ("🔍 View data") if you need to see it. */}
            {(prod.pctOfMarket||0)>0&&<span style={{ fontSize:11, color:T.textSub }}>{Math.round((prod.pctOfMarket||0)*100)}% of {mkt.name} total sales</span>}
          </div>
        </div>
        <div style={{ ...row(8) }}>
          {/* End-of-row big chips — Stock, Pace, Stockout. Only rendered
              for API-connected products so manually-added rows stay clean.
              All three use the same card style for visual consistency. */}
          {isGlobal && stockoutInfo?.connected && (() => {
            const sev = stockoutSeverity(stockoutInfo.daysToOOS, leadTime);
            const stockoutColor = (sev === "critical" || sev === "stale") ? T.red
                                : sev === "warning" ? T.orange
                                : sev === "ok" ? T.green
                                : T.textSub;
            const inStock = stockoutInfo.inStock;
            const stockColor = inStock == null ? T.textSub
                             : inStock === 0 ? T.red
                             : inStock < 50 ? T.orange
                             : T.green;
            const pace = stockoutInfo.pace;
            const paceColor = pace > 0 ? T.accent : T.textSub;
            const Chip = ({ label, value, sub, color, tip }) => (
              <Tooltip content={tip}>
                <div style={{
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  padding: "4px 12px", borderRadius: RADIUS.md,
                  background: color + "12", border: `1.5px solid ${color}55`,
                  minWidth: 78, cursor: "help",
                }}>
                  <div style={{ fontSize: 9, color: T.textSub, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, lineHeight: 1 }}>{label}</div>
                  <div style={{ fontSize: 18, color, fontWeight: 800, fontFamily: FONT, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
                    {value}{sub && <span style={{ fontSize: 11, marginLeft: 2, opacity: 0.75 }}>{sub}</span>}
                  </div>
                </div>
              </Tooltip>
            );
            return (
              <>
                <Chip
                  label="Stock"
                  value={inStock != null ? Math.round(inStock).toLocaleString() : "—"}
                  sub="units"
                  color={stockColor}
                  tip={`Current stock on hand. Source: ${prod.apiStock != null ? "API sync" : prod.currentStock != null ? "manual entry" : "not set"}.`}
                />
                <Chip
                  label="Pace"
                  value={pace > 0 ? pace.toFixed(pace < 1 ? 2 : 1) : "0"}
                  sub="/d"
                  color={paceColor}
                  tip={`Average daily sales over the last ${salesWindow} days. Sold ${stockoutInfo.soldWindow ?? 0} units in that window.`}
                />
                {stockoutInfo.daysToOOS != null && (
                  <Chip
                    label="Stockout"
                    value={stockoutInfo.daysToOOS.toLocaleString()}
                    sub="days"
                    color={stockoutColor}
                    tip={`Runs out of stock in ${stockoutInfo.daysToOOS.toLocaleString()} days at the current ${salesWindow}-day pace (${stockoutInfo.oosDate || "—"}).`}
                  />
                )}
              </>
            );
          })()}
          {isGlobal && (
            <button onClick={e=>{e.stopPropagation();onDecouple?.();}}
              title={prod._decoupled ? "Recouple — bring this product back into every calculation" : "Decouple this product from ALL calculations (income, inventory, stockout, charts)"}
              style={{
                background: prod._decoupled ? T.green + "18" : "transparent",
                border: `1.5px solid ${prod._decoupled ? T.green : T.border}`,
                color: prod._decoupled ? T.green : T.textSub,
                cursor: "pointer", fontSize: 10, fontWeight: 700,
                padding: "3px 10px", borderRadius: RADIUS.pill,
                fontFamily: FONT, opacity: 0.95, transition: "all 0.15s",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={e=>e.currentTarget.style.opacity=1}
              onMouseLeave={e=>e.currentTarget.style.opacity=0.95}>
              {prod._decoupled ? "🔗 Recouple" : "🔌 Decouple"}
            </button>
          )}
          {isGlobal && <button onClick={e=>{e.stopPropagation();onDelete();}} style={{ background:"transparent",border:"none",color:T.red,cursor:"pointer",fontSize:13,padding:"2px 6px",borderRadius:RADIUS.sm,opacity:0.4,transition:"opacity 0.15s" }} onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=0.4}>✕</button>}
          <button onClick={()=>setExpanded(o=>!o)} title={expanded?"Collapse":"Expand"} style={{ width:22,height:22,borderRadius:"50%",background:T.surfaceHi,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:T.textSub,transform:expanded?"rotate(180deg)":"rotate(0deg)",transition:`transform 0.22s ${SPRING}`,border:"none",cursor:"pointer",padding:0 }}>▼</button>
        </div>
      </div>
      {/* Expanded panel — conditionally mounted (was always-mounted with maxHeight:0
          which forced React to keep ~hundreds of nodes per row alive, including a
          full ProductCostEditor instance. With 60 products that produced thousands
          of stale DOM nodes and made bulk-select toggle the page unresponsive). */}
      {expanded && (
      <div style={{ overflow:"hidden", animation:"sfFadeIn 0.18s ease both" }}>
        <div style={{ padding:"0 16px 16px", borderTop:`1px solid ${T.border}` }}>

          <div style={{ marginTop:14 }}>
          </div>

          {/* Row 1: always visible — Product Name, Payout/unit, % of marketplace */}
          <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr 1fr" : (advancedMode ? "repeat(3,1fr)" : "1fr 1fr 1fr"), gap:10, marginBottom:10 }}>
            <div>
              <Tooltip content="The display name for this product. Use your product title, ASIN, SKU, or any label that helps you identify it. This is only for your reference — it doesn't affect calculations.">
              <div style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6 }}>
                Product Name <Info tip="productName"/>
              </div>
              </Tooltip>
              <input value={prod.name||""} placeholder="Product name" onChange={e=>update({name:e.target.value})}
                style={{ background:T.card,border:`1.5px solid ${T.border}`,borderRadius:RADIUS.sm,color:T.text,fontSize:13,padding:"7px 10px",width:"100%",outline:"none",fontFamily:FONT,boxSizing:"border-box",transition:"border-color 0.15s" }}
                onFocus={e=>e.target.style.borderColor=T.accent+"88"} onBlur={e=>e.target.style.borderColor=T.border}/>
            </div>
            <div>
              <Tooltip content={"How much you receive per unit sold after marketplace fees. For Amazon, this is your 'Your Earnings' or net payout per unit from the transaction report. For Shopify, it's the selling price minus payment processing fees. This is the core number that drives your income projections."}>
              <div style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6 }}>
                Payout / Unit ({currSym()}) {monthBadge} <Info tip="payout" saveStatus={saveStatus}/><ResetBtn field="payout"/>
                <FieldChangeMarker fieldKey={`product.${mkt.id}.${prod.id}.payout`} compact />
              </div>
              </Tooltip>
              {inp(prod.payout,e=>update({payout:parseFloat(e.target.value)||0}),T.green,undefined,globalProd?.payout,"payout")}
            </div>
            <div>
              <Tooltip content={"What percentage of "+mkt.name+"'s total revenue comes from this product? If "+mkt.name+" earns $10,000/month and this product drives 30% of that, enter 30. All products in this marketplace must add up to 100%."}>
              <div style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6 }}>
                % of {mkt.name} Sales {monthBadge} <Info tip="pctOfMarket" saveStatus={saveStatus}/><ResetBtn field="pctOfMarket"/>
              </div>
              </Tooltip>
              <div style={{ display:"flex",alignItems:"center",gap:4 }}>{inp(Math.round((prod.pctOfMarket||0)*100)||"",e=>update({pctOfMarket:(parseFloat(e.target.value)||0)/100}),T.accent,undefined,globalProd?Math.round((globalProd.pctOfMarket||0)*100):undefined,"pctOfMarket")}<span style={{ fontSize:11,color:T.textSub,flexShrink:0 }}>%</span></div>
            </div>
          </div>

          {/* Advanced-only fields */}
          {advancedMode && (
            <>
              {/* Delivery/unit — deducted from payout, NOT a Cash Calendar expense */}
              <div style={{ marginBottom:10 }}>
                <Tooltip content={"Fulfillment / delivery cost per unit. Deducted from your payout per unit when 'Deduct from payout' is checked — never appears as a separate Cash Calendar expense."}>
                <div style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6 }}>
                  Delivery / Unit ({currSym()}) {monthBadge} <Info tip="deliveryCost" saveStatus={saveStatus}/><ResetBtn field="deliveryCost"/>
                </div>
                </Tooltip>
                <div style={{ display:"flex", alignItems:"center", gap:10, maxWidth:340, flexWrap:"wrap" }}>
                  <div style={{ maxWidth:140 }}>
                    {inp(prod.deliveryCost,e=>update({deliveryCost:parseFloat(e.target.value)||0}),T.text,undefined,globalProd?.deliveryCost,"deliveryCost")}
                  </div>
                  <label style={{ display:"inline-flex", alignItems:"center", gap:7, fontSize:12, color:T.text, cursor:"pointer", userSelect:"none" }}>
                    <input type="checkbox" checked={!!prod.deductDeliveryFromPayout}
                      onChange={e => update({ deductDeliveryFromPayout: e.target.checked })}
                      style={{ accentColor:T.accent, width:14, height:14, cursor:"pointer" }}/>
                    <span>Deduct from payout</span>
                  </label>
                </div>
                {mkt?.apiConnection?.connected && (Number(prod.deliveryCost) > 0) && prod.deductDeliveryFromPayout && (
                  <div style={{ marginTop:8, padding:"8px 11px", background:T.orange+"15", border:`1px solid ${T.orange}40`, borderRadius:RADIUS.sm, fontSize:11, color:T.orange, lineHeight:1.45 }}>
                    ⚠ This marketplace is API-connected. FBA / managed-fulfillment payouts already have shipping deducted — leaving this checked may double-count. Only enable if you handle FBM (self-fulfilled) for this product.
                  </div>
                )}
              </div>

              {/* Advertising Cost + Refunds */}
              <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:10, marginBottom:10 }}>
                <div style={{ background:T.surface,borderRadius:RADIUS.md,padding:"10px 12px" }}>
                  <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8 }}>
                    <Tooltip content="How much of your payout goes to advertising per unit sold? Enter either a percentage of your payout or a fixed amount per unit. This is the effective rate you spend on ads — regardless of whether you call it ACOS, TACOS, or ROAS.">
                    <div style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5 }}>
                      Advertising Cost per unit {monthBadge} <Info tip="adCostRate" saveStatus={saveStatus}/>
                    </div>
                    </Tooltip>
                    <Seg value={adType} options={[["pct","%"],["fixed",currSym()]]} onChange={v=>update({adCostType:v})} color="#BF5AF2"/>
                  </div>
                  <div style={{ display:"flex",alignItems:"center",gap:4 }}>
                    {adType==="fixed"&&<span style={{ fontSize:11,color:T.textSub }}>{currSym()}</span>}
                    {inp(adType==="pct"?(Math.round(adRate*100)||""):(adAmount||""),e=>{ const v=parseFloat(e.target.value)||0; adType==="pct"?update({adCostRate:v/100,adCostType:"pct"}):update({adCostAmount:v,adCostType:"fixed"}); },"#BF5AF2")}
                    {adType==="pct"&&<span style={{ fontSize:11,color:T.textSub }}>%</span>}
                  </div>
                  {/* Deduct from payout checkbox */}
                  <label style={{ display:"flex",alignItems:"center",gap:6,marginTop:8,cursor:"pointer",fontSize:11,color:prod.deductAdCostFromProjection?T.accent:T.textSub }}>
                    <input type="checkbox" checked={!!prod.deductAdCostFromProjection} onChange={e=>{
                        const on = e.target.checked;
                        const patch = { deductAdCostFromProjection: on };
                        if (on) patch.adSpendPayments = false; // mutually exclusive
                        update(patch);
                      }}
                      style={{ width:14,height:14,accentColor:"#8b5cf6",cursor:"pointer" }}/>
                    Deduct from payout projection
                  </label>
                  {prod.deductAdCostFromProjection && (()=>{ const gross=Number(prod.payout)||0; const adDed=adType==="pct"?gross*adRate:(adAmount||0); const net=Math.max(0,gross-adDed); return <div style={{ fontSize:10,color:T.accent,marginTop:4,lineHeight:1.4 }}>Chart uses {currSym()}{net.toFixed(2)}/unit (after {currSym()}{adDed.toFixed(2)} ad cost) instead of {currSym()}{gross.toFixed(2)}/unit</div>; })()}
                  {/* Generate ad spend payments — mutually exclusive with deduct from projection */}
                  <label style={{ display:"flex",alignItems:"center",gap:6,marginTop:8,cursor:"pointer",fontSize:11,color:prod.adSpendPayments?T.orange:T.textSub }}>
                    <input type="checkbox" checked={!!prod.adSpendPayments} onChange={e=>{
                        const on = e.target.checked;
                        const patch = { adSpendPayments: on };
                        if (on) patch.deductAdCostFromProjection = false; // mutually exclusive
                        update(patch);
                      }}
                      style={{ width:14,height:14,accentColor:T.orange,cursor:"pointer" }}/>
                    Generate ad spend as expense entries
                  </label>
                  {prod.adSpendPayments && (
                    <div style={{ marginTop:6, padding:"8px 10px", background:T.bg, borderRadius:RADIUS.sm, border:`1px solid ${T.border}` }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                        <Seg value={prod.adSpendScheduleType==="every_x_days"&&prod.adSpendScheduleDay===7?"weekly":(prod.adSpendScheduleType||"monthly_day")} options={[["monthly_day","Monthly"],["weekly","Weekly"],["every_x_days","Every X days"]]} onChange={v=>{
                          if(v==="weekly") update({adSpendScheduleType:"every_x_days",adSpendScheduleDay:7});
                          else update({adSpendScheduleType:v});
                        }} color={T.orange}/>
                        {(prod.adSpendScheduleType||"monthly_day")==="monthly_day" && <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                          <span style={{ fontSize:10, color:T.textSub }}>Day:</span>
                          <input type="number" value={prod.adSpendScheduleDay||""} placeholder="1" min="1" max="28"
                            onChange={e=>update({adSpendScheduleDay:parseInt(e.target.value)||0})}
                            style={{ width:50, background:T.card, border:`1px solid ${T.orange}`, borderRadius:RADIUS.sm, color:T.orange, padding:"3px 6px", fontSize:12, fontWeight:700, fontFamily:FONT, outline:"none" }}/>
                        </div>}
                        {prod.adSpendScheduleType==="every_x_days"&&prod.adSpendScheduleDay!==7 && <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                          <span style={{ fontSize:10, color:T.textSub }}>Every</span>
                          <input type="number" value={prod.adSpendScheduleDay||""} placeholder="30" min="1" max="365"
                            onChange={e=>update({adSpendScheduleDay:parseInt(e.target.value)||0})}
                            style={{ width:50, background:T.card, border:`1px solid ${T.orange}`, borderRadius:RADIUS.sm, color:T.orange, padding:"3px 6px", fontSize:12, fontWeight:700, fontFamily:FONT, outline:"none" }}/>
                          <span style={{ fontSize:10, color:T.textSub }}>days</span>
                        </div>}
                      </div>
                      <div style={{ fontSize:10, color:T.textSub, marginTop:4, lineHeight:1.4 }}>
                        Creates expense entries on Cash Calendar: ad cost × units sold for the period. Amount auto-calculated from your targets.
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ background:T.surface,borderRadius:RADIUS.md,padding:"10px 12px" }}>
                  <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8 }}>
                    <Tooltip content="Expected refund rate — what percentage of sales get refunded (or a fixed amount per unit). This reduces your effective payout. Amazon averages 3-8% depending on category. Set to % for rate-based or a fixed refund amount per unit.">
                    <div style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5 }}>
                      Refunds {monthBadge} <Info tip="refundRate" saveStatus={saveStatus}/>
                    </div>
                    </Tooltip>
                    <Seg value={prod.refundType||"pct"} options={[["pct","%"],["fixed",currSym()]]} onChange={v=>update({refundType:v})} color={T.red}/>
                  </div>
                  <div style={{ display:"flex",alignItems:"center",gap:4 }}>
                    {(prod.refundType||"pct")==="fixed"&&<span style={{ fontSize:11,color:T.textSub }}>{currSym()}</span>}
                    {inp((prod.refundType||"pct")==="pct"?(Math.round((prod.refundRate||0)*100)||""):(prod.refundAmount||""),e=>{ const v=parseFloat(e.target.value)||0; (prod.refundType||"pct")==="pct"?update({refundRate:v/100}):update({refundAmount:v}); },T.red)}
                    {(prod.refundType||"pct")==="pct"&&<span style={{ fontSize:11,color:T.textSub }}>%</span>}
                  </div>
                  <label style={{ display:"flex",alignItems:"center",gap:6,marginTop:8,cursor:"pointer",fontSize:11,color:(prod.deductRefundFromProjection??prod.deductAdCostFromProjection)?T.accent:T.textSub }}>
                    <input type="checkbox" checked={!!(prod.deductRefundFromProjection??prod.deductAdCostFromProjection)} onChange={e=>update({deductRefundFromProjection:e.target.checked})}
                      style={{ width:14,height:14,accentColor:"#8b5cf6",cursor:"pointer" }}/>
                    Deduct refunds from payout projection
                  </label>
                  {(prod.deductRefundFromProjection??prod.deductAdCostFromProjection) && (()=>{ const gross=Number(prod.payout)||0; const refDed=(prod.refundType||"pct")==="pct"?gross*(Number(prod.refundRate)||0):(Number(prod.refundAmount)||0); return <div style={{ fontSize:10,color:T.accent,marginTop:4,lineHeight:1.4 }}>Refund deduction: {currSym()}{refDed.toFixed(2)}/unit ({(prod.refundType||"pct")==="pct"?Math.round((Number(prod.refundRate)||0)*100)+"%":currSym()+(Number(prod.refundAmount)||0)})</div>; })()}
                </div>
              </div>

              {/* COGS — hidden: cost per unit now comes from onboarding + orders */}
              {false && <div style={{ marginTop:2,padding:"12px 14px",background:T.bg,borderRadius:RADIUS.md }}>
                <Tooltip content="Cost of Goods Sold — the direct costs to produce and deliver each unit. These are optional but give you a complete picture of per-unit profitability. All four fields are added together and subtracted from your payout.">
                <div style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8 }}>
                  Cost of Goods Sold (COGS) — Optional <Info tip="cogs" saveStatus={saveStatus}/>
                </div>
                </Tooltip>
                <div style={{ display:"grid",gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)",gap:8 }}>
                  {[
                    ["productCost","Product Cost",T.orange,"productCostFactory"],
                    ["shipping","Inbound Shipping","#5E5CE6","productCostShipping"],
                    ["customs","Import Duty","#FF2D55","productCostCustoms"],
                    ["prep","Prep & Labeling","#30D158","productCostPrep"],
                  ].map(([field,label,clr,tipKey])=>(
                    <div key={field}>
                      <div style={{ fontSize:10,color:T.textSub,marginBottom:4,display:"inline-flex",alignItems:"center" }}>{label} <Info tip={tipKey}/></div>
                      <input type="number" value={cogs[field]||""} placeholder="0" onChange={e=>updateCogs(field,e.target.value)}
                        style={{ background:T.card,border:`1px solid ${T.border}`,borderRadius:RADIUS.sm,color:clr,fontSize:13,fontWeight:700,padding:"5px 8px",width:"100%",outline:"none",fontFamily:FONT,boxSizing:"border-box" }}/>
                    </div>
                  ))}
                </div>
                {totalCogs>0&&<div style={{ fontSize:12,color:T.text,marginTop:8 }}>COGS: {currSym()}{totalCogs.toFixed(2)} · Net: <span style={{ color:netCash>=0?T.green:T.red,fontWeight:700 }}>{currSym()}{netCash.toFixed(2)}</span></div>}
              </div>}

              {/* Current Stock — per-product manual override.
                  Sits at level 3 of the stock hierarchy:
                    pool > marketplace > per-product manual > API → ...
                  Sticky: API syncs (which write product.apiStock alongside)
                  never touch product.currentStock. Show the API value
                  underneath as a "↺ Use API value" link when present. */}
              <div style={{ marginTop:10,padding:"12px 14px",background:T.surface,borderRadius:RADIUS.md,border:`1px solid ${T.border}` }}>
                <Tooltip content="Per-product manual stock override. Hierarchy: pool > marketplace > per-product manual > API > none. When set, this number is summed with other products' values to feed the marketplace's stock contribution to the pool. Sticky — API syncs capture their own value separately so you can compare and revert.">
                <div style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6 }}>
                  📦 Current Stock (units) <Info tip="currentStock" saveStatus={saveStatus}/>
                </div>
                </Tooltip>
                <div style={{ display:"flex",alignItems:"center",gap:12,flexWrap:"wrap" }}>
                  <input type="number" value={prod.currentStock||""} placeholder="0" onChange={e=>update({currentStock:parseInt(e.target.value)||0})}
                    style={{ background:T.card,border:`1.5px solid ${T.border}`,borderRadius:RADIUS.sm,color:T.accent,fontSize:15,fontWeight:700,padding:"7px 10px",width:100,outline:"none",fontFamily:FONT,boxSizing:"border-box" }}/>
                  {(prod.currentStock||0)>0 && totalCogs>0 && (
                    <span style={{ fontSize:12,color:T.textSub }}>
                      Value: <strong style={{ color:T.orange }}>{currSym()}{((prod.currentStock||0)*totalCogs).toLocaleString()}</strong>
                    </span>
                  )}
                  {/* "↺ Use API value" — only when there's a fresh API number that differs from the manual one */}
                  {prod.apiStock != null && prod.apiStock !== (Number(prod.currentStock)||0) && (
                    <button onClick={()=>update({currentStock: prod.apiStock})}
                      title={`API last reported ${prod.apiStock} units${prod.apiStockDate ? ` on ${prod.apiStockDate}` : ""}`}
                      style={{ background:T.green+"10",border:`1px solid ${T.green}40`,borderRadius:RADIUS.sm,padding:"3px 9px",fontSize:11,fontFamily:FONT,cursor:"pointer",color:T.green,fontWeight:600 }}>
                      ↺ Use API value ({prod.apiStock})
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ── Cost & Profitability editor (replaces deleted Profitability page) ── */}
          {isGlobal && (
            <ProductCostEditor
              product={prod}
              onPatch={(patched) => {
                // Patch back to the marketplace's products list
                mktOps.updateProduct?.(mkt.id, prod.id, {
                  cogs: patched.cogs,
                  _overridden: patched._overridden,
                  _apiSnapshot: patched._apiSnapshot,
                });
              }}
              onDecouple={async () => {
                const isDecoupling = !prod._decoupled;
                if (isDecoupling) {
                  const ok = await showConfirm({
                    icon: "🔗", title: `Decouple "${prod.name||"this product"}"?`,
                    message: "This product will be EXCLUDED from every calculation: income projections, inventory pools, stockout date, the Dashboard chart, the AI assistant, and the Cash Calendar.\n\nYou can recouple it any time.",
                    confirmLabel: "Decouple", danger: true,
                  });
                  if (!ok) return;
                }
                mktOps.updateProduct?.(mkt.id, prod.id, { _decoupled: isDecoupling });
              }}
              onConfirmOverride={async (field) => {
                return showConfirm({
                  icon: "✏️", title: "Override API value?",
                  message: "This value came from your API sync. If you override it, future re-syncs will preserve your manual value (a \"↺ Reset to API\" button appears).",
                  confirmLabel: "Override", danger: false,
                });
              }}
            />
          )}
        </div>
      </div>
      )}
    </div>
  );
}, (prev, next) => {
  // Custom comparator: only re-render when something this row actually
  // displays has changed. Selecting/deselecting a sibling product no longer
  // re-renders this row (the bulk-select lag fix). Reference equality on
  // prod / mkt is the fast path; if either reference is fresh we fall
  // through to normal render.
  if (prev.prod !== next.prod) return false;
  if (prev.mkt !== next.mkt) return false;
  if (prev.isSelected !== next.isSelected) return false;
  if (prev.isGlobal !== next.isGlobal) return false;
  if (prev.globalSimpleMode !== next.globalSimpleMode) return false;
  if (prev.settingsMonth !== next.settingsMonth) return false;
  if (prev.groupName !== next.groupName) return false;
  if (prev.saveStatus !== next.saveStatus) return false;
  if (prev.lastEditedId !== next.lastEditedId) {
    // Only matters if either id refers to THIS product
    const key = `prod_${next.prod.id}`;
    if (prev.lastEditedId === key || next.lastEditedId === key) return false;
  }
  if (prev.T !== next.T) return false;
  // Function props (onDelete, onDecouple, onToggleSelect, mktOps, monthOps,
  // setLastEditedId) are intentionally NOT compared — the caller passes
  // stable refs via useCallback.
  return true;
});


// ── Side column button — full-width stacked action button ──────────────────
// Used in the left/right columns that flank the marketplace carousel. Keeps
// visual rhythm uniform regardless of whether the button is an "accent"
// (primary) or "ghost" (secondary/muted) action.
function SideBtn({ label, onClick, color, T, disabled, variant = "ghost", title }) {
  const isAccent = variant === "accent";
  const isWarn = variant === "warn";
  const baseColor = color || (isAccent ? T.accent : isWarn ? T.orange : T.textSub);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        gap: 6, padding: "8px 10px", borderRadius: RADIUS.md,
        background: isAccent ? baseColor + "18" : (isWarn ? baseColor + "15" : "transparent"),
        border: `1px solid ${baseColor}${isAccent ? "55" : "33"}`,
        color: baseColor,
        fontSize: 12, fontWeight: 700, fontFamily: FONT,
        cursor: disabled ? "wait" : "pointer",
        opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap", width: "100%",
        transition: "background 0.18s, transform 0.18s",
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = baseColor + "22"; }}
      onMouseLeave={e => { e.currentTarget.style.background = isAccent ? baseColor + "18" : (isWarn ? baseColor + "15" : "transparent"); }}
    >
      {label}
    </button>
  );
}

// ── Side column rendering — left or right of the marketplace carousel ──────
// Groups the per-marketplace actions (connect, sync, import, launch, etc.)
// into two vertical stacks. Placement decisions match the user-facing mental
// model: data-source controls on the left, product + data-inspection
// controls on the right.
function MktSideColumn({
  side, mkt, T, mktColor, isMobile,
  // shared
  syncingMktId,
  // left
  salesWindow, setSalesWindow, onConnect, onDisconnect, onRecompute, onTrySample, onViewData,
  // right
  onSync, onAddProduct, onLaunchProduct, onToggleFulfilment, showFBM, onImport,
  channelPanelMktId, setChannelPanelMktId,
  showManualFields, setShowManualFields, isGlobal,
}) {
  if (!mkt) return null;
  const isConn = !!mkt.apiConnection?.connected;
  const syncError = mkt.apiConnection?.error || null;
  const lastSync = mkt.apiConnection?.lastSync;
  const lastSyncFmt = lastSync
    ? new Date(lastSync).toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "never";
  const platform = mkt.apiConnection?.platform || mkt.platform || (() => {
    const n = (mkt.name || "").toLowerCase();
    if (n.includes("shopify")) return "shopify";
    if (n.includes("amazon")) return "amazon";
    if (n.includes("walmart")) return "walmart";
    if (n.includes("ebay")) return "ebay";
    return null;
  })();
  const colStyle = {
    display: "flex", flexDirection: "column", gap: 8,
    padding: "12px 10px",
    background: mktColor + "06", border: `1px solid ${mktColor}15`,
    borderRadius: RADIUS.lg,
    alignSelf: "stretch",
  };

  if (side === "left") {
    const pillColor = syncError ? T.orange : T.green;
    return (
      <div style={colStyle}>
        {/* Status pills — only when API is connected. Shown as non-clickable
            indicators so the user knows at a glance that data is flowing. */}
        {isConn && (
          <>
            <Tooltip content={syncError ? `Last sync failed: ${String(syncError).slice(0, 220)}` : `Last sync: ${lastSyncFmt}`}>
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                padding: "4px 10px", borderRadius: RADIUS.pill,
                background: pillColor + "15", border: `1px solid ${pillColor}40`,
                color: pillColor, fontSize: 11, fontWeight: 700, fontFamily: FONT,
                whiteSpace: "nowrap", cursor: "help",
              }}>
                {syncError ? "⚠️ Sync failed" : "🔌 Connected"}
              </span>
            </Tooltip>
            {(mkt.unitsCount != null || mkt.ordersCount != null) && (
              <Tooltip content={`Orders and units from ${platform || "API"} over the last ${mkt.windowDays || 30} days.`}>
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                  padding: "4px 10px", borderRadius: RADIUS.pill,
                  background: mktColor + "12", border: `1px solid ${mktColor}33`,
                  color: T.text, fontSize: 11, fontWeight: 600, fontFamily: FONT,
                  whiteSpace: "nowrap", cursor: "help",
                }}>
                  <span style={{ color: T.textSub }}>O</span>
                  <span>{(mkt.ordersCount ?? 0).toLocaleString()}</span>
                  <span style={{ color: T.textSub }}>·</span>
                  <span style={{ color: T.textSub }}>U</span>
                  <span>{(mkt.unitsCount ?? 0).toLocaleString()}</span>
                  <span style={{ color: T.textSub, fontSize: 9 }}>{mkt.windowDays || 30}d</span>
                </span>
              </Tooltip>
            )}
          </>
        )}
        <div style={{ fontSize: 9, fontWeight: 700, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.4, textAlign: "center", marginTop: isConn ? 4 : 0, marginBottom: 2 }}>Connection</div>
        {isConn ? (
          <SideBtn T={T} label="Disconnect" onClick={onDisconnect} variant="ghost" title="Remove API connection" />
        ) : (
          <SideBtn T={T} label={platform ? `🔌 Connect ${platform.charAt(0).toUpperCase() + platform.slice(1)}` : "🔌 Connect API"} onClick={() => platform && onConnect(platform, mkt.id)} variant="accent" color={T.green} disabled={!platform} title={platform ? "" : "Platform not detected from marketplace name"} />
        )}

        {isConn && (
          <SideBtn T={T} label="🔍 View data" onClick={onViewData} variant="accent" title="Open the Marketplace Data Audit page" />
        )}

        <div style={{ fontSize: 9, fontWeight: 700, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.4, textAlign: "center", marginTop: 6, marginBottom: 2 }}>Share window</div>
        <div style={{ width: "100%" }}>
          <StyledSelect sm value={salesWindow} onChange={e => setSalesWindow(parseInt(e.target.value, 10))}>
            {SALES_WINDOWS.map(w => <option key={w.days} value={w.days}>{w.label}</option>)}
          </StyledSelect>
        </div>
        <SideBtn T={T} label="↻ Recompute" onClick={onRecompute} variant="accent" title="Recompute per-product shares from sales in the selected window" />

        <div style={{ fontSize: 9, fontWeight: 700, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.4, textAlign: "center", marginTop: 6, marginBottom: 2 }}>Demo</div>
        <SideBtn T={T} label="🧪 Sample data" onClick={onTrySample} variant="ghost" title="Load fixture data — no real API call" />
      </div>
    );
  }

  // side === "right"
  const channelBreakdown = mkt.apiConnection?.channelBreakdown?.[30] || null;
  const NATIVE = new Set(["web", "pos", "shopify_draft_order"]);
  const nonNative = channelBreakdown ? Object.keys(channelBreakdown).filter(s => !NATIVE.has(s)) : [];
  const excludedChannels = Array.isArray(mkt.apiConnection?.includeChannels)
    ? Object.keys(channelBreakdown || {}).filter(s => !mkt.apiConnection.includeChannels.includes(s))
    : [];

  return (
    <div style={colStyle}>
      {isConn && (
        <>
          <div style={{ fontSize: 9, fontWeight: 700, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.4, textAlign: "center", marginBottom: 2 }}>Data</div>
          <SideBtn T={T} label={syncingMktId === mkt.id ? "Syncing…" : "↻ Sync now"} onClick={onSync} variant="accent" color={T.green} disabled={syncingMktId === mkt.id} />
          {nonNative.length > 0 && (
            <SideBtn
              T={T}
              label={`📡 Channels (${Object.keys(channelBreakdown).length})${excludedChannels.length ? ` · ${excludedChannels.length} off` : ""}`}
              onClick={() => setChannelPanelMktId(channelPanelMktId === mkt.id ? null : mkt.id)}
              variant={excludedChannels.length > 0 ? "warn" : "ghost"}
              title="Include/exclude third-party sales channels from this sync"
            />
          )}
        </>
      )}
      <div style={{ fontSize: 9, fontWeight: 700, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.4, textAlign: "center", marginTop: isConn ? 6 : 0, marginBottom: 2 }}>Products</div>
      <SideBtn T={T} label="+ Add product" onClick={onAddProduct} variant="accent" />
      <SideBtn T={T} label="🚀 Launch" onClick={onLaunchProduct} variant="ghost" title="Launch a new product on this marketplace" />
      <SideBtn T={T} label="📥 Import" onClick={onImport} variant="ghost" title="Import products from CSV" />
      <SideBtn T={T} label={`🏭 ${showFBM ? "Hide" : "Fulfilment cost"}`} onClick={onToggleFulfilment} variant={showFBM ? "warn" : "ghost"} title="Configure fulfilment / storage costs" />
      {isGlobal && setShowManualFields && (
        <>
          <div style={{ fontSize: 9, fontWeight: 700, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.4, textAlign: "center", marginTop: 6, marginBottom: 2 }}>Advanced</div>
          <SideBtn
            T={T}
            label={`${showManualFields ? "▲" : "▼"} Manual fields`}
            onClick={() => setShowManualFields(o => !o)}
            variant={showManualFields ? "accent" : "ghost"}
            title={showManualFields ? "Hide manual fields" : "Show manual fields (% Total, Payout delay, Reserve, Avg COGS, Ad cost, Stock override)"}
          />
        </>
      )}
    </div>
  );
}

// ── Marketplace carousel ─────────────────────────────────────────────────────
function MktCarousel({ displayMkts, selectedIdx, selectedMktId, setSelectedMktId, marketplaces, mktOps, T, iconEditMktId, setIconEditMktId }) {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const CARD_W = isMobile ? 220 : 320;
  const CARD_H = isMobile ? 150 : 180;
  const VPAD = 0;
  const CAROUSEL_H = CARD_H + VPAD * 2;
  const STEP = CARD_W * 0.65;
  const innerW = Math.max(CARD_W + (displayMkts.length - 1) * STEP + 80, 500);

  // Use a ref so we can attach a non-passive wheel listener.
  // React synthetic onWheel is passive in modern browsers -- preventDefault() is ignored,
  // causing page scroll even when we handle the event.
  const containerRef = useRef(null);
  const stateRef = useRef({ selectedIdx, displayMkts, setSelectedMktId });
  stateRef.current = { selectedIdx, displayMkts, setSelectedMktId };

  // Accumulator for trackpad — only advance when enough delta has built up
  const accumRef = useRef(0);
  const lastAdvanceRef = useRef(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    function onWheel(e) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      e.preventDefault();

      const { selectedIdx, displayMkts, setSelectedMktId } = stateRef.current;
      const now = Date.now();

      // Detect trackpad vs mouse wheel: trackpad sends many small deltaY values,
      // mouse sends a few large ones (usually ≥ 100).
      const isTrackpad = Math.abs(e.deltaY) < 50;

      if (isTrackpad) {
        accumRef.current += e.deltaY;
        // Only advance after 80px of accumulated trackpad movement, and not too fast
        const threshold = 80;
        if (Math.abs(accumRef.current) >= threshold && now - lastAdvanceRef.current > 400) {
          const dir = accumRef.current > 0 ? 1 : -1;
          accumRef.current = 0;
          lastAdvanceRef.current = now;
          const next = selectedIdx + dir;
          if (next >= 0 && next < displayMkts.length) setSelectedMktId(displayMkts[next].id);
        }
      } else {
        // Mouse wheel — immediate, but throttle to 350ms
        if (now - lastAdvanceRef.current < 350) return;
        lastAdvanceRef.current = now;
        accumRef.current = 0;
        const canGoNext = e.deltaY > 0 && selectedIdx < displayMkts.length - 1;
        const canGoPrev = e.deltaY < 0 && selectedIdx > 0;
        if (canGoNext) setSelectedMktId(displayMkts[selectedIdx + 1].id);
        else if (canGoPrev) setSelectedMktId(displayMkts[selectedIdx - 1].id);
      }
    }
    el.addEventListener("wheel", onWheel, { passive: false });

    // Touch swipe for mobile
    let touchStartX = null;
    function onTouchStart(e) { touchStartX = e.touches[0].clientX; }
    function onTouchEnd(e) {
      if (touchStartX === null) return;
      const dx = touchStartX - e.changedTouches[0].clientX;
      touchStartX = null;
      if (Math.abs(dx) > 40) {
        const { selectedIdx, displayMkts, setSelectedMktId } = stateRef.current;
        const next = dx > 0 ? selectedIdx + 1 : selectedIdx - 1;
        if (next >= 0 && next < displayMkts.length) setSelectedMktId(displayMkts[next].id);
      }
    }
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  // Edge-fade mask: tiles far from the center get clipped to transparent so
  // they don't spill into the left / right action columns that flank the
  // carousel. Without it the ±2 / ±3 tiles visibly overlap the button
  // stacks at wide viewports.
  const edgeMask = "linear-gradient(to right, transparent 0, #000 12%, #000 88%, transparent 100%)";

  return (
    <div style={{ marginBottom: 20 }}>
      <div
        ref={containerRef}
        style={{
          position: "relative",
          height: CAROUSEL_H,
          overflow: isMobile ? "hidden" : "visible",
          maxWidth: "100%",
          WebkitMaskImage: edgeMask,
          maskImage: edgeMask,
        }}
      >
          {displayMkts.map((mkt, i) => {
            const idx = marketplaces.indexOf(mkt);
            const color = mkt.color || mktColor(idx);
            return (
              <MktCard key={mkt.id} mkt={mkt} color={color}
                isSelected={mkt.id === selectedMktId} position={i - selectedIdx}
                onClick={() => setSelectedMktId(mkt.id)}
                T={T}/>
            );
          })}
      </div>

      {/* Marketplace icon editor */}
      {iconEditMktId && (() => {
        const mkt = marketplaces.find(m => m.id === iconEditMktId);
        if (!mkt) return null;
        return createPortal(
          <div onClick={()=>setIconEditMktId(null)} style={{ position:"fixed",inset:0,zIndex:10500,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.4)" }}>
            <div onClick={e=>e.stopPropagation()} style={{ background:T.card,border:"1px solid "+T.border,borderRadius:RADIUS.xl,padding:24,boxShadow:"0 20px 60px rgba(0,0,0,0.5)",maxWidth:420,width:"92vw",maxHeight:"80vh",overflow:"auto" }}>
              <div style={{ fontSize:14,fontWeight:700,color:T.text,marginBottom:16 }}>Change icon for {mkt.name}</div>
              <div style={{ display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8,marginBottom:16 }}>
                {["🏪","🏬","🛒","🛍️","🏢",
                  "🏦","🏨","🏠","🏗️","🏭",
                  "💻","🌐","📱","🖥️","🔗",
                  "📦","🎁","💼","📊","🏷️",
                  "⭐","💎","🔥","🚀","🏆"].map(ic=>(
                  <button key={ic} onClick={()=>{ mktOps?.update(mkt.id,{icon:ic}); setIconEditMktId(null); }}
                    style={{ fontSize:26,padding:"8px",borderRadius:RADIUS.sm,cursor:"pointer",
                      background:mkt.icon===ic?T.accent+"22":"transparent",
                      border:"1px solid "+(mkt.icon===ic?T.accent:T.border),lineHeight:1,
                      display:"flex",alignItems:"center",justifyContent:"center" }}>
                    {ic}
                  </button>
                ))}
              </div>
              <button onClick={()=>setIconEditMktId(null)} style={{ width:"100%",padding:"10px",borderRadius:RADIUS.md,background:"transparent",border:"1px solid "+T.border,color:T.textSub,cursor:"pointer",fontFamily:FONT,fontSize:13 }}>Cancel</button>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* Dots + scroll hint — immediately below card, full-width centered */}
      <div style={{
        display:"flex", flexDirection:"column", alignItems:"center", gap:4,
        padding:"4px 0 8px",
        borderBottom:`1px solid ${T.border}`,
        marginBottom:0,
      }}>
        <div style={{ display:"inline-flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:11, color:T.textSub, opacity:0.4 }}>←</span>
          {displayMkts.map((mkt, i) => {
            const c = mkt.color || mktColor(marketplaces.indexOf(mkt));
            return (
              <div key={mkt.id} onClick={() => setSelectedMktId(mkt.id)}
                style={{ width:i===selectedIdx?18:6, height:6, borderRadius:3,
                  background:i===selectedIdx?c:T.border,
                  cursor:"pointer", transition:`all 0.3s ${SPRING}` }}/>
            );
          })}
          <span style={{ fontSize:11, color:T.textSub, opacity:0.4 }}>→</span>
        </div>
        <div style={{ fontSize:10, color:T.textSub, opacity:0.35, letterSpacing:0.2 }}>
          scroll or use arrow keys to navigate
        </div>
      </div>
    </div>
  );
}

// ── Page intro card ───────────────────────────────────────────────────────────
function IntroCard({ T }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ marginBottom:20 }}>
      {/* Collapsed: single compact row */}
      <div style={{ display:"flex", alignItems:"center", gap:10,
        background:"transparent", border:"none",
        padding:"8px 4px", cursor:"pointer" }}
        onClick={()=>setExpanded(o=>!o)}>
        <span style={{ fontSize:18, flexShrink:0 }}>🏪</span>
        <div style={{ flex:1, minWidth:0 }}>
          <span style={{ fontSize:13, fontWeight:700, color:T.text }}>Marketplaces — your income engine</span>
          <span style={{ fontSize:12, color:T.textSub, marginLeft:8 }}>
            Set payout/unit and % of income per channel · feeds cashflow automatically
          </span>
        </div>
        <button style={{ background:"none", border:"none", color:T.accent,
          fontSize:12, fontWeight:600, cursor:"pointer", padding:0, fontFamily:FONT, flexShrink:0 }}>
          {expanded ? "▲ Show less" : "▼ Learn how this works"}
        </button>
      </div>
      {/* Expanded: full explanation */}
      {expanded && (
        <div style={{ background:"transparent",
          padding:"8px 4px 14px", fontSize:13, color:T.textSub, lineHeight:1.7 }}>
          <div style={{ marginBottom:8 }}>
            Each marketplace represents a sales channel. You tell CashHorizon two things:
            <strong style={{color:T.accent}}> % of total income</strong> this channel contributes,
            and your average <strong style={{color:T.green}}>payout per unit sold</strong>.
            CashHorizon then multiplies your monthly unit targets × payout/unit = monthly income for your cashflow.
          </div>
          <div style={{ marginBottom:8 }}>
            <strong style={{color:T.text}}>Products</strong> let you split a marketplace's income between
            different product lines. Each product gets a % share of that marketplace's revenue and its own payout/unit.
            All products in a marketplace must add up to 100%.
          </div>
          <div style={{ marginBottom:8 }}>
            <strong style={{color:T.text}}>Payout delay</strong> shifts when income appears in your cashflow —
            e.g. Amazon pays every 2 weeks (14 days), Shopify pays in 2-3 days.
          </div>
          <div style={{ marginBottom:8 }}>
            <strong style={{color:T.text}}>PPC & Refunds</strong> are automatically deducted from payout
            to show your true net income — what actually lands in your bank account after advertising and returns.
          </div>
          <div>
            <strong style={{color:T.text}}>Global vs Month override:</strong> Global settings apply to all months.
            Use Month override to set different values for a specific month — e.g. higher payout in Q4 due to promotions.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── GroupRow ────────────────────────────────────────────────────────────────
// Renders a group as a parent row (Sellerboard-style). Click → expands and
// reveals child ProductRows nested below. The header checkbox selects/deselects
// every child product in one go. Aggregated stats (units share, avg payout)
// are computed from member products via aggregateGroupStats.
function GroupRow({
  group, mkt, isGlobal, mktOps, monthOps, settingsMonth,
  setLastEditedId, lastEditedId, saveStatus,
  T, globalSimpleMode,
  isExpanded, onToggleExpand,
  childProducts, selectedProductIds, selectedProductIdsSet, onToggleSelectMany,
  onConfirmDeleteGroup, onConfirmDeleteProduct, onDecoupleProduct, showConfirm, productsWithMonthOverrides, globalMkt,
  salesWindow = 30, leadTime = 14,
  // Prompt-dialog state setter from the parent (Marketplaces). Optional —
  // when undefined, rename-group / rename-product buttons render but their
  // click handlers early-return without throwing. Clean wire-through
  // pending a broader prop-threading sweep.
  setPromptDialog,
  // Realized per-unit payout Map<productId, {perUnit, basis, units}> computed
  // once in the parent. Optional; ProductRow gracefully falls back to list
  // price when the entry is missing.
  realizedPayoutMap = null,
  // Which identity field (name or sku) the user wants primary on each row.
  primaryIdentity = "name",
  // Phase 7 — child rows render via SlimProductRow; the heavy ProductRow
  // is no longer used here. Edit pivots to Catalog with highlight.
  onEditProduct,
  onToggleProductSync,
}) {
  // Guard: if the parent didn't wire setPromptDialog through yet, silently
  // no-op the click handlers below rather than throw ReferenceError.
  setPromptDialog = setPromptDialog || (() => {});
  const stats = aggregateGroupStats(mkt, group);
  // Stockout aggregation for the group header
  const groupStockout = groupStockoutInfo(childProducts || [], salesWindow);
  // Group is "selected" if every child is selected. Set lookups are O(1).
  const selSet = selectedProductIdsSet || new Set(selectedProductIds || []);
  const allSelected = childProducts.length > 0 && childProducts.every(p => selSet.has(p.id));
  const someSelected = !allSelected && childProducts.some(p => selSet.has(p.id));

  // Group-level ad cost editor — patches the group in place via mktOps.update.
  // Persists alongside the rest of the productGroups array on the marketplace.
  const updateGroup = (patch) => {
    const updated = (mkt.productGroups || []).map(g => g.id === group.id ? { ...g, ...patch } : g);
    mktOps.update(mkt.id, { productGroups: updated });
  };
  const groupAdType = group.adCostType || "pct";
  const groupAdValue = groupAdType === "pct"
    ? (group.adCostRate ? Math.round(group.adCostRate * 100) : "")
    : (group.adCostAmount || "");
  const handleHeaderCheckbox = () => {
    const childIds = childProducts.map(p => p.id);
    if (allSelected) {
      onToggleSelectMany(childIds, false);
    } else {
      onToggleSelectMany(childIds, true);
    }
  };
  return (
    <div style={{
      borderRadius: RADIUS.md, overflow: "hidden", background: T.card,
      border: `1.5px solid ${T.accent}55`,
      boxShadow: isExpanded ? `0 4px 18px ${T.accent}22` : "none",
      transition: `all 0.22s ${SPRING}`,
    }}>
      {/* Group header */}
      <div onClick={onToggleExpand} style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "12px 16px", cursor: "pointer",
        background: T.accent + "08",
      }}>
        {isGlobal && (
          <input
            type="checkbox"
            checked={allSelected}
            ref={el => { if (el) el.indeterminate = someSelected; }}
            onChange={e => { e.stopPropagation(); handleHeaderCheckbox(); }}
            onClick={e => e.stopPropagation()}
            title="Select all products in this group"
            style={{ cursor: "pointer", flexShrink: 0, accentColor: T.accent }}
          />
        )}
        <span style={{ fontSize: 14, color: T.accent, transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.18s", display: "inline-block", width: 12 }}>▶</span>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: T.accent, flexShrink: 0 }}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>👪 {group.name}</span>
            <span style={{ fontSize: 10, color: T.accent, fontWeight: 700, padding: "1px 8px", borderRadius: 8, background: T.accent + "15", border: `1px solid ${T.accent}40` }}>
              {stats.productCount} {stats.productCount === 1 ? "product" : "products"}
            </span>
          </div>
          <div style={{ display: "flex", gap: 14, marginTop: 3, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: T.textSub }}>Group share: <strong style={{ color: T.text }}>{(stats.totalPctOfMarket * 100).toFixed(1)}%</strong></span>
            <span style={{ fontSize: 11, color: T.textSub }}>Avg payout: <strong style={{ color: T.green }}>{currSym()}{stats.avgPayout.toFixed(2)}</strong></span>
            {stats.avgDeliveryCost > 0 && (
              <span style={{ fontSize: 11, color: T.textSub }}>Avg delivery: <strong style={{ color: T.text }}>{currSym()}{stats.avgDeliveryCost.toFixed(2)}</strong></span>
            )}
            {isGlobal && groupStockout.connected && (
              <StockoutStrip info={groupStockout} T={T}
                windowDays={salesWindow} leadTime={leadTime}
                title={groupStockout.firstToOOS
                  ? `First to run out: ${groupStockout.firstToOOS.productName} on ${groupStockout.oosDate} · lead time ${leadTime}d`
                  : `Group stockout forecast · ${salesWindow}d window · lead time ${leadTime}d`} />
            )}
          </div>
        </div>
        {isGlobal && (
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            {/* Group-level COG — fits in the COG hierarchy below marketplace and
                above per-product. Single number (not the full {productCost,shipping,
                customs,prep} breakdown — keep it simple at the parent level). */}
            <Tooltip content="Group-level COG (cost per unit). Hierarchy: pool > marketplace > group > per-product. Setting this overrides per-product COGS for products in THIS group only. Used by Money in Inventory and Runway when the pool and marketplace COGs are unset. Leave at 0 to fall back to per-product COGS.">
              <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 6px", borderRadius: RADIUS.sm, background: T.orange + "08", border: `1px solid ${T.orange}30` }}>
                <span style={{ fontSize: 9, color: T.orange, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>COG</span>
                <span style={{ fontSize: 11, color: T.textSub }}>{currSym()}</span>
                <input type="number" value={group.cogs || ""} placeholder="0"
                  onChange={e => updateGroup({ cogs: parseFloat(e.target.value) || 0 })}
                  style={{ background: T.surface, border: `1px solid ${T.orange}44`, borderRadius: RADIUS.sm, color: T.orange, fontSize: 12, fontWeight: 700, padding: "2px 6px", width: 50, outline: "none", fontFamily: FONT, textAlign: "center" }}/>
              </div>
            </Tooltip>
            {/* Group-level ad cost — overrides marketplace, overridden by per-product. */}
            <Tooltip content="Group-level advertising cost. Applied to every product in this group unless that product has its own per-product ad cost set. Overrides the marketplace-level default. Pick % of payout or fixed $ per unit.">
              <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 6px", borderRadius: RADIUS.sm, background: "#BF5AF208", border: `1px solid #BF5AF230` }}>
                <span style={{ fontSize: 9, color: "#BF5AF2", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>Ads</span>
                <Seg value={groupAdType} options={[["pct","%"],["fixed",currSym()]]} onChange={v => updateGroup({ adCostType: v })} color="#BF5AF2"/>
                {groupAdType === "pct"
                  ? <input type="number" value={groupAdValue} placeholder="0"
                      onChange={e => { const v = parseFloat(e.target.value) || 0; updateGroup({ adCostRate: v / 100, adCostType: "pct" }); }}
                      style={{ background: T.surface, border: `1px solid #BF5AF244`, borderRadius: RADIUS.sm, color: "#BF5AF2", fontSize: 12, fontWeight: 700, padding: "2px 6px", width: 44, outline: "none", fontFamily: FONT, textAlign: "center" }}/>
                  : <input type="number" value={groupAdValue} placeholder="0"
                      onChange={e => { const v = parseFloat(e.target.value) || 0; updateGroup({ adCostAmount: v, adCostType: "fixed" }); }}
                      style={{ background: T.surface, border: `1px solid #BF5AF244`, borderRadius: RADIUS.sm, color: "#BF5AF2", fontSize: 12, fontWeight: 700, padding: "2px 6px", width: 50, outline: "none", fontFamily: FONT, textAlign: "center" }}/>}
              </div>
            </Tooltip>
            <button
              onClick={() => {
                setPromptDialog({
                  title: "Rename group",
                  placeholder: "Group name",
                  defaultValue: group.name,
                  onConfirm: (newName) => {
                    if (newName !== group.name) {
                      const updated = renameGroup(mkt, group.id, newName);
                      mktOps.update(mkt.id, { productGroups: updated.productGroups });
                    }
                    setPromptDialog(null);
                  },
                });
              }}
              title="Rename group"
              style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 12, color: T.textSub, padding: "4px 6px" }}
            >✏</button>
            <button
              onClick={() => onConfirmDeleteGroup(group)}
              title="Delete group (products are kept)"
              style={{ background: "transparent", border: `1px solid ${T.border}`, borderRadius: RADIUS.sm, cursor: "pointer", fontSize: 10, color: T.textSub, padding: "3px 8px", fontFamily: FONT, fontWeight: 600 }}
            >Ungroup</button>
          </div>
        )}
      </div>
      {/* Child products */}
      {isExpanded && (
        <div style={{
          background: T.surface,
          padding: "8px 8px 8px 32px",
          borderTop: `1px solid ${T.accent}22`,
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          {childProducts.length === 0 ? (
            <div style={{ padding: "12px", textAlign: "center", fontSize: 12, color: T.textSub }}>
              No products in this group
            </div>
          ) : childProducts.map(prod => (
            <SlimProductRow
              key={prod.id}
              prod={prod} mkt={mkt} T={T}
              isSelected={selSet.has(prod.id)}
              onToggleSelect={(pid) => onToggleSelectMany([pid], !selSet.has(pid))}
              selectMode={true}
              primaryIdentity={primaryIdentity}
              onToggleSync={(nextSyncOn) => onToggleProductSync?.(prod, nextSyncOn)}
              onEdit={() => onEditProduct?.(prod)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Compute per-marketplace KPI values for the dashboard card row ──────────
// Pulled into its own helper so the dashboard component stays declarative.
function computeMktKpis(mkt, orders) {
  if (!mkt) return { cashBlockedStock: 0, totalStock: 0, avgMonthlyPayout: 0 };
  const products = mkt.products || [];
  let cashBlockedStock = 0;
  let totalStock = 0;
  for (const p of products) {
    if (p?._decoupled || p?.sandbox) continue;
    const stock = Number(p.currentStock) || Number(p.apiStock) || 0;
    const cogs = p.cogs || {};
    const landed = (Number(cogs.productCost) || 0) + (Number(cogs.shipping) || 0)
                 + (Number(cogs.customs) || 0) + (Number(cogs.prep) || 0);
    cashBlockedStock += stock * landed;
    totalStock += stock;
  }
  // Avg monthly payout ≈ per-unit payout × (recent units / windowDays) × 30.
  // Falls back to per-unit × recentSalesVolume when no window is recorded.
  const perUnit = payoutForMkt(mkt);
  const recent = Number(mkt.recentSalesVolume || mkt._recentSalesVolume || 0);
  const winDays = Number(mkt.windowDays) || 30;
  const dailyUnits = recent / Math.max(1, winDays);
  const avgMonthlyPayout = perUnit * dailyUnits * 30;
  return { cashBlockedStock, totalStock, avgMonthlyPayout };
}

// ── Dashboard card row — replaces the carousel + side columns ──────────────
// Renders a chip strip (one chip per marketplace, accent-coloured) plus a
// horizontal row of square cards mixing action and KPI cards. Each KPI is
// scoped to the currently-selected marketplace.
function MktDashboard({
  T, marketplaces, selectedMkt, selectedMktId, setSelectedMktId,
  salesWindow, setSalesWindow,
  onConnect, onDisconnect, onRecompute, onTrySample,
  onAddProduct, onLaunchProduct, onToggleFulfilment, showFBM,
  onAddMarketplace, currSymStr, orders,
}) {
  const connected = !!selectedMkt?.apiConnection?.connected;
  const kpis = useMemo(() => computeMktKpis(selectedMkt, orders), [selectedMkt, orders]);
  if (!selectedMkt) return null;
  const fmtMoney = (n) => `${currSymStr}${Math.round(Number(n) || 0).toLocaleString()}`;
  const fmtInt = (n) => `${Math.round(Number(n) || 0).toLocaleString()}`;
  const reserves = Number(selectedMkt.apiConnection?.reservesHeld) || Number(selectedMkt.reserveHeld) || 0;
  const productsCount = (selectedMkt.products || []).filter(p => !p?.sandbox).length;
  const payout = payoutForMkt(selectedMkt);
  const avgCogs = Number(selectedMkt.avgCogs) || 0;
  const payoutDelay = Number(selectedMkt.payoutDelay) || 0;
  const pctOfTotal = Number(selectedMkt.pctOfTotal) || 0;

  // Common card chrome — square-ish, rounded, tinted background + colored
  // border. Action cards add a hover lift; KPI cards stay still.
  const ActionCard = ({ id, emoji, label, sublabel, color, onClick, disabled, children }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      style={{
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
        gap: 4, width: 130, minHeight: 96,
        padding:"10px 12px", borderRadius: RADIUS.lg,
        background: color + "14", border: `1.5px solid ${color}55`,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1, fontFamily: FONT,
        transition: "transform 0.15s, background 0.15s, box-shadow 0.15s",
      }}
      onMouseEnter={e => { if (!disabled) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.background = color + "22"; } }}
      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.background = color + "14"; }}
    >
      <span style={{ fontSize: 22, lineHeight: 1 }}>{emoji}</span>
      <span style={{ fontSize: 11, fontWeight: 700, color, letterSpacing: 0.3, textAlign: "center", lineHeight: 1.2 }}>{label}</span>
      {sublabel && <span style={{ fontSize: 10, color: T.textSub, fontWeight: 500 }}>{sublabel}</span>}
      {children}
    </button>
  );
  const KpiCard = ({ id, label, value, color, sub }) => (
    <div style={{
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      gap: 4, width: 130, minHeight: 96,
      padding:"10px 12px", borderRadius: RADIUS.lg,
      background: color + "10", border: `1.5px solid ${color}40`,
      fontFamily: FONT,
    }}>
      <span style={{ fontSize: 9, fontWeight: 700, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.4, textAlign: "center" }}>{label}</span>
      <span style={{ fontSize: 20, fontWeight: 800, color, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>{value}</span>
      {sub && <span style={{ fontSize: 10, color: T.textSub }}>{sub}</span>}
    </div>
  );

  return (
    <div style={{ marginTop: 16, marginBottom: 20 }}>
      {/* Marketplace selector chip strip — one chip per marketplace.
          Replaces the old MktCard carousel; the selected chip's color
          mirrors the marketplace's accent. */}
      <div style={{
        display:"flex", gap: 8, flexWrap:"wrap", justifyContent:"center",
        marginBottom: 16,
      }}>
        {marketplaces.map((m, idx) => {
          const isSel = m.id === selectedMktId;
          const c = m.color || mktColor(idx) || T.accent;
          return (
            <button key={m.id} onClick={() => setSelectedMktId(m.id)}
              style={{
                display:"inline-flex", alignItems:"center", gap: 8,
                padding:"7px 14px", borderRadius: RADIUS.pill,
                background: isSel ? c + "22" : "transparent",
                border: `1.5px solid ${isSel ? c : T.border}`,
                color: isSel ? c : T.textSub,
                fontFamily: FONT, fontSize: 12, fontWeight: isSel ? 700 : 500,
                cursor:"pointer", whiteSpace:"nowrap",
                transition:"all 0.15s",
              }}>
              <span style={{ width: 8, height: 8, borderRadius:"50%", background: c, flexShrink: 0 }}/>
              {m.name || m.id}
            </button>
          );
        })}
        {onAddMarketplace && (
          <button onClick={onAddMarketplace}
            style={{
              padding:"7px 14px", borderRadius: RADIUS.pill,
              background:"transparent", border:`1.5px dashed ${T.border}`,
              color: T.textSub, fontFamily: FONT, fontSize: 12, fontWeight: 600,
              cursor:"pointer",
            }}>+ Add</button>
        )}
      </div>

      {/* Horizontal card row — scrolls on narrow viewports. Cards are
          rendered in the order the user specified: Connect → Share window
          → Sample data → Payout → Products → Cash in reserve → Cash
          blocked in stock → Avg monthly payout → Avg COGS → Total stock
          → Payout delay → % Total → Add fulfilment cost → Launch new
          product. */}
      <div style={{
        display:"flex", gap: 10, overflowX:"auto", paddingBottom: 6,
        scrollbarWidth: "thin",
      }}>
        <ActionCard id="connect"
          emoji={connected ? "✓" : "🔌"}
          label={connected ? "Connected" : "Connect"}
          sublabel={connected ? "Tap to disconnect" : selectedMkt.platform || "API"}
          color={connected ? "#10b981" : "#8b5cf6"}
          onClick={connected ? onDisconnect : () => onConnect?.(selectedMkt.platform, selectedMkt.id)}
        />
        <div style={{
          display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
          gap: 4, width: 150, minHeight: 96,
          padding:"10px 12px", borderRadius: RADIUS.lg,
          background: "#06b6d4" + "14", border: `1.5px solid ${"#06b6d4"}55`,
          fontFamily: FONT,
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#06b6d4", letterSpacing: 0.3 }}>📊 Share window</span>
          <select value={salesWindow}
            onChange={e => setSalesWindow(parseInt(e.target.value, 10))}
            style={{
              background: T.card, border:`1px solid ${T.border}`, borderRadius: RADIUS.sm,
              color: T.text, fontSize: 11, fontWeight: 600, fontFamily: FONT,
              padding:"3px 6px", outline:"none", cursor:"pointer", width: "100%",
            }}>
            {[7, 14, 30, 60, 90].map(d => (
              <option key={d} value={d}>Last {d}d</option>
            ))}
          </select>
          <button onClick={onRecompute}
            style={{
              padding:"3px 8px", borderRadius: RADIUS.sm,
              background: "#06b6d4" + "22", border:`1px solid ${"#06b6d4"}55`,
              color: "#06b6d4", fontSize: 10, fontWeight: 700, fontFamily: FONT,
              cursor:"pointer", whiteSpace:"nowrap",
            }}>↻ Recompute</button>
        </div>
        <ActionCard id="sample" emoji="🧪" label="Sample data"
          sublabel="Try fixtures" color="#a855f7" onClick={onTrySample}/>

        <KpiCard id="payout" label="Payout / unit" value={fmtMoney(payout)} color="#10b981"/>
        <KpiCard id="products" label="Products" value={fmtInt(productsCount)} color="#06b6d4"/>
        <KpiCard id="reserve" label="Cash in reserve" value={fmtMoney(reserves)} color="#f59e0b"/>
        <KpiCard id="blocked" label="Cash blocked in stock" value={fmtMoney(kpis.cashBlockedStock)} color="#ef4444"/>
        <KpiCard id="avgPayout" label="Avg monthly payout" value={fmtMoney(kpis.avgMonthlyPayout)} color="#10b981"/>
        <KpiCard id="avgCogs" label="Avg COGS" value={fmtMoney(avgCogs)} color="#94a3b8"/>
        <KpiCard id="totalStock" label="Total stock" value={fmtInt(kpis.totalStock)} color="#06b6d4" sub="units"/>
        <KpiCard id="payoutDelay" label="Payout delay" value={`${payoutDelay}d`} color="#94a3b8"/>
        <KpiCard id="pctTotal" label="% of brand total" value={`${(pctOfTotal * 100).toFixed(0)}%`} color="#8b5cf6"/>

        <ActionCard id="fulfilment" emoji="🏭" label="Add fulfilment cost"
          sublabel={showFBM ? "Panel open" : "Open panel"}
          color="#f59e0b" onClick={onToggleFulfilment}/>
        <ActionCard id="launch" emoji="🚀" label="Launch new product"
          sublabel="Forecast + cogs" color="#8b5cf6" onClick={onLaunchProduct}/>
      </div>
    </div>
  );
}

// ── Slim product row — Phase 7 ─────────────────────────────────────────────
// Replaces the heavy ProductRow inline editor on the Marketplaces page.
// Markets becomes a dashboard surface: this row is just a thumbnail + the
// product's name/SKU, a sync/unsync toggle (same look-and-feel as the
// catalog), and an Edit button that pivots the user to the Catalog page
// with the matching row scrolled + glowed + auto-expanded.
function SlimProductRow({
  prod, mkt, T, isSelected, onToggleSelect, selectMode,
  onToggleSync, onEdit, primaryIdentity = "name",
}) {
  const syncOn = !prod?._decoupled;
  const wantSku = primaryIdentity === "sku";
  const hasSku = !!prod?.sku;
  const skuPrimary = wantSku && hasSku;
  const primary = skuPrimary ? prod.sku : (prod?.name || "Unnamed");
  const secondary = skuPrimary ? prod?.name : (hasSku ? prod.sku : "");
  // Image: explicit user override > API snapshot. Falls back to a small
  // placeholder so empty products still render a consistent layout.
  const imageUrl = prod?.productData?.imageUrl
    || prod?.imageUrl
    || prod?._apiSnapshot?.imageUrl
    || prod?._apiSnapshot?.image_url
    || prod?._apiSnapshot?.image
    || null;
  return (
    <div style={{
      display:"flex", alignItems:"center", gap: 12,
      padding:"10px 14px", borderRadius: RADIUS.md,
      background: T.card, border:`1px solid ${T.border}`,
      opacity: syncOn ? 1 : 0.65, transition: "opacity 0.15s",
    }}>
      {selectMode && (
        <input type="checkbox" checked={!!isSelected}
          onChange={() => onToggleSelect?.(prod.id)}
          onClick={e => e.stopPropagation()}
          style={{ accentColor: T.accent, cursor:"pointer", flexShrink: 0 }}
          title="Select for bulk operations"/>
      )}
      {/* Thumbnail (40 px circle) */}
      <div style={{
        width: 40, height: 40, borderRadius:"50%", flexShrink: 0,
        background: imageUrl ? "transparent" : T.surface,
        border:`1.5px solid ${T.border}`,
        overflow:"hidden",
        display:"inline-flex", alignItems:"center", justifyContent:"center",
      }}>
        {imageUrl
          ? <img src={imageUrl} alt="" aria-hidden="true" loading="lazy"
              style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
          : <span style={{ fontSize: 14, color: T.textSub, opacity: 0.6 }}>📷</span>}
      </div>
      {/* Name + secondary chip */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: T.text,
          fontFamily: skuPrimary ? "monospace" : FONT,
          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
        }}>{primary}</div>
        {secondary && (
          <div style={{
            fontSize: 11, color: T.textSub, marginTop: 2,
            fontFamily: skuPrimary ? FONT : "monospace",
            whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
          }}>{secondary}</div>
        )}
      </div>
      {/* Sync toggle — same visual style as the Catalog page's SyncToggle. */}
      <button
        onClick={() => onToggleSync?.(!syncOn)}
        title={syncOn
          ? "Counts in marketplace payout · click to disable sync"
          : "Excluded from marketplace payout · click to enable sync"}
        style={{
          display:"inline-flex", alignItems:"center", gap: 8,
          padding:"5px 12px", borderRadius: RADIUS.pill,
          background: syncOn ? "#10b981" + "1f" : T.surface,
          border:`1px solid ${syncOn ? "#10b981" + "55" : T.border}`,
          color: syncOn ? "#10b981" : T.textSub,
          fontFamily: FONT, fontSize: 11, fontWeight: 700,
          cursor:"pointer", flexShrink: 0,
        }}>
        <span style={{
          display:"inline-block", width: 22, height: 12, borderRadius: 7,
          background: syncOn ? "#10b981" : T.border, position:"relative",
          transition:"background 0.15s",
        }}>
          <span style={{
            position:"absolute", top: 1, left: syncOn ? 11 : 1, width: 10, height: 10,
            borderRadius:"50%", background:"#fff", transition:"left 0.15s",
          }}/>
        </span>
        {syncOn ? "Sync ON" : "Sync OFF"}
      </button>
      {/* Edit button — navigates to Catalog and auto-scrolls + glows + expands the row */}
      <button
        onClick={onEdit}
        title="Edit this product on the Catalog page"
        style={{
          display:"inline-flex", alignItems:"center", gap: 6,
          padding:"6px 14px", borderRadius: RADIUS.pill,
          background: T.accent + "1f", border:`1px solid ${T.accent}55`,
          color: T.accent, fontFamily: FONT, fontSize: 11, fontWeight: 700,
          cursor:"pointer", flexShrink: 0,
          transition:"background 0.15s",
        }}
        onMouseEnter={e => e.currentTarget.style.background = T.accent + "33"}
        onMouseLeave={e => e.currentTarget.style.background = T.accent + "1f"}
      >✏ Edit</button>
    </div>
  );
}

export default function Marketplaces({ marketplaces, months, mktOps, monthOps, visibleYears, saveStatus, navContext, fbmSettings, setFbmSettings, deliveryCreditLimit, setDeliveryCreditLimit, onAddDecision, dataKey="", can, isOwner, profitCards, onNavigate, inventoryPools=[], poolOps, reloadData, hashQuery, orders=[] }) {
  const T = useTheme();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const TODAY = useMemo(()=>new Date(),[]);
  const [selectedMktId, _setSelectedMktId] = useState(() => LS.get(LS.SELECTED_MKT_ID, ""));
  function setSelectedMktId(id) {
    _setSelectedMktId(id);
    if (id) LS.set(LS.SELECTED_MKT_ID, id);
  }
  // Phase 7 — settingsMonth + showMonthPicker removed; Marketplaces no
  // longer surfaces per-month editing. Keep these as no-op constants so
  // the existing call sites (ProductRow, GroupRow) don't error out
  // before they're rewritten in the same pass.
  const settingsMonth = "global";
  const [searchQuery, setSearchQuery] = useState("");
  const [lastEditedId, setLastEditedId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Custom prompt dialog state — replaces window.prompt for group create/rename
  // Shape: { title, placeholder, defaultValue, onConfirm: (value)=>void } | null
  const [promptDialog, setPromptDialog] = useState(null);
  const [showAddMktModal, setShowAddMktModal] = useState(false);
  const [newMktName, setNewMktName] = useState("");
  const [newMktIcon, setNewMktIcon] = useState("store");
  const [newMktColor, setNewMktColor] = useState(MKT_COLORS[0]);
  const [newMktFrom, setNewMktFrom] = useState(mkKey(TODAY.getFullYear(), TODAY.getMonth()+1));
  const [newMktCopyFrom, setNewMktCopyFrom] = useState("");
  const [newMktImage, setNewMktImage] = useState(null);
  const [newMktBrand, setNewMktBrand] = useState(""); // brand key for new marketplace
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [editPoolId, setEditPoolId] = useState(null);
  const [connectSheet, setConnectSheet] = useState(null); // {platform, mktId}
  const [connectInput, setConnectInput] = useState({ shopDomain:"", clientId:"", clientSecret:"" });
  const [connecting, setConnecting] = useState(false);
  const [syncingMktId, setSyncingMktId] = useState(null);
  const [syncSummary, setSyncSummary] = useState(null); // {summary, errors} from latest sync
  // Which marketplace has its channel-filter panel open. Only one at a time.
  const [channelPanelMktId, setChannelPanelMktId] = useState(null);
  const postOAuthHandledRef = useRef(false);
  // Phase 2: sales window for auto-share calc (7/14/30/60/90 days).
  // Persisted so the selection survives refresh / navigation. Shared key
  // with the MarketplaceDataAudit page so both views agree on the default.
  const [salesWindow, setSalesWindow] = useState(() => {
    const v = parseInt(LS.get?.("sf_mkt_sales_window", "") || "", 10);
    return Number.isFinite(v) && v > 0 ? v : DEFAULT_SALES_WINDOW;
  });
  useEffect(() => { try { LS.set?.("sf_mkt_sales_window", String(salesWindow)); } catch {} }, [salesWindow]);
  // Effective supplier lead time — mirrors Targets.jsx logic:
  //   customLeadTime (localStorage "sf_t_custLead") wins when set,
  //   otherwise fall back to the last order's lead time from aggregateLeadTimes.
  // Used for the StockoutStrip severity coloring so red ≡ "you can't
  // reorder in time" rather than a hardcoded 14-day threshold.
  const effectiveLeadTime = useMemo(() => {
    let custom = 0;
    try { custom = parseInt(localStorage.getItem("sf_t_custLead")) || 0; } catch {}
    if (custom > 0) return custom;
    const agg = aggregateLeadTimes(orders || []);
    return agg.last ?? agg.avg ?? 30;
  }, [orders]);
  // Phase 5: per-marketplace product selection state for grouping
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  // Per-group expand/collapse state. Stored as array (not Set) so it survives
  // re-renders cleanly. Group IDs are unique across marketplaces.
  const [expandedGroupIds, setExpandedGroupIds] = useState([]);
  // Product list sort. "default" preserves author order; other modes sort by
  // share, price (payout), or recent-window unit sales. Persisted so the
  // user's last choice survives refreshes.
  const [productSort, setProductSort] = useState(() => LS.get?.("sf_mkt_product_sort", "default") || "default");
  useEffect(() => { try { LS.set?.("sf_mkt_product_sort", productSort); } catch {} }, [productSort]);
  // Which identity field is shown as the PRIMARY (big) line on each
  // product row. "sku" is the new default for API-connected stores where
  // sellers reference products by SKU in day-to-day speech; "name" is
  // available for operators who prefer titles. Persisted per user.
  const [primaryIdentity, setPrimaryIdentity] = useState(() => LS.get?.("sf_mkt_primary_identity", "sku") || "sku");
  useEffect(() => { try { LS.set?.("sf_mkt_primary_identity", primaryIdentity); } catch {} }, [primaryIdentity]);
  // Back-to-top button is always visible (per user request). No scroll
  // listener needed — the button itself finds the right scroll target
  // inside its onClick handler.
  // Stable callbacks — must be stable refs so the memoized ProductRow
  // doesn't re-render on every parent render. Functional setState pattern
  // means we don't need selectedProductIds in the dep array.
  const toggleGroupExpand = useCallback((gid) => {
    setExpandedGroupIds(prev => prev.includes(gid) ? prev.filter(x => x !== gid) : [...prev, gid]);
  }, []);
  const toggleSelectMany = useCallback((ids, selected) => {
    setSelectedProductIds(prev => {
      const set = new Set(prev);
      if (selected) ids.forEach(id => set.add(id));
      else ids.forEach(id => set.delete(id));
      return Array.from(set);
    });
  }, []);
  // Single-product toggle — flips membership. Uses functional setState so
  // the callback ref is stable across renders (critical for ProductRow memo).
  const toggleSelectOne = useCallback((pid) => {
    setSelectedProductIds(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);
  }, []);
  // Generic promise-based confirm dialog (replaces all window.confirm calls)
  const [genericConfirm, setGenericConfirm] = useState(null); // { icon, title, message, confirmLabel, danger, resolve }
  const showConfirm = useCallback(({ icon, title, message, confirmLabel="Confirm", danger=true }) => {
    return new Promise(resolve => {
      setGenericConfirm({ icon, title, message, confirmLabel, danger, resolve });
    });
  }, []);
  // Info alert dialog (replaces all window.alert calls)
  const [infoAlert, setInfoAlert] = useState(null); // { icon, title, message }

  // ── Marketplace-level field update with API-override warning ──
  // Same pattern as ProductRow.update — if a field has an _apiSnapshot value
  // and the user is changing it for the first time, show a confirm dialog
  // and mark _overridden[field] so re-syncs preserve the manual value.
  async function updateMktWithOverrideCheck(mkt, fields) {
    const newOverridden = { ...(mkt._overridden || {}) };
    let confirmed = true;
    for (const [field, value] of Object.entries(fields)) {
      const apiVal = (mkt._apiSnapshot || {})[field];
      if (apiVal !== undefined && apiVal !== value && !mkt._overridden?.[field]) {
        if (confirmed) {
          confirmed = await showConfirm({
            icon: "✏️", title: "Override API value?",
            message: "This value came from your API sync. If you override it, future re-syncs will preserve your manual value.\n\nA \"↺ Reset to API\" button will appear so you can revert at any time.",
            confirmLabel: "Override", danger: false,
          });
          if (!confirmed) return false;
        }
        newOverridden[field] = true;
      }
    }
    setLastEditedId(mkt.id);
    mktOps.update(mkt.id, { ...fields, _overridden: newOverridden });
    return true;
  }

  // Reset a marketplace-level field back to its API-cached value
  function resetMktField(mkt, field) {
    const apiVal = mkt._apiSnapshot?.[field];
    if (apiVal === undefined) return;
    const newOver = { ...(mkt._overridden || {}) };
    delete newOver[field];
    setLastEditedId(mkt.id);
    mktOps.update(mkt.id, { [field]: apiVal, _overridden: newOver });
  }
  const [poolStockInput, setPoolStockInput] = useState("");
  const [poolCostInput, setPoolCostInput] = useState("");
  // Marketplace filter for the Pools tab — null = show all, otherwise show
  // only pools whose marketplaceIds includes this id.
  const [poolFilterMktId, setPoolFilterMktId] = useState(null);
  // Hide manual input fields (% Total, Payout delay, Reserve, Avg COGS, Ad
  // cost, Stock override) by default to reduce clutter on the marketplace
  // header. The user can click the toggle to expand them when they need to
  // edit. Persisted to LS so the preference sticks across reloads.
  const [showManualFields, setShowManualFields] = useState(() => LS.getBool?.("sf_mkt_show_manual", false) || false);
  useEffect(() => { try { LS.setBool?.("sf_mkt_show_manual", showManualFields); } catch {} }, [showManualFields]);
  // Pool sharing dialog state. Shape:
  //   { poolId, mktId, step: "ask" | "merge", mergeStock: number, mergeStockSource: string }
  // step="ask" → "Is this inventory physically shared?" Yes/No
  // step="merge" → "What's the actual physical stock?" Sum / Use A / Use B / Manual
  const [poolShareDialog, setPoolShareDialog] = useState(null);
  const [mergeStockInput, setMergeStockInput] = useState("");

  // Add a marketplace to a pool. If the pool already has at least one
  // marketplace, prompt the share question first; otherwise just add it
  // (single-marketplace pools have no sharing concept yet).
  function addMktToPool(pool, newMktId) {
    const existingMkts = (pool.marketplaceIds || []).filter(id => id !== newMktId);
    if (existingMkts.length === 0) {
      // First marketplace in this pool — no share decision needed yet
      poolOps?.update(pool.id, { marketplaceIds: [...existingMkts, newMktId] });
      return;
    }
    // Multi-marketplace pool — ask the share question
    setPoolShareDialog({
      poolId: pool.id,
      mktId: newMktId,
      step: "ask",
    });
  }
  // Called when the user answers the share question. mode="shared" or "aggregate"
  function handleShareAnswer(mode) {
    if (!poolShareDialog) return;
    const { poolId, mktId } = poolShareDialog;
    const pool = (inventoryPools || []).find(p => p.id === poolId);
    if (!pool) { setPoolShareDialog(null); return; }
    if (mode === "aggregate") {
      // Just add the marketplace; pool stays in aggregate-only mode
      poolOps?.update(poolId, {
        marketplaceIds: [...(pool.marketplaceIds || []), mktId],
        isShared: false,
      });
      setPoolShareDialog(null);
      return;
    }
    // mode === "shared" — go to the merge dialog
    const candidateMkts = [...(pool.marketplaceIds || []), mktId];
    setPoolShareDialog({ ...poolShareDialog, step: "merge", candidateMkts });
  }
  // Called when the user picks a merge strategy from the merge dialog.
  // strategy: "sum" | "manual" | <mktId> (use that marketplace's lastSyncStock)
  function handleMergeStock(strategy) {
    if (!poolShareDialog) return;
    const { poolId, mktId, candidateMkts } = poolShareDialog;
    const pool = (inventoryPools || []).find(p => p.id === poolId);
    if (!pool) { setPoolShareDialog(null); return; }
    const finalMktIds = candidateMkts || [...(pool.marketplaceIds || []), mktId];
    const mktsInPool = marketplaces.filter(m => finalMktIds.includes(m.id));
    let chosenStock = 0;
    if (strategy === "sum") {
      chosenStock = mktsInPool.reduce((s, m) => s + (Number(m.lastSyncStock) || 0), 0);
    } else if (strategy === "manual") {
      chosenStock = parseInt(mergeStockInput) || 0;
    } else {
      // strategy is a marketplace id
      const m = mktsInPool.find(x => x.id === strategy);
      chosenStock = Number(m?.lastSyncStock) || 0;
    }
    const today = new Date();
    const dateStr = `${String(today.getDate()).padStart(2,"0")}.${String(today.getMonth()+1).padStart(2,"0")}.${today.getFullYear()}`;
    poolOps?.update(poolId, {
      marketplaceIds: finalMktIds,
      isShared: true,
      initialStock: chosenStock,
      initialStockDate: dateStr,
      stockOverridden: true,
    });
    setPoolShareDialog(null);
    setMergeStockInput("");
  }
  // COG-cascade warning dialog. Shape:
  //   { kind: "pool" | "mkt", title, message, onConfirm, onCancel? }
  // Used when the user changes a cost-per-unit at pool or marketplace level —
  // the change cascades through the COG hierarchy (pool > mkt > group > prod)
  // and we want the user to acknowledge what's about to be overridden.
  const [cogWarning, setCogWarning] = useState(null);
  // Local draft for the marketplace avgCogs input so we don't fire a warning
  // on every keystroke. Commits onBlur (or Enter) via the confirm dialog.
  const [mktAvgCogsDraft, setMktAvgCogsDraft] = useState(null); // { mktId, value } | null
  // Same draft pattern for the marketplace-level stock override.
  const [mktStockDraft, setMktStockDraft] = useState(null); // { mktId, value } | null
  const [activeTab, setActiveTab] = useState("marketplaces"); // "marketplaces" | "pools"
  const [iconEditMktId, setIconEditMktId] = useState(null);
  const [importType, setImportType] = useState("amazon_transactions"); // amazon_transactions | amazon_ads | shopify
  const [launchStep, setLaunchStep] = useState(1);
  const [launchProduct, setLaunchProduct] = useState({ name:"", activeFrom:mkKey(TODAY.getFullYear(),TODAY.getMonth()+1), pctOfMarket:0, payout:0, deliveryCost:0, marketplaceIds:[], ppcType:"acos", ppcRate:0, refundType:"pct", refundRate:0, refundAmount:0, cogs:{productCost:0,shipping:0,customs:0,prep:0} });
  const prevNavTs = useRef(null);
  const productsRef = useRef(null);
  // Keep a live ref to marketplaces so async callbacks (setTimeout in
  // doSync, post-OAuth recompute, etc.) don't capture a stale snapshot
  // from the closure. Without this, the auto-share-recompute that runs
  // 200 ms after sync would write the pre-sync marketplaces array over
  // the freshly synced one — which is exactly the "products appear for
  // a second then disappear" bug.
  const marketplacesRef = useRef(marketplaces);
  useEffect(() => { marketplacesRef.current = marketplaces; }, [marketplaces]);
  const [showFBM, setShowFBM] = useState(false);
  const [showAddProductSheet, setShowAddProductSheet] = useState(false);
  const [globalSimpleMode, setGlobalSimpleMode] = useState(false);
  function setSimpleModeAndPersist(v) {
    setGlobalSimpleMode(v);
    try { localStorage.setItem("sf_mkt_simple", String(v)); } catch {}
  }
  const [quickProduct, setQuickProduct] = useState({ name:"", payout:"", pctOfMarket:"", deliveryCost:"", ppcRate:"", refundRate:"", refundAmount:"", cogsProduct:"", cogsShipping:"", cogsCustoms:"", cogsPrep:"" });
  const addMktBtnRef = useRef(null);
  const launchBtnRef = useRef(null);
  useEffect(()=>{
    if (!navContext || navContext.ts === prevNavTs.current) return;
    prevNavTs.current = navContext.ts;
    // Navigate to specific marketplace if highlighted
    if (navContext.expandId) setSelectedMktId(navContext.expandId);
    if (navContext.highlightType === "issue" && navContext.highlightId) {
      // If it's a marketplace-specific issue, select that marketplace
      const mkt = marketplaces.find(m => m.id === navContext.highlightId);
      if (mkt) setSelectedMktId(mkt.id);
      // Scroll pct_total warning into view
      if (navContext.highlightId === "pct_total") {
        setTimeout(() => {
          const el = document.querySelector("[data-ch-pct-warn]");
          if (el) el.scrollIntoView({ behavior:"smooth", block:"center" });
        }, 400);
      } else {
        // Scroll to the specific marketplace element
        setTimeout(() => {
          const el = document.getElementById(`mkt-row-${navContext.highlightId}`);
          if (el) el.scrollIntoView({ behavior:"smooth", block:"center" });
        }, 400);
      }
    }
  },[navContext, marketplaces]);
  useEffect(()=>{ if(!selectedMktId&&marketplaces.length>0) setSelectedMktId(marketplaces[0].id); },[marketplaces.length]);

  // ── Manual sync trigger ──────────────────────────────────────────────
  // Pull data from a connected marketplace's platform API and refetch state.
  async function doSync(platform, mktId) {
    if (!mktId || !platform) return;
    setSyncingMktId(mktId);
    setSyncSummary(null);
    try {
      // 1. Refresh brand state first so we pick up any apiConnection changes
      //    written by the OAuth callback (which uses the service-role key
      //    server-side and bypasses the bearer-token brand-state endpoint).
      if (reloadData) await reloadData({ skipReset: true });
      // 2. Get a fresh access token
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not authenticated");
      // 3. Trigger the sync handler — pulls products/inventory/orders/payouts
      const result = await triggerSync(platform, mktId, token);
      setSyncSummary(result);
      // 4. Refetch state to see the newly synced data propagate
      if (reloadData) await reloadData({ skipReset: true });
      // 5. Auto-recompute % shares using the freshly synced sales windows.
      //    Read from the ref (not the closure) so we use the FRESH
      //    post-reload marketplaces snapshot, not the pre-sync one that
      //    the outer async function captured at invocation time.
      setTimeout(() => {
        try {
          const win = salesWindow;
          const latestMkts = marketplacesRef.current || [];
          if (!latestMkts.length) return; // nothing to recompute
          const updatedMkts = latestMkts.map(m => {
            const products = (m.products || []).map(p => {
              const windowVol = p._salesWindows?.[win]?.units;
              const v = windowVol != null ? windowVol : (Number(p.recentSalesVolume ?? p._recentSalesVolume) || 0);
              return { ...p, recentSalesVolume: v, _recentSalesVolume: v };
            });
            const mktVol = products.reduce((s, p) => s + (p.recentSalesVolume || 0), 0);
            return {
              ...m,
              products: recomputeProductShares(products),
              recentSalesVolume: mktVol,
              _recentSalesVolume: mktVol,
            };
          });
          const finalMkts = recomputeMarketplaceShares(updatedMkts);
          mktOps.setMarketplacesAll?.(finalMkts);
        } catch (err) {
          console.warn("[Marketplaces] auto-recompute after sync failed:", err);
        }
      }, 200);
    } catch (e) {
      console.error("Sync failed:", e);
      setSyncSummary({ ok: false, error: String(e?.message || e) });
    } finally {
      setSyncingMktId(null);
    }
  }

  // ── Post-OAuth detection ─────────────────────────────────────────────
  // After Shopify (or any OAuth) redirects back with `?connected=<platform>`,
  // strip the query, refetch brand state to see apiConnection.connected=true,
  // and immediately kick off an initial sync.
  useEffect(() => {
    const platform = hashQuery?.connected;
    if (!platform) return;
    if (postOAuthHandledRef.current) return;
    postOAuthHandledRef.current = true;
    // Strip the query from the URL so a refresh doesn't retrigger
    try { history.replaceState(null, "", "#marketplaces"); } catch {}
    // Find the marketplace that just got connected — pick the one whose
    // apiConnection.platform matches and is connected (set by the callback)
    // OR fall back to selectedMktId if we can't determine it yet.
    (async () => {
      // Refresh first so we can find the connected marketplace
      if (reloadData) await reloadData({ skipReset: true });
      const fresh = (marketplaces || []).find(m => m.apiConnection?.platform === platform);
      const targetId = fresh?.id || selectedMktId;
      if (!targetId) return;
      setSelectedMktId(targetId);
      await doSync(platform, targetId);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashQuery?.connected]);


  // Phase 7 — month-override layer ripped out of Marketplaces. `isGlobal`
  // is hard-coded true; mvData/hasMonthOv/displayMkts collapse to the raw
  // marketplaces list. Kept as locals (vs deleted) so the existing
  // closures + memos in this 3800-line component don't need a global
  // find/replace.
  const isGlobal = true;
  const hasMonthOv = false;
  const globalTotal = marketplaces.reduce((s,m)=>s+(m.pctOfTotal||0),0);
  const globalPctWarn = marketplaces.length>0&&Math.abs(globalTotal-1)>0.01;
  const displayMkts = marketplaces;
  const selectedMkt = useMemo(() => {
    return marketplaces.find(m => m.id === selectedMktId)
      || null;
  }, [marketplaces, displayMkts, isGlobal, selectedMktId]);
  const selectedMktIdx = selectedMkt?marketplaces.indexOf(selectedMkt):0;
  const selectedMktColor = selectedMkt?.color || mktColor(Math.max(0, selectedMktIdx)) || T.text;
  const filteredProducts = useMemo(()=>{ if(!selectedMkt) return []; if(!searchQuery) return selectedMkt.products; return selectedMkt.products.filter(p=>(p.name||"").toLowerCase().includes(searchQuery.toLowerCase())); },[selectedMkt,searchQuery]);
  // Selection lookup as a Set — O(1) per row instead of O(N) per .includes call.
  // With 60 products this turns the per-render cost from 60×60 = 3600 ops to 60.
  const selectedProductIdsSet = useMemo(() => new Set(selectedProductIds), [selectedProductIds]);
  const expandedGroupIdsSet = useMemo(() => new Set(expandedGroupIds), [expandedGroupIds]);
  // Group + ungrouped layout computed once per (selectedMkt, searchQuery) change
  // instead of inside the JSX on every render. Search filter applies: a group
  // passes if its name matches OR any child matches; only matching children
  // are shown when the group expands (or all children if the group name itself
  // matched the search).
  // Realized per-unit payout per product for the active share window.
  // Computed once per (selectedMkt, months, salesWindow) change and passed
  // down to every ProductRow so the net/realized badge reflects actual
  // Shopify-Payments cash, not list price.
  const realizedPayoutMap = useMemo(() => {
    if (!selectedMkt) return null;
    return realizedPayoutPerProduct(selectedMkt, { months }, salesWindow);
  }, [selectedMkt, months, salesWindow]);

  const productLayout = useMemo(() => {
    if (!selectedMkt) return { groupRows: [], ungroupedProducts: [] };
    const groups = selectedMkt.productGroups || [];
    const groupedProductIds = new Set();
    groups.forEach(g => (g.productIds || []).forEach(id => groupedProductIds.add(id)));
    const q = (searchQuery || "").toLowerCase();
    const matchProd = (p) => !q || (p.name || "").toLowerCase().includes(q);
    // Sort key — compares two products by the active productSort mode.
    // "default" returns 0 so the original product order is preserved.
    const salesUnits = (p) => Number(p?._salesWindows?.[salesWindow]?.units) || 0;
    // Stock = apiStock first, then manual currentStock, else 0.
    const stockUnits = (p) => Number(p?.apiStock ?? p?.currentStock) || 0;
    // Stockout days at the current share window — uses the shared
    // stockoutMath helper so the number matches the per-row chip.
    const stockoutDays = (p) => {
      const info = productStockoutInfo(p, salesWindow);
      // Products without a stockout estimate sort to the BOTTOM of the
      // asc list (Infinity) and BOTTOM of the desc list (-Infinity) so
      // they don't pollute the front of the list.
      return info?.daysToOOS ?? null;
    };
    const cmpWithNull = (a, b, dir) => {
      const va = stockoutDays(a);
      const vb = stockoutDays(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;  // null always sinks
      if (vb == null) return -1;
      return dir === "asc" ? va - vb : vb - va;
    };
    const sortFn = (() => {
      switch (productSort) {
        case "pct_desc":      return (a, b) => (Number(b.pctOfMarket)||0) - (Number(a.pctOfMarket)||0);
        case "pct_asc":       return (a, b) => (Number(a.pctOfMarket)||0) - (Number(b.pctOfMarket)||0);
        case "price_desc":    return (a, b) => (Number(b.payout)||0) - (Number(a.payout)||0);
        case "price_asc":     return (a, b) => (Number(a.payout)||0) - (Number(b.payout)||0);
        case "sales_desc":    return (a, b) => salesUnits(b) - salesUnits(a);
        case "sales_asc":     return (a, b) => salesUnits(a) - salesUnits(b);
        case "stock_desc":    return (a, b) => stockUnits(b) - stockUnits(a);
        case "stock_asc":     return (a, b) => stockUnits(a) - stockUnits(b);
        case "stockout_asc":  return (a, b) => cmpWithNull(a, b, "asc");
        case "stockout_desc": return (a, b) => cmpWithNull(a, b, "desc");
        // Decoupled products at the top of the list (sort=decoupled) or at
        // the bottom (sort=coupled). Inside each group, preserve the
        // original order.
        case "decoupled_first": return (a, b) => (b._decoupled ? 1 : 0) - (a._decoupled ? 1 : 0);
        case "coupled_first":   return (a, b) => (a._decoupled ? 1 : 0) - (b._decoupled ? 1 : 0);
        default:              return null;
      }
    })();
    const applySort = (arr) => (sortFn ? [...arr].sort(sortFn) : arr);
    const groupRows = groups
      .map(g => {
        const children = (selectedMkt.products || []).filter(p => (g.productIds || []).includes(p.id));
        const matchingChildren = children.filter(matchProd);
        const groupNameMatches = !q || (g.name || "").toLowerCase().includes(q);
        if (!groupNameMatches && matchingChildren.length === 0) return null;
        const shown = groupNameMatches ? children : matchingChildren;
        return { group: g, children: applySort(shown) };
      })
      .filter(Boolean);
    const ungroupedProducts = applySort(
      (selectedMkt.products || []).filter(p => !groupedProductIds.has(p.id) && matchProd(p))
    );
    return { groupRows, ungroupedProducts };
  }, [selectedMkt, searchQuery, productSort, salesWindow]);

  // Products with ANY month override — for orange border in global mode
  const productsWithMonthOverrides = useMemo(() => {
    if (!selectedMkt) return new Set();
    const ids = new Set();
    const mktId = selectedMkt.id;
    for (const [key, mData] of Object.entries(months || {})) {
      if (!/^\d{4}-\d{2}$/.test(key) || !mData?.vars) continue;
      for (const varKey of Object.keys(mData.vars)) {
        // Match payout_mktId_prodId, deliv_mktId_prodId, pct_mktId_prodId, etc.
        if (varKey.includes(`_${mktId}_`)) {
          const prodId = varKey.split(`_${mktId}_`)[1];
          if (prodId) ids.add(prodId);
        }
      }
    }
    return ids;
  }, [selectedMkt, months]);

  // Carousel: compute position of each card relative to selected
  const selectedIdx = displayMkts.findIndex(m => m.id === selectedMktId);

  function handleAddMarketplace() {
    if(!newMktName.trim()) return;
    const sourceProducts = newMktCopyFrom?(marketplaces.find(m=>m.id===newMktCopyFrom)?.products||[]).map(p=>({...p,id:uid()})):[];
    mktOps.addWithConfig?.(newMktName, newMktFrom, sourceProducts, newMktIcon, newMktColor, newMktBrand||dataKey);
    setShowAddMktModal(false); setNewMktName(""); setNewMktCopyFrom(""); setNewMktIcon("store"); setNewMktColor(MKT_COLORS[0]);
  }
  function openLaunchModal() {
    setLaunchStep(1);
    setLaunchProduct({ name:"", activeFrom:mkKey(TODAY.getFullYear(),TODAY.getMonth()+1), pctOfMarket:0, payout:0, deliveryCost:0, marketplaceIds:selectedMktId?[selectedMktId]:(marketplaces.length===1?[marketplaces[0].id]:[]), ppcType:"acos", ppcRate:0, refundType:"pct", refundRate:0, refundAmount:0, cogs:{productCost:0,shipping:0,customs:0,prep:0} });
    setShowLaunchModal(true);
  }
  function handleLaunchProduct() {
    const {name,activeFrom,pctOfMarket,payout,deliveryCost,marketplaceIds,ppcType,ppcRate,refundType,refundRate,refundAmount,cogs}=launchProduct;
    const id=uid();
    // Add product to marketplaces
    const mkts=marketplaces.map(m=>!marketplaceIds.includes(m.id)?m:{...m,products:[...m.products,{id,name,pctOfMarket:pctOfMarket/100,payout,deliveryCost,activeFrom,ppcType,ppcRate,refundType,refundRate,refundAmount,cogs}]});
    mktOps.setMarketplacesAll?.(sortMkts(mkts));
    // Also create a Planner decision for this product launch
    if (typeof onAddDecision === "function") {
      const mktNames = marketplaceIds.map(id=>marketplaces.find(m=>m.id===id)?.name).filter(Boolean).join(", ");
      onAddDecision({
        id: uid(),
        name: "🚀 Launch: " + name,
        icon: "🚀",
        category: "product",
        date: { prec:"month", month:activeFrom },
        status: "planned",
        notes: "New product on " + mktNames + " · " + currSym() + payout + "/unit · " + pctOfMarket + "% of marketplace",
        color: selectedMktColor || "#34C759",
      });
    }
    setShowLaunchModal(false);
  }

  // Keyboard navigation for carousel
  useEffect(() => {
    function onKey(e) {
      if (!displayMkts.length) return;
      if (e.key === "ArrowLeft") {
        const i = Math.max(0, selectedIdx - 1);
        setSelectedMktId(displayMkts[i].id);
      } else if (e.key === "ArrowRight") {
        const i = Math.min(displayMkts.length - 1, selectedIdx + 1);
        setSelectedMktId(displayMkts[i].id);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [displayMkts, selectedIdx]);

  const lp = launchProduct;
  return (
    <div style={{ fontFamily:FONT }}>
      <style>{`
        @keyframes sfFadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes sfSpringIn{from{opacity:0;transform:scale(0.88)}to{opacity:1;transform:scale(1)}}
        @keyframes prodSlideIn{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
      `}</style>

      {can && !can("marketplaces","edit") && !can("marketplaces","create") && (
        <div style={{ display:"flex", alignItems:"center", gap:10, background:"rgba(139,92,246,0.08)", border:"1px solid rgba(139,92,246,0.2)", borderRadius:12, padding:"10px 16px", marginBottom:16, animation:"sfFadeIn 0.25s ease both" }}>
          <span style={{ fontSize:16 }}>👁</span>
          <div><div style={{ fontSize:13, fontWeight:600, color:"#8b5cf6" }}>View-only access</div><div style={{ fontSize:11, color:"#b0b4cc" }}>You can view marketplaces but can't make changes.</div></div>
        </div>
      )}

      {/* ── Top toolbar — tab selector + settings + actions all in one row ── */}
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16, flexWrap:"wrap" }}>
        {/* Tab selector */}
        <div style={{ display:"flex", gap:0, borderRadius:RADIUS.sm, overflow:"hidden", border:`1px solid ${T.border}`, flexShrink:0 }}>
          {[["marketplaces","Marketplaces"],["pools","Inventory Pools"]].map(([v,label])=>(
            <button key={v} onClick={()=>setActiveTab(v)}
              style={{ padding:"7px 18px", fontSize:13, fontFamily:FONT, cursor:"pointer", fontWeight:activeTab===v?700:400, border:"none",
                background:activeTab===v?T.accent+"18":"transparent", color:activeTab===v?T.accent:T.textSub, transition:"all 0.15s ease", whiteSpace:"nowrap" }}>{label}</button>
          ))}
        </div>
        {/* SETTINGS FOR + per-month override picker removed (Phase 7
            redesign). Marketplaces is now a single-source dashboard;
            per-month overrides go through monthOps in other surfaces if
            needed. The right-end +Add Marketplace button is preserved. */}
        {activeTab === "marketplaces" && (
          <>
            <span style={{ flex:1 }}/>
            {(!can || can("marketplaces","create")) && <span data-tour="add-marketplace"><Btn ref={addMktBtnRef} onClick={()=>setShowAddMktModal(true)} color={T.accent} size="sm">+ Add Marketplace</Btn></span>}
          </>
        )}
      </div>

      <ConfirmSheet open={!!confirmDelete} icon="🗑" title={confirmDelete?`Delete "${confirmDelete.name}"?`:""} message={confirmDelete?.type==="mkt"?"This marketplace and all its products will be permanently removed.":confirmDelete?.type==="pool"?"Marketplaces in this pool will return to global inventory.":confirmDelete?.type==="api_disconnect"?"Disconnect API? Your manual data will be preserved.":"This product will be removed from this marketplace."} confirmLabel="Yes, Delete" onConfirm={()=>{
        if (confirmDelete.type==="pool") {
          poolOps?.delete(confirmDelete.poolId);
          setConfirmDelete(null);
          return;
        }
        if (confirmDelete.type==="api_disconnect") {
          const mkt = marketplaces.find(m=>m.id===confirmDelete.mktId);
          if (mkt) {
            // Disconnect API + clear API-sourced data.
            // Keep marketplace in its inventory pool (reconnecting shouldn't create duplicates).
            const patch = {
              apiConnection: { ...(mkt.apiConnection||{}), connected:false, accessToken:null, refreshToken:null },
              reserveHeld: 0, reserveRate: 0,
              lastSyncStock: null, lastSyncStockDate: null,
            };
            // Clear apiStock from products so COG warning doesn't count them
            const cleanedProducts = (mkt.products||[]).map(p => ({ ...p, apiStock: 0, apiStockDate: null }));
            // Update marketplace + recompute shares
            const updatedMkts = marketplaces.map(m => m.id === mkt.id ? { ...m, ...patch, products: cleanedProducts } : m);
            const finalMkts = recomputeMarketplaceShares(updatedMkts);
            mktOps.setMarketplacesAll?.(finalMkts);
          }
          setConfirmDelete(null);
          return;
        }
        if (confirmDelete.type==="mkt") {
          const delId = confirmDelete.mktId;
          const idx = marketplaces.findIndex(m => m.id === delId);
          mktOps.delete(delId);
          const remaining = marketplaces.filter(m => m.id !== delId);
          if (remaining.length > 0) {
            const nextIdx = Math.min(idx, remaining.length - 1);
            setSelectedMktId(remaining[nextIdx].id);
          }
        } else {
          mktOps.deleteProduct(confirmDelete.mktId, confirmDelete.prodId);
        }
        setConfirmDelete(null);
      }} onCancel={()=>setConfirmDelete(null)}/>

      {/* COG cascade warning — fires when the user changes a pool or marketplace
          cost-per-unit. Uses ConfirmSheet with danger=false (blue) so it visually
          distinguishes from delete confirms. The user clicks Confirm to commit
          the override or Cancel to abort. */}
      <ConfirmSheet
        open={!!cogWarning}
        icon="⚠️"
        title={cogWarning?.title || ""}
        message={cogWarning?.message || ""}
        confirmLabel="Yes, override"
        danger={false}
        onConfirm={() => cogWarning?.onConfirm?.()}
        onCancel={() => cogWarning?.onCancel ? cogWarning.onCancel() : setCogWarning(null)}
      />

      {/* Generic confirm dialog — replaces all window.confirm calls */}
      <ConfirmSheet
        open={!!genericConfirm}
        icon={genericConfirm?.icon || ""}
        title={genericConfirm?.title || ""}
        message={genericConfirm?.message || ""}
        confirmLabel={genericConfirm?.confirmLabel || "Confirm"}
        danger={genericConfirm?.danger ?? true}
        onConfirm={() => { genericConfirm?.resolve?.(true); setGenericConfirm(null); }}
        onCancel={() => { genericConfirm?.resolve?.(false); setGenericConfirm(null); }}
      />

      {/* Info alert dialog — replaces all window.alert calls */}
      <ConfirmSheet
        open={!!infoAlert}
        icon={infoAlert?.icon || "ℹ️"}
        title={infoAlert?.title || ""}
        message={infoAlert?.message || ""}
        confirmLabel="OK"
        danger={false}
        onConfirm={() => setInfoAlert(null)}
        onCancel={() => setInfoAlert(null)}
      />

      <PromptSheet
        open={!!promptDialog}
        title={promptDialog?.title || ""}
        message={promptDialog?.message || ""}
        placeholder={promptDialog?.placeholder || ""}
        defaultValue={promptDialog?.defaultValue || ""}
        confirmLabel={promptDialog?.defaultValue ? "Rename" : "Create"}
        icon="📦"
        onConfirm={promptDialog?.onConfirm || (() => setPromptDialog(null))}
        onCancel={() => setPromptDialog(null)}
      />

      {/* Pool sharing dialog — fires when the user adds a SECOND (or later)
          marketplace to a pool. Two steps:
            step 1 ("ask"): "Is this inventory physically shared?" Yes / No
            step 2 ("merge"): if yes, "What's the actual physical stock?"
              with options Sum / Use mkt A / Use mkt B / Manual
          On Cancel: nothing changes — the marketplace is NOT added to the pool. */}
      {poolShareDialog && (() => {
        const pool = (inventoryPools || []).find(p => p.id === poolShareDialog.poolId);
        if (!pool) return null;
        const newMkt = marketplaces.find(m => m.id === poolShareDialog.mktId);
        const existingMkts = marketplaces.filter(m => (pool.marketplaceIds || []).includes(m.id));
        if (poolShareDialog.step === "ask") {
          return (
            <BottomSheet open={true} onClose={() => setPoolShareDialog(null)}
              title={`Add ${newMkt?.name || "marketplace"} to "${pool.name}"`} maxWidth={500}
              footer={null}>
              <div style={{ padding:"4px 0 16px" }}>
                <div style={{ fontSize:13, color:T.textSub, lineHeight:1.6, marginBottom:18 }}>
                  Is this inventory <strong style={{ color:T.text }}>physically shared</strong> across all marketplaces in this pool?
                </div>
                <div style={{ background:T.surface, borderRadius:RADIUS.md, padding:14, marginBottom:14, border:`1px solid ${T.border}` }}>
                  <button onClick={() => handleShareAnswer("shared")}
                    style={{ width:"100%", textAlign:"left", padding:14, borderRadius:RADIUS.md, background:T.accent+"10", border:`1.5px solid ${T.accent}55`, color:T.text, cursor:"pointer", fontFamily:FONT, marginBottom:10 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:T.accent, marginBottom:4 }}>🔗 Yes — physically shared</div>
                    <div style={{ fontSize:12, color:T.textSub, lineHeight:1.5 }}>
                      One physical warehouse / FBA bucket. A sale on any marketplace decrements the same stock counter. COG, targets, and supplier orders all live at the pool level. Marketplaces become sales channels with no independent stock.
                    </div>
                  </button>
                  <button onClick={() => handleShareAnswer("aggregate")}
                    style={{ width:"100%", textAlign:"left", padding:14, borderRadius:RADIUS.md, background:"transparent", border:`1.5px solid ${T.border}`, color:T.text, cursor:"pointer", fontFamily:FONT }}>
                    <div style={{ fontSize:14, fontWeight:700, color:T.text, marginBottom:4 }}>📊 No — just grouping for reporting</div>
                    <div style={{ fontSize:12, color:T.textSub, lineHeight:1.5 }}>
                      Each marketplace keeps its own physical stock; the pool just sums them for KPI display. Sales on Amazon don't affect eBay's stock.
                    </div>
                  </button>
                </div>
                <button onClick={() => setPoolShareDialog(null)}
                  style={{ width:"100%", padding:10, background:"transparent", border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.textSub, fontFamily:FONT, fontSize:13, cursor:"pointer" }}>
                  Cancel — don't add this marketplace
                </button>
              </div>
            </BottomSheet>
          );
        }
        // step === "merge"
        const candidateMkts = poolShareDialog.candidateMkts || [...(pool.marketplaceIds || []), poolShareDialog.mktId];
        const mktsInfo = marketplaces.filter(m => candidateMkts.includes(m.id)).map(m => ({
          id: m.id,
          name: m.name,
          stock: Number(m.lastSyncStock) || 0,
          syncDate: m.lastSyncStockDate || "—",
        }));
        const sumStock = mktsInfo.reduce((s, m) => s + m.stock, 0);
        return (
          <BottomSheet open={true} onClose={() => setPoolShareDialog(null)}
            title={`Set physical stock for "${pool.name}"`} maxWidth={520}
            footer={null}>
            <div style={{ padding:"4px 0 16px" }}>
              <div style={{ fontSize:13, color:T.textSub, lineHeight:1.6, marginBottom:14 }}>
                The pool is now <strong style={{ color:T.accent }}>shared</strong>. We need ONE physical stock count for the whole pool. Pick the option that matches your warehouse reality:
              </div>
              <div style={{ marginBottom:14, padding:12, background:T.surface, borderRadius:RADIUS.md, border:`1px solid ${T.border}`, fontSize:12 }}>
                <div style={{ color:T.textSub, marginBottom:6, fontWeight:600 }}>What each marketplace currently reports:</div>
                {mktsInfo.map(m => (
                  <div key={m.id} style={{ display:"flex", justifyContent:"space-between", padding:"3px 0" }}>
                    <span style={{ color:T.text }}>{m.name}</span>
                    <span style={{ color:T.text, fontWeight:700 }}>{m.stock.toLocaleString()} units <span style={{ color:T.textSub, fontWeight:400, fontSize:11 }}>({m.syncDate})</span></span>
                  </div>
                ))}
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                <button onClick={() => handleMergeStock("sum")}
                  style={{ padding:12, textAlign:"left", borderRadius:RADIUS.md, background:T.accent+"15", border:`1.5px solid ${T.accent}`, color:T.text, cursor:"pointer", fontFamily:FONT }}>
                  <div style={{ fontSize:13, fontWeight:700, color:T.accent }}>➕ Sum ({sumStock.toLocaleString()} units)</div>
                  <div style={{ fontSize:11, color:T.textSub, marginTop:2 }}>Use when each marketplace reports a SEPARATE allocation of distinct physical units (most common — e.g. FBA + own warehouse).</div>
                </button>
                {mktsInfo.map(m => (
                  <button key={m.id} onClick={() => handleMergeStock(m.id)}
                    style={{ padding:12, textAlign:"left", borderRadius:RADIUS.md, background:"transparent", border:`1.5px solid ${T.border}`, color:T.text, cursor:"pointer", fontFamily:FONT }}>
                    <div style={{ fontSize:13, fontWeight:700, color:T.text }}>Use {m.name}'s number ({m.stock.toLocaleString()})</div>
                    <div style={{ fontSize:11, color:T.textSub, marginTop:2 }}>Use when the other marketplaces' numbers are stale, zero, or wrong reflections of the same physical stock.</div>
                  </button>
                ))}
                <div style={{ padding:12, borderRadius:RADIUS.md, background:"transparent", border:`1.5px solid ${T.border}` }}>
                  <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:6 }}>✏️ Enter manually</div>
                  <div style={{ fontSize:11, color:T.textSub, marginBottom:8 }}>Use when none of the API numbers reflect reality (warehouse count, recent shipments not yet synced, shrinkage, etc.).</div>
                  <div style={{ display:"flex", gap:6 }}>
                    <input type="number" value={mergeStockInput} onChange={e => setMergeStockInput(e.target.value)}
                      placeholder="Enter units" style={{ flex:1, background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.text, padding:"8px 10px", fontSize:13, outline:"none", fontFamily:FONT }}/>
                    <button onClick={() => handleMergeStock("manual")} disabled={!mergeStockInput}
                      style={{ padding:"8px 14px", background:mergeStockInput?T.accent:T.border, border:"none", borderRadius:RADIUS.sm, color:"#fff", fontFamily:FONT, fontWeight:700, fontSize:12, cursor:mergeStockInput?"pointer":"not-allowed" }}>Use this</button>
                  </div>
                </div>
              </div>
              <button onClick={() => { setPoolShareDialog(null); setMergeStockInput(""); }}
                style={{ width:"100%", marginTop:14, padding:10, background:"transparent", border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.textSub, fontFamily:FONT, fontSize:13, cursor:"pointer" }}>
                Cancel
              </button>
            </div>
          </BottomSheet>
        );
      })()}

      {/* Add Marketplace BottomSheet — all settings in one go, brand selector */}
      <BottomSheet open={showAddMktModal} onClose={()=>setShowAddMktModal(false)}
        title="Add Marketplace" height="85vh"
        footer={<>
          <Btn onClick={()=>setShowAddMktModal(false)} variant="outline" color={T.textSub} style={{flex:1}}>Cancel</Btn>
          <Btn onClick={handleAddMarketplace} color={T.accent} disabled={!newMktName.trim()} style={{flex:2}}>Add Marketplace</Btn>
        </>}>
        <div style={{ display:"flex",flexDirection:"column",gap:18 }}>

          {/* Icon + Color + Name row */}
          <div style={{ display:"flex",gap:16,alignItems:"flex-start" }}>
            <div>
              <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8 }}>Icon / Image</div>
              <div style={{ display:"flex",alignItems:"center",gap:12 }}>
                <IconPicker value={newMktIcon} onChange={v=>{setNewMktIcon(v);setNewMktImage(null);}} color={newMktColor}/>
                <Tooltip content="Upload custom image (PNG/JPG)"><label style={{ cursor:"pointer" }}>
                  <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display:"none" }}
                    onChange={e=>{
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 300000) { e.target.value=""; return; }
                      const reader = new FileReader();
                      reader.onload = ev => setNewMktImage(ev.target.result);
                      reader.readAsDataURL(file);
                    }}/>
                  <div style={{ width:52,height:52,borderRadius:"50%",
                    background:newMktImage?`url(${newMktImage}) center/cover`:`linear-gradient(135deg,${newMktColor}ee,${newMktColor}88)`,
                    display:"flex",alignItems:"center",justifyContent:"center",fontSize:newMktImage?0:24,
                    boxShadow:`0 4px 16px ${newMktColor+"55"}`,border:`2px dashed ${newMktImage?newMktColor:T.border}`,
                    position:"relative",overflow:"hidden" }}>
                    {!newMktImage && (SHOP_ICONS.find(i=>i.id===newMktIcon)||SHOP_ICONS[1]).emoji}
                    <div style={{ position:"absolute",bottom:0,left:0,right:0,background:"rgba(0,0,0,0.4)",fontSize:9,color:"#fff",textAlign:"center",padding:"2px 0" }}>📷</div>
                  </div>
                </label></Tooltip>
                {newMktImage && <button onClick={()=>setNewMktImage(null)} style={{ background:"none",border:"none",color:T.textSub,cursor:"pointer",fontSize:11 }}>✕ Remove</button>}
              </div>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8 }}>Color</div>
              <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>
                {MKT_COLORS.map(c=>(
                  <button key={c} onClick={()=>setNewMktColor(c)} style={{
                    width:24,height:24,borderRadius:"50%",border:`3px solid ${newMktColor===c?"#fff":c}`,
                    background:c,cursor:"pointer",padding:0,
                    boxShadow:newMktColor===c?`0 0 0 3px ${c}, 0 0 0 5px ${c+"55"}`:"none",
                    transform:newMktColor===c?"scale(1.2)":"scale(1)",
                    transition:`all 0.18s ${SPRING}`,
                  }}/>
                ))}
              </div>
            </div>
          </div>

          {/* Name */}
          <div>
            <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8 }}>Marketplace Name</div>
            <input value={newMktName} onChange={e=>setNewMktName(e.target.value)}
              placeholder="Amazon, Shopify, Walmart…" autoFocus
              onKeyDown={e=>e.key==="Enter"&&handleAddMarketplace()}
              style={{ ...inputStyle(T),width:"100%",padding:"11px 14px",boxSizing:"border-box" }}
              onFocus={e=>e.target.style.borderColor=T.accent}
              onBlur={e=>e.target.style.borderColor=T.border}/>
          </div>

          {/* Active From */}
          <div>
            <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8,display:"inline-flex",alignItems:"center" }}
 >Active from <Info tip="mktActiveFrom"/></div>
            <StyledSelect value={newMktFrom} onChange={e=>setNewMktFrom(e.target.value)}>
              {visibleYears.slice().sort().map(y=>Array.from({length:12},(_,i)=>{ const k=mkKey(y,i+1); return <option key={k} value={k}>{MONTH_NAMES[i]} {y}</option>; }))}
            </StyledSelect>
          </div>

          {/* Brand Assignment */}
          <div>
            <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8,display:"inline-flex",alignItems:"center" }}
 >Assign to Brand <Info tip="mktAssignBrand"/></div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {[{ key: dataKey, name: "This brand" }, ...getStoredBrands().filter(b => b.key !== dataKey)].map(b => {
                const isSel = (newMktBrand||dataKey) === b.key;
                return (
                  <button key={b.key} onClick={()=>setNewMktBrand(b.key)}
                    style={{ padding:"6px 14px", borderRadius:RADIUS.pill, fontSize:12, fontFamily:FONT, cursor:"pointer",
                      fontWeight:isSel?700:400, background:isSel?T.accent+"22":"transparent",
                      color:isSel?T.accent:T.textSub, border:`1.5px solid ${isSel?T.accent:T.border}` }}>
                    {b.emoji||"🏷"} {b.name||b.key}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize:11, color:T.textSub, marginTop:6, fontStyle:"italic" }}>
              ℹ Only the marketplace icon and name are created here. Products, targets, and cashflow data are configured after creation.
            </div>
          </div>

          {/* Copy From (always visible) */}
          {marketplaces.length>0&&(
            <div>
              <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8,display:"inline-flex",alignItems:"center" }}
 >Copy products from (optional) <Info tip="mktCopyFrom"/></div>
              <StyledSelect value={newMktCopyFrom} onChange={e=>setNewMktCopyFrom(e.target.value)}>
                <option value="">None — start fresh</option>
                {marketplaces.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
              </StyledSelect>
            </div>
          )}
        </div>
      </BottomSheet>

      {/* Launch Product BottomSheet */}
      <BottomSheet open={showLaunchModal} onClose={()=>setShowLaunchModal(false)}
        title={`🚀 Launch Product · Step ${launchStep}/2`}
        subtitle={<span style={{ color:"inherit", fontSize:12 }}>All product fields</span>}
        height="80vh"
        footer={launchStep===1
          ? <><Btn onClick={()=>setShowLaunchModal(false)} variant="outline" color={T.textSub} style={{flex:1}}>Cancel</Btn>
              {globalSimpleMode
                ? <Btn onClick={handleLaunchProduct} color={T.green} disabled={!lp.name.trim()||lp.marketplaceIds.length===0||!lp.payout} style={{flex:2}}>🚀 Launch Product</Btn>
                : <Btn onClick={()=>setLaunchStep(2)} color={T.accent} disabled={!lp.name.trim()||lp.marketplaceIds.length===0} style={{flex:2}}>Next: Payout →</Btn>
              }</>
          : <><Btn onClick={()=>setLaunchStep(1)} variant="outline" color={T.textSub} style={{flex:1}}>← Back</Btn><Btn onClick={handleLaunchProduct} color={T.green} disabled={!lp.payout} style={{flex:2}}>🚀 Launch Product</Btn></>
        }>
        {launchStep===1&&(
          <div style={{ display:"flex",flexDirection:"column",gap:16 }}>
            {/* Product Name */}
            <div>
              <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8 }}>Product Name</div>
              <input value={lp.name} onChange={e=>setLaunchProduct(p=>({...p,name:e.target.value}))} placeholder="e.g. Blue Widget XL" autoFocus
                style={{ ...inputStyle(T), width:"100%", padding:"11px 14px", boxSizing:"border-box" }}/>
            </div>
            {/* Marketplace selection — multi-select with All button, default = currently selected */}
            <div>
              <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8 }}>Marketplaces</div>
              <div style={{ display:"flex",gap:6,flexWrap:"wrap",marginBottom:6 }}>
                {/* All marketplaces shortcut */}
                <button onClick={()=>setLaunchProduct(p=>({
                  ...p,
                  marketplaceIds: p.marketplaceIds.length===marketplaces.length ? (selectedMktId?[selectedMktId]:[]) : marketplaces.map(m=>m.id)
                }))} style={{
                  padding:"6px 14px",borderRadius:RADIUS.pill,fontSize:13,fontFamily:FONT,cursor:"pointer",
                  fontWeight:lp.marketplaceIds.length===marketplaces.length?700:400,
                  background:lp.marketplaceIds.length===marketplaces.length?T.accent+"22":"transparent",
                  color:lp.marketplaceIds.length===marketplaces.length?T.accent:T.textSub,
                  border:`1.5px solid ${lp.marketplaceIds.length===marketplaces.length?T.accent:T.border}`
                }}>
                  {lp.marketplaceIds.length===marketplaces.length?"✓ ":""}All
                </button>
                {marketplaces.map(m=>{ const sel=lp.marketplaceIds.includes(m.id); const c=mktColor(marketplaces.indexOf(m)); return (
                  <button key={m.id} onClick={()=>setLaunchProduct(p=>({...p,marketplaceIds:sel?p.marketplaceIds.filter(x=>x!==m.id):[...p.marketplaceIds,m.id]}))}
                    style={{ padding:"6px 14px",borderRadius:RADIUS.pill,fontSize:13,fontFamily:FONT,cursor:"pointer",fontWeight:sel?700:400,background:sel?c+"22":"transparent",color:sel?c:T.textSub,border:`1.5px solid ${sel?c:T.border}` }}>
                    {sel?"✓ ":""}{mktIcon(m)} {m.name}
                  </button>
                );})}
              </div>
              {lp.marketplaceIds.length===0&&<div style={{ fontSize:12,color:T.orange }}>Select at least one marketplace</div>}
              {lp.marketplaceIds.length>0&&<div style={{ fontSize:11,color:T.textSub }}>Product will be added to {lp.marketplaceIds.length} marketplace{lp.marketplaceIds.length>1?"s":""}</div>}
            </div>
            {/* Simple mode: Payout + % on same step */}
            {globalSimpleMode && (<>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
                <div>
                  <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8,display:"inline-flex",alignItems:"center" }}
 >Net Payout / Unit ({currSym()}) <Info tip="payout"/></div>
                  <input type="number" value={lp.payout||""} onChange={e=>setLaunchProduct(p=>({...p,payout:parseFloat(e.target.value)||0}))} placeholder="0.00" autoFocus={false}
                    style={{ width:"100%",background:T.surface,border:`1.5px solid ${T.border}`,borderRadius:RADIUS.md,color:T.green,fontSize:18,fontWeight:800,padding:"11px 14px",outline:"none",fontFamily:FONT,boxSizing:"border-box" }}/>
                </div>
                <div>
                  <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8,display:"inline-flex",alignItems:"center" }}>% of Marketplace <Info tip="pctOfMarket"/></div>
                  <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                    <input type="number" value={lp.pctOfMarket||""} onChange={e=>setLaunchProduct(p=>({...p,pctOfMarket:parseFloat(e.target.value)||0}))} placeholder="0"
                      style={{ flex:1,background:T.surface,border:`1.5px solid ${T.border}`,borderRadius:RADIUS.md,color:T.accent,fontSize:18,fontWeight:800,padding:"11px 12px",outline:"none",fontFamily:FONT }}/>
                    <span style={{ color:T.textSub,fontSize:14 }}>%</span>
                  </div>
                </div>
              </div>
            </>)}
          </div>
        )}
        {launchStep===2&&(
          <div style={{ display:"flex",flexDirection:"column",gap:16 }}>
            <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}><span style={{ background:T.accent+"15",color:T.accent,borderRadius:RADIUS.pill,padding:"4px 12px",fontSize:12,fontWeight:600 }}>{lp.name}</span>{lp.marketplaceIds.map(id=>{ const m=marketplaces.find(x=>x.id===id); return m?<span key={id} style={{ background:T.green+"15",color:T.green,borderRadius:RADIUS.pill,padding:"4px 12px",fontSize:12 }}>{mktIcon(m)} {m.name}</span>:null; })}</div>
            <div><div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8 }}>Net Payout / Unit ({currSym()})</div><input type="number" value={lp.payout||""} onChange={e=>setLaunchProduct(p=>({...p,payout:parseFloat(e.target.value)||0}))} placeholder="0.00" autoFocus style={{ width:"100%",background:T.surface,border:`1.5px solid ${T.border}`,borderRadius:RADIUS.md,color:T.green,fontSize:22,fontWeight:800,padding:"11px 14px",outline:"none",fontFamily:FONT,boxSizing:"border-box" }}/></div>
            {!globalSimpleMode && (<>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
              <div>
                <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8,display:"inline-flex",alignItems:"center" }}>% of Marketplace Sales <Info tip="pctOfMarket"/></div>
                <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                  <input type="number" value={lp.pctOfMarket||""} onChange={e=>setLaunchProduct(p=>({...p,pctOfMarket:parseFloat(e.target.value)||0}))} placeholder="0"
                    style={{ flex:1,background:T.surface,border:`1.5px solid ${T.border}`,borderRadius:RADIUS.md,color:T.accent,fontSize:16,fontWeight:700,padding:"9px 12px",outline:"none",fontFamily:FONT }}/>
                  <span style={{ color:T.textSub }}>%</span>
                </div>
              </div>
              <div>
                <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8,display:"inline-flex",alignItems:"center" }}
 >Delivery / Unit <Info tip="deliveryCost"/></div>
                <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                  <span style={{ color:T.textSub }}>{currSym()}</span>
                  <input type="number" value={lp.deliveryCost||""} onChange={e=>setLaunchProduct(p=>({...p,deliveryCost:parseFloat(e.target.value)||0}))} placeholder="0"
                    style={{ flex:1,background:T.surface,border:`1.5px solid ${T.border}`,borderRadius:RADIUS.md,color:T.text,fontSize:16,fontWeight:700,padding:"9px 12px",outline:"none",fontFamily:FONT }}/>
                </div>
              </div>
            </div>

            {/* PPC + Refunds */}
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
              <div style={{ background:T.surface,borderRadius:RADIUS.md,padding:"12px 14px" }}>
                <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8,display:"inline-flex",alignItems:"center" }}
 >Advertising (PPC) — ACOS <Info tip="adCostRate"/></div>
                <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                  <input type="number" value={lp.ppcRate||""} onChange={e=>setLaunchProduct(p=>({...p,ppcRate:parseFloat(e.target.value)||0}))} placeholder="0"
                    style={{ flex:1,background:T.card,border:`1.5px solid ${"#BF5AF2"}44`,borderRadius:RADIUS.sm,color:"#BF5AF2",fontSize:15,fontWeight:700,padding:"8px 10px",outline:"none",fontFamily:FONT }}/>
                  <span style={{ color:T.textSub }}>%</span>
                </div>
              </div>
              <div style={{ background:T.surface,borderRadius:RADIUS.md,padding:"12px 14px" }}>
                <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8,display:"inline-flex",alignItems:"center" }}
 >Refund Rate <Info tip="refundRate"/></div>
                <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                  <input type="number" value={lp.refundRate||""} onChange={e=>setLaunchProduct(p=>({...p,refundRate:parseFloat(e.target.value)||0}))} placeholder="0"
                    style={{ flex:1,background:T.card,border:`1.5px solid ${T.red}44`,borderRadius:RADIUS.sm,color:T.red,fontSize:15,fontWeight:700,padding:"8px 10px",outline:"none",fontFamily:FONT }}/>
                  <span style={{ color:T.textSub }}>%</span>
                </div>
              </div>
            </div>

            {/* COGS */}
            <div style={{ padding:"12px 14px",background:T.bg,borderRadius:RADIUS.md }}>
              <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:10,display:"inline-flex",alignItems:"center" }}
 >Cost of Goods Sold (COGS) — Optional <Info tip="cogs"/></div>
              <div style={{ display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10 }}>
                {[
                  ["productCost","Product Cost ({currSym()})","#e67e22","productCostFactory"],
                  ["shipping","Inbound Shipping ({currSym()})","#5E5CE6","productCostShipping"],
                  ["customs","Import Duty ({currSym()})","#FF2D55","productCostCustoms"],
                  ["prep","Prep & Labeling ({currSym()})","#30D158","productCostPrep"],
                ].map(([field,label,clr,tipKey])=>(
                  <div key={field}>
                    <div style={{ fontSize:10,color:T.textSub,marginBottom:6,display:"inline-flex",alignItems:"center" }}>{label.replace("{currSym()}",currSym())} <Info tip={tipKey}/></div>
                    <input type="number" value={lp.cogs?.[field]||""} placeholder="0"
                      onChange={e=>setLaunchProduct(p=>({...p,cogs:{...(p.cogs||{}), [field]:parseFloat(e.target.value)||0}}))}
                      style={{ width:"100%",background:T.card,border:`1px solid ${T.border}`,borderRadius:RADIUS.sm,color:clr,fontSize:13,fontWeight:700,padding:"7px 10px",outline:"none",fontFamily:FONT,boxSizing:"border-box" }}/>
                  </div>
                ))}
              </div>
            </div>
            </>)}
          </div>
        )}
      </BottomSheet>

      {/* Import Report BottomSheet */}
      <BottomSheet open={showImportModal} onClose={()=>{setShowImportModal(false);setImportFile(null);}}
        title="📥 Import Report" height="80vh"
        footer={<><Btn onClick={()=>{setShowImportModal(false);setImportFile(null);}} variant="outline" color={T.textSub} style={{flex:1}}>Cancel</Btn><Btn onClick={()=>{setShowImportModal(false);setImportFile(null);}} color={T.green} disabled={!importFile} style={{flex:2}}>Import & Calculate</Btn></>}>
        <div style={{ display:"flex",flexDirection:"column",gap:16 }}>
          <div style={{ fontSize:12,color:T.textSub,lineHeight:1.6 }}>Upload a CSV report to automatically calculate your blended payout per unit.</div>
          <div>
            <div style={{ fontSize:11,fontWeight:600,color:T.textSub,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8 }}>Report Type</div>
            <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
              {[["amazon_transactions","🛒 Amazon Transactions","All Transactions CSV from Seller Central → Payments → Reports"],["amazon_ads","📣 Amazon Ads","Advertising Report CSV — deducts ad spend from payout"],["shopify","🛍 Shopify Payouts","Shopify Payments payout export"],["custom","📋 Custom CSV","Any CSV with date + amount columns"]].map(([v,l,desc])=>(
                <button key={v} onClick={()=>setImportType(v)} style={{ padding:"8px 14px",borderRadius:RADIUS.md,fontSize:12,fontFamily:FONT,cursor:"pointer",textAlign:"left",background:importType===v?T.accent+"18":"transparent",color:importType===v?T.accent:T.textSub,border:`1px solid ${importType===v?T.accent:T.border}` }}>
                  <div style={{ fontWeight:700 }}>{l}</div>
                  <div style={{ fontSize:10,opacity:0.7,marginTop:2 }}>{desc}</div>
                </button>
              ))}
            </div>
          </div>
          <label style={{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8,padding:"24px 16px",borderRadius:RADIUS.lg,border:`2px dashed ${importFile?T.green:T.border}`,background:importFile?T.green+"08":T.surface,cursor:"pointer" }}>
            <input type="file" accept=".csv,.txt" style={{ display:"none" }} onChange={e=>setImportFile(e.target.files?.[0]||null)}/>
            <div style={{ fontSize:28 }}>{importFile?"✅":"📄"}</div>
            <div style={{ fontSize:13,color:importFile?T.green:T.textSub,fontWeight:importFile?700:400 }}>{importFile?importFile.name:"Click to select CSV file"}</div>
            {!importFile&&<div style={{ fontSize:11,color:T.textSub,opacity:0.6 }}>or drag & drop here</div>}
          </label>
        </div>
      </BottomSheet>

      {marketplaces.length===0?(
        <div style={{ textAlign:"center",padding:"80px 20px" }}>
          <div style={{ fontSize:64,marginBottom:16 }}>🏪</div>
          <div style={{ fontSize:22,fontWeight:700,color:T.text,marginBottom:8,letterSpacing:-0.5 }}>No marketplaces yet</div>
          <div style={{ fontSize:15,marginBottom:12,color:T.textSub }}>Add your first marketplace to start building your cashflow.</div>
          <div style={{ fontSize:13,marginBottom:28,color:T.textSub,opacity:0.85,maxWidth:420,margin:"0 auto 28px" }}>
            <strong style={{ color:T.text }}>Shopify</strong> auto-syncs products, orders and payouts. Amazon, Walmart and eBay are coming soon — use manual entry for now.
          </div>
          <Btn ref={addMktBtnRef} onClick={()=>setShowAddMktModal(true)} color={T.accent} size="lg">+ Add First Marketplace</Btn>
        </div>
      ):(
        <>
          {/* ── Inventory Pools ── */}
          {activeTab === "pools" && (
          <>
          <div style={{ marginBottom:16, padding:"12px 16px", background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:inventoryPools.length?12:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:14, fontWeight:700, color:T.text }}>📦 Inventory Pools</span>
                <Tooltip content="Inventory Pools let you group marketplaces that share the same physical stock. For example, Amazon and Shopify might sell from your FBA warehouse, while eBay ships from a separate 3PL. Each pool has its own stock count, cost per unit, and stockout projections. If no pools are created, all marketplaces share one global inventory.">
                  <span style={{ width:16, height:16, borderRadius:"50%", border:`1.5px solid ${T.textSub}35`, display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:T.textSub, cursor:"help" }}>i</span>
                </Tooltip>
              </div>
              {(!can || can("marketplaces","edit")) && (
                <button onClick={()=>{ if(poolOps) poolOps.add("New Pool"); }}
                  style={{ background:T.accent+"15", border:`1px solid ${T.accent}30`, borderRadius:RADIUS.sm, padding:"4px 12px", fontSize:12, fontFamily:FONT, cursor:"pointer", color:T.accent, fontWeight:600 }}>
                  + Add Pool
                </button>
              )}
            </div>

            {inventoryPools.length === 0 && (
              <div style={{ fontSize:12, color:T.textSub, lineHeight:1.5 }}>
                No pools configured — all marketplaces share one global inventory. Add a pool to separate stock by warehouse or fulfillment channel.
              </div>
            )}

            {/* Marketplace filter for the pool list. Click a marketplace pill
                to show only pools that touch that marketplace. "All" shows
                everything (the default). Helpful when you have many pools
                and want to find the one for a specific marketplace. */}
            {inventoryPools.length > 0 && marketplaces.length > 0 && (
              <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap", marginBottom:10 }}>
                <span style={{ fontSize:11, color:T.textSub, fontWeight:600, letterSpacing:0.3 }}>FILTER:</span>
                <button onClick={() => setPoolFilterMktId(null)}
                  style={{
                    padding:"3px 9px", borderRadius:RADIUS.pill, fontSize:11, fontFamily:FONT,
                    cursor:"pointer", fontWeight:!poolFilterMktId?700:500,
                    background: !poolFilterMktId ? T.accent+"22" : "transparent",
                    border:`1px solid ${!poolFilterMktId ? T.accent : T.border}`,
                    color: !poolFilterMktId ? T.accent : T.textSub,
                  }}>All</button>
                {marketplaces.map(m => (
                  <button key={m.id} onClick={() => setPoolFilterMktId(poolFilterMktId === m.id ? null : m.id)}
                    style={{
                      padding:"3px 9px", borderRadius:RADIUS.pill, fontSize:11, fontFamily:FONT,
                      cursor:"pointer", fontWeight:poolFilterMktId===m.id?700:500, whiteSpace:"nowrap",
                      background: poolFilterMktId === m.id ? T.accent+"22" : "transparent",
                      border:`1px solid ${poolFilterMktId === m.id ? T.accent : T.border}`,
                      color: poolFilterMktId === m.id ? T.accent : T.textSub,
                    }}>
                    {poolFilterMktId === m.id ? "✓ " : ""}{m.name}
                  </button>
                ))}
              </div>
            )}

            {inventoryPools
              .filter(p => !poolFilterMktId || (p.marketplaceIds || []).includes(poolFilterMktId))
              .map(pool => {
              const isEditing = editPoolId === pool.id;
              const assignedMkts = marketplaces.filter(m => (pool.marketplaceIds||[]).includes(m.id));
              const unassignedMkts = marketplaces.filter(m => {
                if ((pool.marketplaceIds||[]).includes(m.id)) return false;
                // Check if assigned to another pool
                return !inventoryPools.some(p => p.id !== pool.id && (p.marketplaceIds||[]).includes(m.id));
              });
              return (
                <div key={pool.id} style={{ border:`1px solid ${T.border}`, borderRadius:RADIUS.md, padding:"10px 14px", marginBottom:8, background:T.surface }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                    {isEditing ? (
                      <input type="text" value={pool.name} onChange={e=>poolOps?.update(pool.id,{name:e.target.value})}
                        style={{ background:T.card, border:`1px solid ${T.accent}`, borderRadius:RADIUS.sm, color:T.text, fontSize:14, fontWeight:700, padding:"4px 8px", outline:"none", fontFamily:FONT, width:200 }}
                        autoFocus onBlur={()=>setEditPoolId(null)} onKeyDown={e=>{if(e.key==="Enter")setEditPoolId(null);}}/>
                    ) : (
                      <span style={{ fontSize:14, fontWeight:700, color:T.text }}>{pool.name}</span>
                    )}
                    <div style={{ display:"flex", gap:6 }}>
                      <button onClick={()=>setEditPoolId(isEditing?null:pool.id)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:14, color:T.textSub }}>✏️</button>
                      <button onClick={()=>setConfirmDelete({type:"pool",poolId:pool.id,name:pool.name})}
                        style={{ background:"none", border:"none", cursor:"pointer", fontSize:14, color:T.red }}>🗑️</button>
                    </div>
                  </div>

                  {/* Marketplace assignment pills */}
                  <div style={{ marginBottom:8 }}>
                    <div style={{ fontSize:12, color:T.textSub, marginBottom:4, display:"flex", alignItems:"center", gap:6 }}>
                      Marketplaces in this pool:
                      {pool.isShared && (
                        <Tooltip content="Inventory is physically SHARED across these marketplaces. A sale on any of them decrements the same stock counter. COG, targets, and supplier orders all live at the pool level.">
                          <span style={{ fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:RADIUS.pill, background:T.accent+"22", color:T.accent, border:`1px solid ${T.accent}55`, letterSpacing:0.4 }}>
                            🔗 SHARED
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip content="Assigning a marketplace to this pool affects: ⚡ Targets page — stockout/reorder/inventory KPIs use this pool's stock. ⚡ Orders — orders tagged for any of these marketplaces feed this pool's inventory. ⚡ Dashboard — inventory value rolls up to this pool.">
                        <span style={{ width:14, height:14, borderRadius:"50%", border:`1.5px solid ${T.textSub}35`, display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:T.textSub, cursor:"help" }}>i</span>
                      </Tooltip>
                    </div>
                    <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                      {assignedMkts.map(m => (
                        <button key={m.id} onClick={()=>{
                          const next = (pool.marketplaceIds||[]).filter(id=>id!==m.id);
                          // If removing brings the pool back to <=1 marketplace, also clear isShared
                          const patch = { marketplaceIds: next };
                          if (next.length <= 1) patch.isShared = false;
                          poolOps?.update(pool.id, patch);
                        }}
                          style={{ padding:"3px 10px", borderRadius:RADIUS.pill, fontSize:12, fontFamily:FONT, cursor:"pointer", fontWeight:700, background:T.accent+"18", border:`1px solid ${T.accent}`, color:T.accent }}>
                          {m.name} ✕
                        </button>
                      ))}
                      {unassignedMkts.map(m => (
                        <button key={m.id} onClick={() => addMktToPool(pool, m.id)}
                          style={{ padding:"3px 10px", borderRadius:RADIUS.pill, fontSize:12, fontFamily:FONT, cursor:"pointer", fontWeight:400, background:"transparent", border:`1px solid ${T.border}`, color:T.textSub }}>
                          + {m.name}
                        </button>
                      ))}
                      {assignedMkts.length === 0 && unassignedMkts.length === 0 && (
                        <span style={{ fontSize:12, color:T.textSub }}>No marketplaces available</span>
                      )}
                    </div>
                  </div>

                  {/* Stock info */}
                  <div style={{ display:"flex", gap:12, alignItems:"center", flexWrap:"wrap", fontSize:12, color:T.textSub }}>
                    <span><strong style={{ color:T.text }}>{(pool.initialStock||0).toLocaleString()}</strong> units</span>
                    {/* Source badge — tells the user where this number came from */}
                    {pool.stockOverridden ? (
                      <span title="Manual user override — never overwritten by API sync" style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:RADIUS.pill, background:T.accent+"15", border:`1px solid ${T.accent}40`, color:T.accent }}>🔒 Manual override</span>
                    ) : (
                      pool.apiStockSnapshot != null && (
                        <span title={`From API sync · ${pool.apiStockSnapshotDate || "recent"}`} style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:RADIUS.pill, background:T.green+"15", border:`1px solid ${T.green}40`, color:T.green }}>📡 API sync</span>
                      )
                    )}
                    <span><strong style={{ color:T.text }}>{currSym()}{pool.initialCostPerUnit||0}</strong>/unit</span>
                    {pool.initialStockDate && <span>Since {pool.initialStockDate}</span>}
                    {/* "↺ Use API value" — only shown when override is on AND
                        we have a fresh API snapshot to revert to */}
                    {pool.stockOverridden && pool.apiStockSnapshot != null && pool.apiStockSnapshot !== pool.initialStock && (
                      <button
                        onClick={async () => {
                          const ok = await showConfirm({
                            icon: "↺", title: `Revert "${pool.name}" to API value?`,
                            message: `This will set stock to ${pool.apiStockSnapshot.toLocaleString()} units (from ${pool.apiStockSnapshotDate || "last sync"}) and clear your manual override.`,
                            confirmLabel: "Revert", danger: false,
                          });
                          if (ok) {
                            poolOps?.update?.(pool.id, {
                              initialStock: pool.apiStockSnapshot,
                              initialStockDate: pool.apiStockSnapshotDate,
                              stockOverridden: false,
                            });
                          }
                        }}
                        style={{ background:T.green+"10", border:`1px solid ${T.green}40`, borderRadius:RADIUS.sm, padding:"2px 8px", fontSize:11, fontFamily:FONT, cursor:"pointer", color:T.green, fontWeight:600 }}
                        title={`Last API value: ${pool.apiStockSnapshot} units`}
                      >↺ Use API value ({pool.apiStockSnapshot.toLocaleString()})</button>
                    )}
                    <Tooltip content="Edit the starting stock for this pool. ⚡ Affects: Targets stockout/reorder calculations, Dashboard inventory value, and all cashflow projections for marketplaces in this pool. Saving locks the value as a manual override that survives API syncs.">
                    <button onClick={()=>{
                      setPoolStockInput(String(pool.initialStock||""));
                      setPoolCostInput(String(pool.initialCostPerUnit||""));
                      setEditPoolId(pool.id+"_stock");
                    }} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, padding:"2px 8px", fontSize:12, fontFamily:FONT, cursor:"pointer", color:T.textSub }}>
                      Edit Stock
                    </button>
                    </Tooltip>
                  </div>

                  {/* Inline stock editing */}
                  {editPoolId === pool.id+"_stock" && (
                    <div style={{ display:"flex", gap:8, alignItems:"center", marginTop:8, flexWrap:"wrap" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                        <span style={{ fontSize:12, color:T.textSub }}>Units:</span>
                        <input type="number" value={poolStockInput} onChange={e=>setPoolStockInput(e.target.value)}
                          style={{ width:80, background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.text, fontSize:13, fontWeight:700, padding:"4px 8px", outline:"none", fontFamily:FONT }}/>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                        <span style={{ fontSize:12, color:T.textSub }}>Cost/unit:</span>
                        <input type="number" value={poolCostInput} onChange={e=>setPoolCostInput(e.target.value)}
                          style={{ width:80, background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.text, fontSize:13, fontWeight:700, padding:"4px 8px", outline:"none", fontFamily:FONT }}/>
                      </div>
                      <button onClick={()=>{
                        const newUnits = parseInt(poolStockInput)||0;
                        const newCost  = parseFloat(poolCostInput)||0;
                        const oldUnits = Number(pool.initialStock)||0;
                        const oldCost  = Number(pool.initialCostPerUnit)||0;
                        const stockChanged = newUnits !== oldUnits;
                        const costChanged  = newCost !== oldCost;
                        // Save handler — for stock, mark the pool as
                        // overridden so subsequent API syncs respect it.
                        const commit = () => {
                          // setStock writes initialStock + cost; we then
                          // patch the override flag separately so the API
                          // sync knows to leave the value alone.
                          poolOps?.setStock(pool.id, newUnits, newCost, "Manual edit");
                          if (stockChanged) {
                            poolOps?.update?.(pool.id, { stockOverridden: true });
                          }
                          setEditPoolId(null);
                          setCogWarning(null);
                        };
                        // Build the cascade-impact list once for any warning.
                        const mktsInPool = marketplaces.filter(m => (pool.marketplaceIds||[]).includes(m.id));
                        const mktNames = mktsInPool.map(m => m.name).join(", ") || "(no marketplaces yet)";

                        if (stockChanged && costChanged) {
                          setCogWarning({
                            title: `Override pool stock and cost?`,
                            message: `Setting "${pool.name}" to ${newUnits.toLocaleString()} units at ${currSym()}${newCost}/unit will OVERRIDE the entire stock + COG hierarchy for this pool.\n\nMarketplaces affected: ${mktNames}\n\n• Stock will be locked as a manual override — API syncs won't overwrite it (you can revert via "↺ Use API value")\n• Money in Inventory, Runway, and Stockout KPIs recompute using these values\n\nProceed?`,
                            onConfirm: commit,
                            onCancel: () => setCogWarning(null),
                          });
                        } else if (stockChanged) {
                          setCogWarning({
                            title: `Override pool stock?`,
                            message: `Locking "${pool.name}" stock to ${newUnits.toLocaleString()} units will OVERRIDE every per-marketplace, per-product, and API stock value for products in this pool.\n\nMarketplaces affected: ${mktNames}\n\nThis becomes a sticky manual override — subsequent API syncs will store new values in a snapshot but won't replace your number until you click "↺ Use API value".\n\nProceed?`,
                            onConfirm: commit,
                            onCancel: () => setCogWarning(null),
                          });
                        } else if (costChanged) {
                          setCogWarning({
                            title: `Override pool cost-per-unit?`,
                            message: `Setting "${pool.name}" cost to ${currSym()}${newCost}/unit will OVERRIDE every marketplace COG, group COG, and per-product COGS for products in this pool.\n\nMarketplaces affected: ${mktNames}\n\nMoney in Inventory, Runway, and Stockout KPIs will recompute using ${currSym()}${newCost}/unit. Change this back to 0 to fall back to the marketplace/group/product hierarchy.`,
                            onConfirm: commit,
                            onCancel: () => setCogWarning(null),
                          });
                        } else {
                          commit();
                        }
                      }} style={{ background:T.green+"15", border:`1px solid ${T.green}30`, borderRadius:RADIUS.sm, padding:"4px 12px", fontSize:12, fontFamily:FONT, cursor:"pointer", color:T.green, fontWeight:600 }}>Save</button>
                      <button onClick={()=>setEditPoolId(null)} style={{ background:"transparent", border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, padding:"4px 12px", fontSize:12, fontFamily:FONT, cursor:"pointer", color:T.textSub }}>Cancel</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
          )}

          {/* ── Marketplace carousel — always pinned at top ── */}
          {activeTab === "marketplaces" && (
          <>
          {/* Phase 7 redesign — the 3-column carousel + side panels are
              replaced by a single MktDashboard component: marketplace
              chip selector on top + horizontal row of action and KPI
              cards. Every handler the old MktSideColumn used is still
              wired up below, just routed through the new card row. */}
          <MktDashboard
            T={T}
            marketplaces={marketplaces}
            selectedMkt={selectedMkt}
            selectedMktId={selectedMktId}
            setSelectedMktId={setSelectedMktId}
            salesWindow={salesWindow}
            setSalesWindow={setSalesWindow}
            currSymStr={currSym()}
            orders={orders}
            onConnect={(platform, mktId) => { setConnectInput({ shopDomain:"", clientId:"", clientSecret:"" }); setConnectSheet({ platform: platform || selectedMkt?.platform, mktId: mktId || selectedMkt?.id }); }}
            onDisconnect={() => setConfirmDelete({ type:"api_disconnect", mktId:selectedMkt.id, name:selectedMkt.name+" API" })}
            onRecompute={() => {
              const win = salesWindow;
              const updatedMkts = (marketplaces || []).map(m => {
                const products = (m.products || []).map(p => {
                  if (p._decoupled) return { ...p, recentSalesVolume: 0, _recentSalesVolume: 0 };
                  const windowVol = p._salesWindows?.[win]?.units;
                  const v = windowVol != null ? windowVol : (Number(p.recentSalesVolume ?? p._recentSalesVolume) || 0);
                  return { ...p, recentSalesVolume: v, _recentSalesVolume: v };
                });
                const mktVol = products.filter(p => !p._decoupled).reduce((s, p) => s + (p.recentSalesVolume || 0), 0);
                return { ...m, products: recomputeProductShares(products), recentSalesVolume: mktVol, _recentSalesVolume: mktVol };
              });
              mktOps.setMarketplacesAll?.(recomputeMarketplaceShares(updatedMkts));
            }}
            onTrySample={async () => {
              if (!selectedMkt) return;
              const platform = selectedMkt.platform || ((selectedMkt.name||"").toLowerCase().includes("shopify") ? "shopify" : (selectedMkt.name||"").toLowerCase().includes("amazon") ? "amazon" : (selectedMkt.name||"").toLowerCase().includes("walmart") ? "walmart" : (selectedMkt.name||"").toLowerCase().includes("ebay") ? "ebay" : null);
              if (!platform) return;
              try {
                const { applyFixtureToState } = await import("../lib/marketplaceFixtures.js");
                const state = { marketplaces, orders, months };
                await applyFixtureToState(state, selectedMkt.id, platform, { marketplaceName: selectedMkt.name });
                mktOps.setMarketplacesAll?.(state.marketplaces);
                if (state.orders?.length) mktOps.setOrders?.(state.orders);
                setInfoAlert({ icon: "✓", title: `Sample data loaded for ${selectedMkt.name}`, message: "Fixture data loaded. No real API was called." });
              } catch (err) {
                setInfoAlert({ icon: "⚠️", title: "Failed to load sample data", message: err.message });
              }
            }}
            onAddProduct={() => { setQuickProduct({ name:"", payout:"", pctOfMarket:"", deliveryCost:"", ppcRate:"", refundRate:"", refundAmount:"", cogsProduct:"", cogsShipping:"", cogsCustoms:"", cogsPrep:"" }); setShowAddProductSheet(true); }}
            onLaunchProduct={openLaunchModal}
            onToggleFulfilment={() => setShowFBM(f => !f)}
            showFBM={showFBM}
            onAddMarketplace={(!can || can("marketplaces","create")) ? () => setShowAddMktModal(true) : null}
          />
          {/* Page intro + month-override banner removed (Phase 7 redesign).
              Marketplaces no longer surfaces per-month editing — everything
              is global. */}

          {/* Add Product BottomSheet — Simple/Advanced modes */}
          {selectedMkt && (
            <BottomSheet open={showAddProductSheet} onClose={()=>setShowAddProductSheet(false)}
              title="+ Add Product"
              subtitle={
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:12 }}>To {selectedMkt.name}</span>
                  <div style={{ display:"flex", background:"rgba(255,255,255,0.12)", borderRadius:RADIUS.md, padding:2, gap:1 }}>
                    {[["Simple",true],["Advanced",false]].map(([lbl,val])=>{
                      const act = globalSimpleMode===val;
                      return <button key={lbl} onClick={()=>setSimpleModeAndPersist(val)} style={{ padding:"2px 9px", borderRadius:RADIUS.sm, border:"none", cursor:"pointer", fontSize:11, fontWeight:act?700:400, fontFamily:FONT, background:act?"rgba(255,255,255,0.25)":"transparent", color:"inherit" }}>{lbl}</button>;
                    })}
                  </div>
                </div>
              }
              height={globalSimpleMode ? "auto" : "85vh"}
              footer={<>
                <Btn onClick={()=>setShowAddProductSheet(false)} variant="outline" color={T.textSub} style={{flex:1}}>Cancel</Btn>
                <Btn onClick={()=>{
                  if (!selectedMkt) return;
                  mktOps.addProduct(selectedMkt.id, {
                    id:uid(),
                    name: quickProduct.name.trim()||"New Product",
                    pctOfMarket: (parseFloat(quickProduct.pctOfMarket)||0)/100,
                    payout: parseFloat(quickProduct.payout)||0,
                    deliveryCost: parseFloat(quickProduct.deliveryCost)||0,
                    ppcType: quickProduct.ppcType||"acos",
                    ppcRate: (parseFloat(quickProduct.ppcRate)||0)/100,
                    refundType: quickProduct.refundType||"pct",
                    refundRate: (parseFloat(quickProduct.refundRate)||0)/100,
                    refundAmount: parseFloat(quickProduct.refundAmount)||0,
                    cogs: {
                      productCost: parseFloat(quickProduct.cogsProduct)||0,
                      shipping: parseFloat(quickProduct.cogsShipping)||0,
                      customs: parseFloat(quickProduct.cogsCustoms)||0,
                      prep: parseFloat(quickProduct.cogsPrep)||0,
                    },
                  });
                  setShowAddProductSheet(false);
                }} color={selectedMktColor} style={{flex:2}}>Add Product</Btn>
              </>}>
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                {/* Always: Name */}
                <div>
                  <Label style={{ marginBottom:6 }} tip="productName">Product Name</Label>
                  <input value={quickProduct.name} onChange={e=>setQuickProduct(q=>({...q,name:e.target.value}))}
                    placeholder="e.g. Blue Widget XL" autoFocus
                    style={{ width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.md, color:T.text, padding:"10px 14px", fontSize:14, outline:"none", fontFamily:FONT, boxSizing:"border-box" }}/>
                </div>
                {/* Always: Payout + % */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  <div>
                    <Label style={{ marginBottom:6 }} tip="payout">Net Payout / Unit ({currSym()})</Label>
                    <input type="number" value={quickProduct.payout} onChange={e=>setQuickProduct(q=>({...q,payout:e.target.value}))}
                      placeholder="0.00"
                      style={{ width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.md, color:T.green, padding:"10px 12px", fontSize:15, fontWeight:700, outline:"none", fontFamily:FONT, boxSizing:"border-box" }}/>
                  </div>
                  <div>
                    <Label style={{ marginBottom:6 }} tip={`What % of ${selectedMkt.name}'s total revenue comes from this product? All products in a marketplace must add up to 100%.`}>% of {selectedMkt.name} Sales</Label>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <input type="number" value={quickProduct.pctOfMarket} onChange={e=>setQuickProduct(q=>({...q,pctOfMarket:e.target.value}))}
                        placeholder="0" min={0} max={100}
                        style={{ flex:1, background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.md, color:selectedMktColor, padding:"10px 12px", fontSize:15, fontWeight:700, outline:"none", fontFamily:FONT }}/>
                      <span style={{ color:T.textSub }}>%</span>
                    </div>
                  </div>
                </div>

                {/* Advanced: Delivery + PPC + Refunds + COGS */}
                {!globalSimpleMode && (<>
                  <div>
                    <Label style={{ marginBottom:6 }} tip="deliveryCost">Delivery / Unit ({currSym()})</Label>
                    <input type="number" value={quickProduct.deliveryCost||""} onChange={e=>setQuickProduct(q=>({...q,deliveryCost:e.target.value}))}
                      placeholder="0"
                      style={{ width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.md, color:T.text, padding:"10px 14px", fontSize:14, outline:"none", fontFamily:FONT, boxSizing:"border-box" }}/>
                  </div>

                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                    <div style={{ background:T.surface, borderRadius:RADIUS.md, padding:"12px 14px" }}>
                      <Label style={{ marginBottom:8 }} tip="adCostRate">Advertising / PPC (ACOS %)</Label>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <input type="number" value={quickProduct.ppcRate||""} onChange={e=>setQuickProduct(q=>({...q,ppcRate:e.target.value}))}
                          placeholder="0"
                          style={{ flex:1, background:T.card, border:`1px solid ${"#BF5AF2"}44`, borderRadius:RADIUS.sm, color:"#BF5AF2", padding:"8px 10px", fontSize:14, fontWeight:700, outline:"none", fontFamily:FONT }}/>
                        <span style={{ color:T.textSub, fontSize:13 }}>%</span>
                      </div>
                    </div>
                    <div style={{ background:T.surface, borderRadius:RADIUS.md, padding:"12px 14px" }}>
                      <Label style={{ marginBottom:8 }} tip="refundRate">Refund Rate (%)</Label>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <input type="number" value={quickProduct.refundRate||""} onChange={e=>setQuickProduct(q=>({...q,refundRate:e.target.value}))}
                          placeholder="0"
                          style={{ flex:1, background:T.card, border:`1px solid ${T.red}44`, borderRadius:RADIUS.sm, color:T.red, padding:"8px 10px", fontSize:14, fontWeight:700, outline:"none", fontFamily:FONT }}/>
                        <span style={{ color:T.textSub, fontSize:13 }}>%</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ padding:"12px 14px", background:T.bg, borderRadius:RADIUS.md }}>
                    <Label style={{ marginBottom:10 }} tip="cogs">Cost of Goods Sold (COGS) — Optional</Label>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10 }}>
                      {[
                        ["cogsProduct", "Product Cost", "#e67e22", "productCostFactory"],
                        ["cogsShipping", "Inbound Shipping", "#5E5CE6", "productCostShipping"],
                        ["cogsCustoms", "Import Duty", "#FF2D55", "productCostCustoms"],
                        ["cogsPrep", "Prep & Labeling", "#30D158", "productCostPrep"],
                      ].map(([field, label, clr, tipKey]) => (
                        <div key={field}>
                          <div style={{ fontSize:10, color:T.textSub, marginBottom:5, display:"inline-flex", alignItems:"center" }}>{label} <Info tip={tipKey}/></div>
                          <input type="number" value={quickProduct[field]||""} placeholder="0"
                            onChange={e=>setQuickProduct(q=>({...q,[field]:e.target.value}))}
                            style={{ width:"100%", background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:clr, fontSize:13, fontWeight:700, padding:"7px 10px", outline:"none", fontFamily:FONT, boxSizing:"border-box" }}/>
                        </div>
                      ))}
                    </div>
                  </div>
                </>)}

                {globalSimpleMode && (
                  <div style={{ fontSize:12, color:T.textSub, background:T.surface, borderRadius:RADIUS.md, padding:"10px 14px", lineHeight:1.6 }}>
                    💡 Switch to <strong>Advanced</strong> to set delivery cost, PPC rate, refund rate, and COGS directly during creation.
                  </div>
                )}
              </div>
            </BottomSheet>
          )}

          {/* Fulfillment Costs BottomSheet */}
          <BottomSheet open={showFBM} onClose={()=>setShowFBM(false)}
            title="🏭 Fulfilment & Storage Costs"
 sub
            height="85vh">
            <FulfilmentSection
              fbmSettings={fbmSettings||[]}
              setFbmSettings={setFbmSettings||(() =>{})}
              deliveryCreditLimit={deliveryCreditLimit||0}
              setDeliveryCreditLimit={setDeliveryCreditLimit||(() =>{})}
              marketplaces={marketplaces}
              inventoryPools={inventoryPools}
              saveStatus={saveStatus}
              can={can}
            />
          </BottomSheet>

          {globalPctWarn&&<div data-ch-pct-warn="true" style={{ background:T.red+"12",border:`1px solid ${T.red+"33"}`,borderRadius:RADIUS.md,padding:"10px 16px",marginBottom:16,fontSize:13,color:T.red,fontWeight:600 }}>⚠ Marketplace totals {Math.round(globalTotal*100)}% — must equal 100%</div>}
          {/* ── Selected marketplace details ── */}
          {selectedMkt&&(
            <div ref={productsRef} key={selectedMktId} style={{ animation:"prodSlideIn 0.32s ease both" }}>
              {/* Duplicate header card removed — the carousel tile already
                  shows the name, payout, and product/reserve stats. The
                  left + right columns carry every action button. */}

              {/* Admin + inline panels (Delete marketplace, Channel filter,
                  Brand sharing). Rendered as plain siblings now that the
                  old styled "Controls bar" wrapper has been removed —
                  without a connected pill in it, that wrapper was rendering
                  as an empty pastel box for most sellers. */}
              {isGlobal&&selectedMkt.canDelete&&(!can || can("marketplaces","delete"))&&(
                <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:8 }}>
                  <Btn onClick={()=>setConfirmDelete({type:"mkt",mktId:selectedMkt.id,name:selectedMkt.name})} color={T.red} size="sm">Delete</Btn>
                </div>
              )}
              {false && <div style={{ display:"flex",gap:10,flexWrap:"wrap" }}>
                    <Tooltip content="What percentage of your total business revenue comes from this marketplace? Example: if Amazon generates 60% of your income and Shopify 40%, Amazon gets 60 here. All marketplaces must add up to 100%. This drives your cashflow income calculation — higher % = larger share of your monthly income estimate.">
 <div style={{ textAlign:"center" }}><div style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.4,marginBottom:3,display:"inline-flex",alignItems:"center",gap:3 }} >% Total <Info tip="pctOfTotal"/>{selectedMkt._overridden?.pctOfTotal && selectedMkt._apiSnapshot?.pctOfTotal !== undefined && <button onClick={()=>resetMktField(selectedMkt,"pctOfTotal")} title="Reset to API value" style={{ marginLeft:3, padding:"1px 6px", fontSize:8, fontWeight:700, background:T.green+"15", border:`1px solid ${T.green}40`, borderRadius:RADIUS.pill, color:T.green, fontFamily:FONT, cursor:"pointer" }}>↺</button>}</div><div style={{ display:"flex",alignItems:"center",gap:3 }}><input type="number" value={Math.round((selectedMkt.pctOfTotal||0)*100)} placeholder="0" onChange={e=>updateMktWithOverrideCheck(selectedMkt,{pctOfTotal:(parseFloat(e.target.value)||0)/100})} style={{ background:T.surface,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:selectedMktColor,fontSize:14,fontWeight:700,padding:"4px 8px",width:54,outline:"none",fontFamily:FONT,textAlign:"center" }}/><span style={{ ...subText(T) }}>%</span></div></div>
                    </Tooltip>
                    <Tooltip content="How many days after a sale does this marketplace transfer money to your bank account? Amazon typically pays every 14 days (enter 14). Shopify Payments is usually 2-3 days (enter 2). This shifts your income in the cashflow by exactly this many days, so you see when cash actually arrives — not just when sales happen.">
 <div style={{ textAlign:"center" }}><div style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.4,marginBottom:3,display:"inline-flex",alignItems:"center",gap:3 }} >Payout delay <Info tip="mktPayoutDelay"/>{selectedMkt._overridden?.payoutDelay && selectedMkt._apiSnapshot?.payoutDelay !== undefined && <button onClick={()=>resetMktField(selectedMkt,"payoutDelay")} title="Reset to API value" style={{ marginLeft:3, padding:"1px 6px", fontSize:8, fontWeight:700, background:T.green+"15", border:`1px solid ${T.green}40`, borderRadius:RADIUS.pill, color:T.green, fontFamily:FONT, cursor:"pointer" }}>↺</button>}</div><div style={{ display:"flex",alignItems:"center",gap:3 }}><input type="number" value={selectedMkt.payoutDelay||""} placeholder="0" onChange={e=>updateMktWithOverrideCheck(selectedMkt,{payoutDelay:parseFloat(e.target.value)||0})} style={{ background:T.surface,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:T.text,fontSize:14,fontWeight:700,padding:"4px 8px",width:54,outline:"none",fontFamily:FONT,textAlign:"center" }}/><span style={{ ...subText(T) }}>d</span></div></div>
                    </Tooltip>
                    {/* Reserve held + Reserve rate — two fields side by side.
                        Reserve held: the dollar amount (manual override or API-synced).
                        Reserve rate: a percentage for platforms without a reserve API
                        (eBay, Walmart) — the app estimates the held amount from
                        recent payouts automatically. */}
                    <Tooltip content="Amount currently held in reserve by this marketplace. For Amazon/Shopify this is auto-synced from the API. For eBay/Walmart enter the amount manually, or set a Reserve Rate (%) to estimate from recent payouts.">
 <div style={{ textAlign:"center" }}>
   <div style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.4,marginBottom:3,display:"inline-flex",alignItems:"center",gap:3 }}>
     Reserve held
     {/* Source badge */}
     {selectedMkt.apiConnection?.reservesHeld > 0 && !selectedMkt._overridden?.reserveHeld
       ? <span style={{ fontSize:7, padding:"1px 4px", borderRadius:RADIUS.pill, background:T.green+"18", color:T.green, fontWeight:700 }}>API</span>
       : selectedMkt._overridden?.reserveHeld
         ? <span style={{ fontSize:7, padding:"1px 4px", borderRadius:RADIUS.pill, background:T.accent+"18", color:T.accent, fontWeight:700 }}>MANUAL</span>
         : Number(selectedMkt.reserveRate) > 0
           ? <span style={{ fontSize:7, padding:"1px 4px", borderRadius:RADIUS.pill, background:T.orange+"18", color:T.orange, fontWeight:700 }}>{Math.round(selectedMkt.reserveRate*100)}%</span>
           : null}
     {selectedMkt._overridden?.reserveHeld && selectedMkt._apiSnapshot?.reserveHeld !== undefined && <button onClick={()=>resetMktField(selectedMkt,"reserveHeld")} title="Reset to API value" style={{ marginLeft:3, padding:"1px 6px", fontSize:8, fontWeight:700, background:T.green+"15", border:`1px solid ${T.green}40`, borderRadius:RADIUS.pill, color:T.green, fontFamily:FONT, cursor:"pointer" }}>↺</button>}
   </div>
   <div style={{ display:"flex",alignItems:"center",gap:3 }}><span style={{ ...subText(T) }}>{currSym()}</span><input type="number" value={selectedMkt.reserveHeld||""} placeholder="0" onChange={e=>updateMktWithOverrideCheck(selectedMkt,{reserveHeld:parseFloat(e.target.value)||0})} style={{ background:T.surface,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:T.orange,fontSize:14,fontWeight:700,padding:"4px 8px",width:70,outline:"none",fontFamily:FONT,textAlign:"center" }}/></div>
 </div>
                    </Tooltip>
                    <Tooltip content="Reserve rate (%) — for platforms that don't expose reserves via API (eBay, Walmart). Enter your current reserve percentage from Seller Hub/Seller Center. The app estimates the held amount from your recent payout total. Leave at 0 if the platform syncs reserves automatically (Amazon, Shopify) or you prefer to enter the exact amount manually.">
 <div style={{ textAlign:"center" }}><div style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.4,marginBottom:3,display:"inline-flex",alignItems:"center",gap:3 }} >Reserve rate</div><div style={{ display:"flex",alignItems:"center",gap:3 }}><input type="number" value={selectedMkt.reserveRate ? Math.round(selectedMkt.reserveRate*100) : ""} placeholder="0" onChange={e=>mktOps.update(selectedMkt.id,{reserveRate:(parseFloat(e.target.value)||0)/100})} style={{ background:T.surface,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:T.orange,fontSize:14,fontWeight:700,padding:"4px 8px",width:50,outline:"none",fontFamily:FONT,textAlign:"center" }}/><span style={{ ...subText(T) }}>%</span></div></div>
                    </Tooltip>
                    <Tooltip content="Marketplace-level COG. Hierarchy: pool > marketplace > group > per-product. Setting this here overrides every group and per-product COGS in THIS marketplace. Drives Money in Inventory, Runway, and Stockout for any pool whose own cost-per-unit is unset. Leave at 0 to defer to group/per-product COGS.">
 <div style={{ textAlign:"center" }}><div style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.4,marginBottom:3,display:"inline-flex",alignItems:"center",gap:3 }} >Avg COGS/unit</div><div style={{ display:"flex",alignItems:"center",gap:3 }}><span style={{ ...subText(T) }}>{currSym()}</span><input type="number"
                          value={mktAvgCogsDraft && mktAvgCogsDraft.mktId === selectedMkt.id ? mktAvgCogsDraft.value : (selectedMkt.avgCogs||"")}
                          placeholder="0"
                          onChange={e=>setMktAvgCogsDraft({ mktId: selectedMkt.id, value: e.target.value })}
                          onBlur={()=>{
                            if (!mktAvgCogsDraft || mktAvgCogsDraft.mktId !== selectedMkt.id) return;
                            const newVal = parseFloat(mktAvgCogsDraft.value) || 0;
                            const oldVal = Number(selectedMkt.avgCogs) || 0;
                            if (newVal === oldVal) { setMktAvgCogsDraft(null); return; }
                            const commit = () => {
                              mktOps.update(selectedMkt.id, { avgCogs: newVal });
                              setMktAvgCogsDraft(null);
                              setCogWarning(null);
                            };
                            const cancel = () => { setMktAvgCogsDraft(null); setCogWarning(null); };
                            // Show the cascade warning. List groups/products that get overridden.
                            const groupCount = (selectedMkt.productGroups || []).length;
                            const prodCount = (selectedMkt.products || []).length;
                            setCogWarning({
                              kind: "mkt",
                              title: `Override marketplace COG?`,
                              message: `Setting "${selectedMkt.name}" Avg COG to ${currSym()}${newVal}/unit will OVERRIDE every group COG and per-product COGS inside THIS marketplace only — other marketplaces (even ones in the same inventory pool) keep their own values.\n\nProducts affected: ${prodCount}${groupCount ? ` · Groups affected: ${groupCount}` : ""}\n\nMoney in Inventory, Runway, and Stockout KPIs will recompute. Set back to 0 to fall back to the group/per-product hierarchy.`,
                              onConfirm: commit,
                              onCancel: cancel,
                            });
                          }}
                          onKeyDown={e=>{ if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setMktAvgCogsDraft(null); e.currentTarget.blur(); } }}
                          style={{ background:T.surface,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:T.text,fontSize:14,fontWeight:700,padding:"4px 8px",width:70,outline:"none",fontFamily:FONT,textAlign:"center" }}/></div></div>
                    </Tooltip>
                    {/* Marketplace-level Ad Cost — same fallback pattern as Avg COGS.
                        APIs don't return advertising spend (it lives in separate
                        ad-platform APIs we don't integrate). Used as a default for
                        every product on this marketplace that doesn't override it
                        per-product or via a parent group. */}
                    <Tooltip content="Marketplace-level fallback for advertising cost per unit. None of the marketplace APIs return ad spend (it lives in separate ad-platform APIs). Hierarchy: (1) per-product ad cost if filled in, (2) parent group ad cost, (3) this marketplace default, (4) zero. Pick % of payout (e.g. 12% ACoS) or fixed $ per unit. Subtracted from your projected payout in every brand-level KPI.">
 <div style={{ textAlign:"center" }}><div style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.4,marginBottom:3,display:"inline-flex",alignItems:"center",gap:3 }} >Ad cost/unit</div><div style={{ display:"flex",alignItems:"center",gap:3 }}>
                        <Seg value={selectedMkt.adCostType||"pct"} options={[["pct","%"],["fixed",currSym()]]} onChange={v=>mktOps.update(selectedMkt.id,{adCostType:v})} color="#BF5AF2"/>
                        {(selectedMkt.adCostType||"pct")==="pct"
                          ? <input type="number" value={selectedMkt.adCostRate?Math.round(selectedMkt.adCostRate*100):""} placeholder="0" onChange={e=>{const v=parseFloat(e.target.value)||0; mktOps.update(selectedMkt.id,{adCostRate:v/100,adCostType:"pct"});}} style={{ background:T.surface,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:"#BF5AF2",fontSize:14,fontWeight:700,padding:"4px 8px",width:50,outline:"none",fontFamily:FONT,textAlign:"center" }}/>
                          : <input type="number" value={selectedMkt.adCostAmount||""} placeholder="0" onChange={e=>{const v=parseFloat(e.target.value)||0; mktOps.update(selectedMkt.id,{adCostAmount:v,adCostType:"fixed"});}} style={{ background:T.surface,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:"#BF5AF2",fontSize:14,fontWeight:700,padding:"4px 8px",width:60,outline:"none",fontFamily:FONT,textAlign:"center" }}/>}
                      </div></div>
                    </Tooltip>
                    {/* Marketplace-level stock override — sticky, ignored by API
                        sync. Sits in the resolution hierarchy at level 2 (after
                        pool override). Uses the same draft + onBlur pattern as
                        Avg COGS so a confirm dialog only fires once on commit. */}
                    <Tooltip content="Marketplace-level stock override. When set, it locks this marketplace's stock to the value below — API syncs (Source c) and per-product values (Source b) are ignored for this marketplace's slice of the pool total. The most recent API total is still captured in the background as 'last sync stock' so you can compare. Leave at 0 to fall back to API → per-product → none.">
 <div style={{ textAlign:"center" }}><div style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.4,marginBottom:3,display:"inline-flex",alignItems:"center",gap:3 }} >Stock override</div><div style={{ display:"flex",alignItems:"center",gap:3 }}>
                        <input type="number"
                          value={mktStockDraft && mktStockDraft.mktId === selectedMkt.id ? mktStockDraft.value : (selectedMkt.currentStockOverride||"")}
                          placeholder="0"
                          onChange={e=>setMktStockDraft({ mktId: selectedMkt.id, value: e.target.value })}
                          onBlur={()=>{
                            if (!mktStockDraft || mktStockDraft.mktId !== selectedMkt.id) return;
                            const newVal = parseInt(mktStockDraft.value) || 0;
                            const oldVal = Number(selectedMkt.currentStockOverride) || 0;
                            if (newVal === oldVal) { setMktStockDraft(null); return; }
                            const commit = () => {
                              mktOps.update(selectedMkt.id, {
                                currentStockOverride: newVal,
                                stockOverridden: newVal > 0,
                              });
                              setMktStockDraft(null);
                              setCogWarning(null);
                            };
                            const cancel = () => { setMktStockDraft(null); setCogWarning(null); };
                            // No warning when clearing the override (going back to 0)
                            if (newVal === 0) { commit(); return; }
                            setCogWarning({
                              title: "Override marketplace stock?",
                              message: `Locking "${selectedMkt.name}" stock to ${newVal.toLocaleString()} units will OVERRIDE every per-product manual stock and the API total for THIS marketplace only. Other marketplaces in the same inventory pool are unaffected.\n\nThis is a sticky override — subsequent API syncs will store new values in 'last sync stock' but won't replace your number until you set this back to 0.\n\nProceed?`,
                              onConfirm: commit,
                              onCancel: cancel,
                            });
                          }}
                          onKeyDown={e=>{ if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setMktStockDraft(null); e.currentTarget.blur(); } }}
                          style={{ background:T.surface,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:selectedMkt.stockOverridden?T.accent:T.text,fontSize:14,fontWeight:700,padding:"4px 8px",width:70,outline:"none",fontFamily:FONT,textAlign:"center" }}/>
                        <span style={{ fontSize:11,color:T.textSub }}>u</span>
                      </div></div>
                    </Tooltip>
                  </div>}
              {/* Channel filter inline panel — expanded from the "Channels"
                  chip in the right side column. Lets the user tick individual
                  sources off so Amazon/eBay orders aren't double-counted once
                  the user connects those marketplaces directly. Apply = re-sync. */}
                  {channelPanelMktId === selectedMkt.id && (() => {
                    const cb = selectedMkt.apiConnection?.channelBreakdown?.[30] || {};
                    const detected = Object.keys(cb).sort((a, b) => (cb[b]?.units || 0) - (cb[a]?.units || 0));
                    const include = Array.isArray(selectedMkt.apiConnection?.includeChannels)
                      ? selectedMkt.apiConnection.includeChannels
                      : detected.slice();
                    const isIncluded = (src) => include.includes(src);
                    const toggle = (src) => {
                      const next = isIncluded(src) ? include.filter(c => c !== src) : [...include, src];
                      mktOps.update(selectedMkt.id, {
                        apiConnection: { ...(selectedMkt.apiConnection || {}), includeChannels: next },
                      });
                    };
                    return (
                      <div style={{ width:"100%", background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.md, padding:"10px 12px", marginTop:4 }}>
                        <div style={{ fontSize:11, color:T.textSub, fontWeight:700, textTransform:"uppercase", letterSpacing:0.3, marginBottom:8, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                          <span>Sales channels in this sync</span>
                          <span style={{ fontSize:10, color:T.textMuted, fontWeight:500, textTransform:"none", letterSpacing:0 }}>
                            Untick any channel you're syncing separately via its own marketplace connection. Shopify surfaces every sales-channel order in one API response; Amazon / eBay / Walmart return only their own orders so this panel only appears on Shopify connections.
                          </span>
                        </div>
                        <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                          {detected.map(src => {
                            const stats = cb[src] || {};
                            const checked = isIncluded(src);
                            return (
                              <label key={src} style={{ display:"flex", alignItems:"center", gap:10, padding:"5px 6px", borderRadius:RADIUS.sm, background: checked ? "transparent" : T.orange+"08", cursor:"pointer", fontSize:12 }}>
                                <input type="checkbox" checked={checked} onChange={() => toggle(src)} style={{ accentColor: T.accent }}/>
                                <span style={{ flex:1, color:T.text, fontWeight:600 }}>{src}</span>
                                <span style={{ color:T.textSub, fontVariantNumeric:"tabular-nums" }}>
                                  {(stats.orders || 0).toLocaleString()} orders · {(stats.units || 0).toLocaleString()} units · {currSym()}{(stats.revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </span>
                                {!checked && <span style={{ fontSize:10, color:T.orange, fontWeight:700, textTransform:"uppercase" }}>Excluded</span>}
                              </label>
                            );
                          })}
                        </div>
                        <div style={{ display:"flex", gap:8, marginTop:10, justifyContent:"flex-end" }}>
                          <button
                            onClick={() => setChannelPanelMktId(null)}
                            style={{ padding:"5px 14px", borderRadius:RADIUS.sm, fontSize:12, fontFamily:FONT, fontWeight:600, cursor:"pointer", background:"transparent", border:`1px solid ${T.border}`, color:T.textSub }}>
                            Close
                          </button>
                          <button
                            onClick={() => { setChannelPanelMktId(null); doSync(selectedMkt.apiConnection?.platform, selectedMkt.id); }}
                            disabled={syncingMktId === selectedMkt.id}
                            style={{ padding:"5px 14px", borderRadius:RADIUS.sm, fontSize:12, fontFamily:FONT, fontWeight:700, cursor:"pointer", background:T.accent+"22", border:`1px solid ${T.accent}55`, color:T.accent }}>
                            {syncingMktId === selectedMkt.id ? "Syncing…" : "Apply & re-sync"}
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Manual fields toggle moved to the right side column. */}
                {/* Brand sharing */}
                {(()=>{
                  const allBrands = getStoredBrands();
                  const currentBrand = LS.get(LS.BRAND,"main");
                  const otherBrands = allBrands.filter(b=>b.key!==currentBrand);
                  if (!otherBrands.length) return null;
                  const sharedBrands = selectedMkt.sharedBrands||[];
                  return (
                    <div style={{ display:"flex",gap:4,alignItems:"center",flexWrap:"wrap" }}>
                      <span style={{ fontSize:11,color:T.textSub }}>🔗</span>
                      {otherBrands.map(b=>{
                        const isSel=sharedBrands.includes(b.key);
                        return (
                          <Tooltip key={b.key} content={isSel?`Remove from ${b.label}`:`Share with ${b.label}`}>
                          <button
                            onClick={()=>{
                              const next=isSel?sharedBrands.filter(k=>k!==b.key):[...sharedBrands,b.key];
                              mktOps.update(selectedMkt.id,{sharedBrands:next});
                              if(!isSel) writeMarketplaceToBrands({...selectedMkt,sharedBrands:next},[b.key]);
                            }}
                            style={{ padding:"2px 8px",borderRadius:RADIUS.pill,fontSize:11,fontFamily:FONT,cursor:"pointer",
                              background:isSel?T.blue+"18":"transparent",color:isSel?T.blue:T.textSub,
                              border:`1px solid ${isSel?T.blue:T.border}`,fontWeight:isSel?700:400 }}>
                            {isSel?"✓ ":""}{b.label}
                          </button>
                          </Tooltip>
                        );
                      })}
                    </div>
                  );
                })()}
              {/* Impact summary moved inline into the marketplace name row above */}

              {/* ── Cash-blocked KPIs (Phase 3 of marketplaces redesign) ── */}
              {isGlobal && (() => {
                // Inventory blocked: sum of (units × landed cost) for products in this marketplace's pool.
                // Per-product stock now follows the resolution hierarchy:
                // manual currentStock → API apiStock → 0. The pool/marketplace
                // override levels don't fan out per-SKU so they're not used here.
                const products = selectedMkt.products || [];
                let inventoryBlocked = 0;
                for (const p of products) {
                  if (p._decoupled) continue;
                  const stock = Number(p.currentStock) || Number(p.apiStock) || 0;
                  const cogs = p.cogs || {};
                  const landed = (Number(cogs.productCost)||0) + (Number(cogs.shipping)||0)
                               + (Number(cogs.customs)||0) + (Number(cogs.prep)||0);
                  inventoryBlocked += stock * landed;
                }
                let orderCashBlocked = 0;
                for (const o of (orders || [])) {
                  if (o.completed) continue;
                  const linkedMktIds = o.marketplaceIds || [];
                  if (linkedMktIds.length === 0 || !linkedMktIds.includes(selectedMkt.id)) continue;
                  // Crude: total amount × fraction not yet arrived
                  const batches = o.containerBatches || [];
                  const totalCt = batches.reduce((s,b)=>s+(Number(b.containers)||0), 0) || 1;
                  const arrivedCt = batches.reduce((s,b)=>{
                    if (!b.arrivalDate) return s;
                    const [d,m,y] = b.arrivalDate.split(".").map(Number);
                    if (!d||!m||!y) return s;
                    return new Date(y,m-1,d) <= new Date() ? s+(Number(b.containers)||0) : s;
                  }, 0);
                  const undeliveredFraction = Math.max(0, 1 - (arrivedCt / totalCt));
                  // Split by linked marketplace count
                  orderCashBlocked += (Number(o.amount)||0) * undeliveredFraction / linkedMktIds.length;
                }
                const reserves = Number(selectedMkt.apiConnection?.reservesHeld) || Number(selectedMkt.reserveHeld) || 0;
                if (inventoryBlocked === 0 && orderCashBlocked === 0 && reserves === 0) return null;
                return (
                  <div style={{
                    display:"flex", gap:10, padding:"10px 18px", marginBottom:14, marginTop:6,
                    flexWrap:"wrap",
                  }}>
                    {inventoryBlocked > 0 && (
                      <div style={{
                        flex:"1 1 180px",
                        padding:"10px 14px",
                        background:T.surface, border:`1px solid ${T.border}`,
                        borderRadius:RADIUS.md,
                      }}>
                        <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:4 }}>
                          <span style={{ fontSize:9, color:T.textSub, fontWeight:700, textTransform:"uppercase", letterSpacing:0.4 }}>📦 Cash blocked in stock</span>
                          <Info tip="cashBlockedInventory"/>
                        </div>
                        <div style={{ fontSize:18, fontWeight:800, color:T.orange }}>{currSym()}{Math.round(inventoryBlocked).toLocaleString()}</div>
                      </div>
                    )}
                    {orderCashBlocked > 0 && (
                      <div style={{
                        flex:"1 1 180px",
                        padding:"10px 14px",
                        background:T.surface, border:`1px solid ${T.border}`,
                        borderRadius:RADIUS.md,
                      }}>
                        <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:4 }}>
                          <span style={{ fontSize:9, color:T.textSub, fontWeight:700, textTransform:"uppercase", letterSpacing:0.4 }}>🚢 Cash projected in orders</span>
                          <Info tip="cashBlockedOrders"/>
                        </div>
                        <div style={{ fontSize:18, fontWeight:800, color:T.orange }}>{currSym()}{Math.round(orderCashBlocked).toLocaleString()}</div>
                      </div>
                    )}
                    {/* Reserves display moved to carousel cards */}
                  </div>
                );
              })()}

              {/* ── Sync skeleton — shows while we're pulling data from the platform ── */}
              {syncingMktId === selectedMkt.id && (
                <div style={{
                  padding:"16px 20px", background:T.surface, border:`1px solid ${T.border}`,
                  borderRadius:RADIUS.md, marginBottom:14, marginTop:10,
                  display:"flex", gap:14, alignItems:"center"
                }}>
                  <div style={{
                    width:42, height:42, borderRadius:12,
                    background:"linear-gradient(135deg,#8b5cf6,#06b6d4)",
                    boxShadow:"0 4px 16px rgba(139,92,246,0.4)",
                    animation:"chSpin 1.2s linear infinite",
                    flexShrink:0
                  }}/>
                  <div style={{ flex:1, display:"flex", flexDirection:"column", gap:6 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:T.text }}>
                      Syncing {(selectedMkt.apiConnection?.platform || "marketplace")}…
                    </div>
                    <div style={{ fontSize:11, color:T.textSub, marginBottom:4 }}>
                      Pulling products, inventory, orders & payouts. Your KPIs will update automatically.
                    </div>
                    <SkeletonBlock w="80%" h={8}/>
                    <SkeletonBlock w="60%" h={8}/>
                  </div>
                </div>
              )}

              {/* ── Sync result toast — last sync summary or error ── */}
              {syncSummary && syncingMktId !== selectedMkt.id && (() => {
                // Normalise summary across platforms:
                //   Shopify returns { products, inventory, orders, payouts, errors }
                //   Walmart  returns { orders, inventory, settlements, pages, errors }
                //   Amazon/eBay will follow the same shape.
                // We render any count > 0 and skip the rest so the line is tight.
                const S = syncSummary.summary || {};
                const partial = syncSummary.partial === true;
                const parts = [];
                const add = (v, label) => {
                  if (v && v > 0) parts.push(`${v} ${label}`);
                };
                add(S.products, "products");
                add(S.inventory, "inventory snapshots");
                add(S.orders, "orders");
                add(S.payouts, "payouts");
                add(S.settlements, "settlements");
                const errs = (S.errors || [])
                  .map(e => (typeof e === "string" ? e : (e?.message || JSON.stringify(e))))
                  .filter(Boolean);
                // Colour: red only on hard failure (!ok && no summary). Partial = orange, success = green.
                const tone = syncSummary.ok === false
                  ? { bg: T.red + "12", border: T.red + "30", fg: T.red }
                  : partial
                    ? { bg: T.orange + "12", border: T.orange + "30", fg: T.orange }
                    : { bg: T.green + "10", border: T.green + "30", fg: T.green };
                return (
                  <div style={{
                    padding: "12px 16px", marginBottom: 14, marginTop: 10,
                    borderRadius: RADIUS.md, fontSize: 12, lineHeight: 1.5,
                    background: tone.bg,
                    border: `1px solid ${tone.border}`,
                    color: tone.fg,
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                  }}>
                    <div>
                      {syncSummary.ok === false && !S ? (
                        <><strong>Sync failed:</strong> {syncSummary.error || "Unknown error"}</>
                      ) : (
                        <>
                          <strong>
                            {partial ? "Sync completed with warnings:" : "Sync complete:"}
                          </strong>{" "}
                          {parts.length > 0 ? parts.join(" · ") : "no new data"}
                          {errs.length > 0 && (
                            <div style={{ color: T.orange, marginTop: 4, fontSize: 11 }}>
                              ⚠ {errs.join(" · ")}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <button onClick={()=>setSyncSummary(null)} style={{
                      background:"transparent", border:"none", color:"inherit",
                      cursor:"pointer", fontSize:14, padding:"0 4px",
                    }}>✕</button>
                  </div>
                );
              })()}

              {/* Manual fields toggle / Share window / Recompute moved into
                  the header action row above. The collapsible panel itself
                  remains below so it can expand to full width. */}

              {/* ── Collapsible manual fields — inline label+input ── */}
              {isGlobal && showManualFields && (
                <div style={{
                  display:"flex", flexWrap:"wrap", gap:"6px 14px",
                  // Compact padding — panel stays in its natural position
                  // below the header row, just with less vertical waste.
                  padding:"6px 14px",
                  marginBottom:10,
                  background:T.surface, border:`1px solid ${T.border}`,
                  borderRadius:RADIUS.md,
                  animation:"sfFadeIn 0.18s ease both",
                  alignItems:"center",
                }}>
                  {/* Each field: label + input on the same line, wrapped in a flex item */}
                  <Tooltip content="What percentage of your total business revenue comes from this marketplace?">
                    <div style={{ display:"flex",alignItems:"center",gap:5,flexShrink:0 }}>
                      <span style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.3,fontWeight:600,whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:3 }}>% Total <Info tip="pctOfTotal"/>{selectedMkt._overridden?.pctOfTotal && selectedMkt._apiSnapshot?.pctOfTotal !== undefined && <button onClick={()=>resetMktField(selectedMkt,"pctOfTotal")} title="Reset to API value" style={{ padding:"1px 5px", fontSize:8, fontWeight:700, background:T.green+"15", border:`1px solid ${T.green}40`, borderRadius:RADIUS.pill, color:T.green, fontFamily:FONT, cursor:"pointer" }}>↺</button>}</span>
                      <input type="number" value={Math.round((selectedMkt.pctOfTotal||0)*100)} placeholder="0" onChange={e=>updateMktWithOverrideCheck(selectedMkt,{pctOfTotal:(parseFloat(e.target.value)||0)/100})} style={{ background:T.card,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:selectedMktColor,fontSize:13,fontWeight:700,padding:"4px 6px",width:48,outline:"none",fontFamily:FONT,textAlign:"center" }}/>
                      <span style={{ fontSize:11,color:T.textSub }}>%</span>
                    </div>
                  </Tooltip>
                  <Tooltip content="Days between sale and bank payout. Amazon ~14d, Shopify ~2d.">
                    <div style={{ display:"flex",alignItems:"center",gap:5,flexShrink:0 }}>
                      <span style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.3,fontWeight:600,whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:3 }}>Payout delay <Info tip="mktPayoutDelay"/>{selectedMkt._overridden?.payoutDelay && selectedMkt._apiSnapshot?.payoutDelay !== undefined && <button onClick={()=>resetMktField(selectedMkt,"payoutDelay")} title="Reset to API value" style={{ padding:"1px 5px", fontSize:8, fontWeight:700, background:T.green+"15", border:`1px solid ${T.green}40`, borderRadius:RADIUS.pill, color:T.green, fontFamily:FONT, cursor:"pointer" }}>↺</button>}</span>
                      <input type="number" value={selectedMkt.payoutDelay||""} placeholder="0" onChange={e=>updateMktWithOverrideCheck(selectedMkt,{payoutDelay:parseFloat(e.target.value)||0})} style={{ background:T.card,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:T.text,fontSize:13,fontWeight:700,padding:"4px 6px",width:48,outline:"none",fontFamily:FONT,textAlign:"center" }}/>
                      <span style={{ fontSize:11,color:T.textSub }}>d</span>
                    </div>
                  </Tooltip>
                  <Tooltip content="Reserve held — manual override or API-synced amount.">
                    <div style={{ display:"flex",alignItems:"center",gap:5,flexShrink:0 }}>
                      <span style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.3,fontWeight:600,whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:3 }}>Reserve held{" "}
                        {selectedMkt.apiConnection?.reservesHeld > 0 && !selectedMkt._overridden?.reserveHeld
                          ? <span style={{ fontSize:7, padding:"1px 4px", borderRadius:RADIUS.pill, background:T.green+"18", color:T.green, fontWeight:700 }}>API</span>
                          : selectedMkt._overridden?.reserveHeld
                            ? <span style={{ fontSize:7, padding:"1px 4px", borderRadius:RADIUS.pill, background:T.accent+"18", color:T.accent, fontWeight:700 }}>MANUAL</span>
                            : Number(selectedMkt.reserveRate) > 0
                              ? <span style={{ fontSize:7, padding:"1px 4px", borderRadius:RADIUS.pill, background:T.orange+"18", color:T.orange, fontWeight:700 }}>{Math.round(selectedMkt.reserveRate*100)}%</span>
                              : null}
                      </span>
                      <span style={{ fontSize:11,color:T.textSub }}>{currSym()}</span>
                      <input type="number" value={selectedMkt.reserveHeld||""} placeholder="0" onChange={e=>updateMktWithOverrideCheck(selectedMkt,{reserveHeld:parseFloat(e.target.value)||0})} style={{ background:T.card,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:T.orange,fontSize:13,fontWeight:700,padding:"4px 6px",width:64,outline:"none",fontFamily:FONT,textAlign:"center" }}/>
                    </div>
                  </Tooltip>
                  <Tooltip content="Reserve rate (%) — for platforms without a reserve API. Estimates held amount from recent payouts.">
                    <div style={{ display:"flex",alignItems:"center",gap:5,flexShrink:0 }}>
                      <span style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.3,fontWeight:600,whiteSpace:"nowrap" }}>Reserve rate</span>
                      <input type="number" value={selectedMkt.reserveRate ? Math.round(selectedMkt.reserveRate*100) : ""} placeholder="0" onChange={e=>mktOps.update(selectedMkt.id,{reserveRate:(parseFloat(e.target.value)||0)/100})} style={{ background:T.card,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:T.orange,fontSize:13,fontWeight:700,padding:"4px 6px",width:44,outline:"none",fontFamily:FONT,textAlign:"center" }}/>
                      <span style={{ fontSize:11,color:T.textSub }}>%</span>
                    </div>
                  </Tooltip>
                  <Tooltip content="Marketplace-level COG fallback. Overrides group/product COGS.">
                    <div style={{ display:"flex",alignItems:"center",gap:5,flexShrink:0 }}>
                      <span style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.3,fontWeight:600,whiteSpace:"nowrap" }}>Avg COGS</span>
                      <span style={{ fontSize:11,color:T.textSub }}>{currSym()}</span>
                      <input type="number"
                        value={mktAvgCogsDraft && mktAvgCogsDraft.mktId === selectedMkt.id ? mktAvgCogsDraft.value : (selectedMkt.avgCogs||"")}
                        placeholder="0"
                        onChange={e=>setMktAvgCogsDraft({ mktId: selectedMkt.id, value: e.target.value })}
                        onBlur={()=>{
                          if (!mktAvgCogsDraft || mktAvgCogsDraft.mktId !== selectedMkt.id) return;
                          const newVal = parseFloat(mktAvgCogsDraft.value) || 0;
                          const oldVal = Number(selectedMkt.avgCogs) || 0;
                          if (newVal === oldVal) { setMktAvgCogsDraft(null); return; }
                          const commit = () => { mktOps.update(selectedMkt.id, { avgCogs: newVal }); setMktAvgCogsDraft(null); setCogWarning(null); };
                          const cancel = () => { setMktAvgCogsDraft(null); setCogWarning(null); };
                          setCogWarning({ title:"Override marketplace COG?", message:`Setting "${selectedMkt.name}" Avg COG to ${currSym()}${newVal}/unit will override group/product COGS in THIS marketplace.`, onConfirm:commit, onCancel:cancel });
                        }}
                        onKeyDown={e=>{ if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setMktAvgCogsDraft(null); e.currentTarget.blur(); } }}
                        style={{ background:T.card,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:T.text,fontSize:13,fontWeight:700,padding:"4px 6px",width:60,outline:"none",fontFamily:FONT,textAlign:"center" }}/>
                    </div>
                  </Tooltip>
                  <Tooltip content="Marketplace-level ad cost fallback.">
                    <div style={{ display:"flex",alignItems:"center",gap:5,flexShrink:0 }}>
                      <span style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.3,fontWeight:600,whiteSpace:"nowrap" }}>Ad cost</span>
                      <Seg value={selectedMkt.adCostType||"pct"} options={[["pct","%"],["fixed",currSym()]]} onChange={v=>mktOps.update(selectedMkt.id,{adCostType:v})} color="#BF5AF2"/>
                      {(selectedMkt.adCostType||"pct")==="pct"
                        ? <input type="number" value={selectedMkt.adCostRate?Math.round(selectedMkt.adCostRate*100):""} placeholder="0" onChange={e=>{const v=parseFloat(e.target.value)||0; mktOps.update(selectedMkt.id,{adCostRate:v/100,adCostType:"pct"});}} style={{ background:T.card,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:"#BF5AF2",fontSize:13,fontWeight:700,padding:"4px 6px",width:44,outline:"none",fontFamily:FONT,textAlign:"center" }}/>
                        : <input type="number" value={selectedMkt.adCostAmount||""} placeholder="0" onChange={e=>{const v=parseFloat(e.target.value)||0; mktOps.update(selectedMkt.id,{adCostAmount:v,adCostType:"fixed"});}} style={{ background:T.card,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:"#BF5AF2",fontSize:13,fontWeight:700,padding:"4px 6px",width:54,outline:"none",fontFamily:FONT,textAlign:"center" }}/>}
                    </div>
                  </Tooltip>
                  <Tooltip content="Marketplace-level stock override. Locks stock — API syncs won't overwrite.">
                    <div style={{ display:"flex",alignItems:"center",gap:5,flexShrink:0 }}>
                      <span style={{ fontSize:10,color:T.textSub,textTransform:"uppercase",letterSpacing:0.3,fontWeight:600,whiteSpace:"nowrap" }}>Stock</span>
                      <input type="number"
                        value={mktStockDraft && mktStockDraft.mktId === selectedMkt.id ? mktStockDraft.value : (selectedMkt.currentStockOverride||"")}
                        placeholder="0"
                        onChange={e=>setMktStockDraft({ mktId: selectedMkt.id, value: e.target.value })}
                        onBlur={()=>{
                          if (!mktStockDraft || mktStockDraft.mktId !== selectedMkt.id) return;
                          const newVal = parseInt(mktStockDraft.value) || 0;
                          const oldVal = Number(selectedMkt.currentStockOverride) || 0;
                          if (newVal === oldVal) { setMktStockDraft(null); return; }
                          const commit = () => { mktOps.update(selectedMkt.id, { currentStockOverride: newVal, stockOverridden: newVal > 0 }); setMktStockDraft(null); setCogWarning(null); };
                          const cancel = () => { setMktStockDraft(null); setCogWarning(null); };
                          if (newVal === 0) { commit(); return; }
                          setCogWarning({ title:"Override marketplace stock?", message:`Locking "${selectedMkt.name}" stock to ${newVal.toLocaleString()} units.`, onConfirm:commit, onCancel:cancel });
                        }}
                        onKeyDown={e=>{ if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { setMktStockDraft(null); e.currentTarget.blur(); } }}
                        style={{ background:T.card,border:`1px solid ${selectedMktColor+"44"}`,borderRadius:RADIUS.sm,color:selectedMkt.stockOverridden?T.accent:T.text,fontSize:13,fontWeight:700,padding:"4px 6px",width:60,outline:"none",fontFamily:FONT,textAlign:"center" }}/>
                      <span style={{ fontSize:11,color:T.textSub }}>u</span>
                    </div>
                  </Tooltip>
                </div>
              )}

              {/* API Connection status + actions have been hoisted into the
                  marketplace header row (Import / Add Product / Launch
                  Product). No separate panel here anymore. */}

              {/* ── Selection + Grouping toolbar ── */}
              {selectedMkt.products.length > 1 && (() => {
                const allFilteredIds = filteredProducts.map(p => p.id);
                const allFilteredSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedProductIdsSet.has(id));
                return (
                <div style={{
                  display:"flex", alignItems:"center", gap:10,
                  padding:"8px 14px", marginTop:20, marginBottom:10,
                  background:T.surface, border:`1px solid ${T.border}`,
                  borderRadius:RADIUS.md, flexWrap:"wrap",
                }}>
                  <span style={{ fontSize:11, color:T.textSub, fontWeight:700, textTransform:"uppercase", letterSpacing:0.4 }}>
                    {selectedProductIds.length > 0 ? `${selectedProductIds.length} selected` : "Bulk actions"}
                  </span>
                  {/* Select all / Clear */}
                  <button
                    onClick={() => {
                      if (allFilteredSelected) {
                        const filteredSet = new Set(allFilteredIds);
                        setSelectedProductIds(prev => prev.filter(id => !filteredSet.has(id)));
                      } else {
                        toggleSelectMany(allFilteredIds, true);
                      }
                    }}
                    style={{ padding:"5px 10px", borderRadius:RADIUS.md, fontSize:11, fontWeight:700, fontFamily:FONT, background: allFilteredSelected ? T.accent+"22" : "transparent", border:`1px solid ${T.accent}55`, color:T.accent, cursor:"pointer" }}
                    title={allFilteredSelected ? "Clear selection of visible products" : "Select all visible products"}
                  >
                    {allFilteredSelected ? "✓ All selected" : `Select all (${allFilteredIds.length})`}
                  </button>
                  {/* Sort — always visible, compact select matching toolbar style */}
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ fontSize:10, color:T.textSub, fontWeight:600, letterSpacing:0.3, textTransform:"uppercase", whiteSpace:"nowrap" }}>
                      Sort
                    </span>
                    <select
                      value={productSort}
                      onChange={e => setProductSort(e.target.value)}
                      title="Sort products by share, price, or recent unit sales"
                      style={{
                        background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm,
                        color:T.text, fontSize:11, fontWeight:600, fontFamily:FONT,
                        padding:"4px 8px", outline:"none", cursor:"pointer",
                      }}
                    >
                      <option value="default">Default</option>
                      <option value="pct_desc">% share · high → low</option>
                      <option value="pct_asc">% share · low → high</option>
                      <option value="price_desc">Price · high → low</option>
                      <option value="price_asc">Price · low → high</option>
                      <option value="sales_desc">Sales · high → low</option>
                      <option value="sales_asc">Sales · low → high</option>
                      <option value="stock_desc">Stock · high → low</option>
                      <option value="stock_asc">Stock · low → high</option>
                      <option value="stockout_asc">Stockout · soonest first</option>
                      <option value="stockout_desc">Stockout · latest first</option>
                      <option value="coupled_first">Status · coupled first</option>
                      <option value="decoupled_first">Status · decoupled first</option>
                    </select>
                  </div>
                  {/* Primary identity toggle — swap big-line ↔ small-chip
                      between product name and SKU. Arrow icon hints at
                      "switch these two". Persists per user. */}
                  <Tooltip content={primaryIdentity === "sku"
                    ? "Showing SKU as the main identity. Click to show product NAME first, SKU as a chip."
                    : "Showing product NAME as the main identity. Click to show SKU first, name as a chip."}>
                    <button
                      onClick={() => setPrimaryIdentity(p => p === "sku" ? "name" : "sku")}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "4px 10px", borderRadius: RADIUS.sm,
                        background: "transparent", border: `1px solid ${T.border}`,
                        color: T.textSub, fontSize: 11, fontWeight: 700,
                        fontFamily: FONT, cursor: "pointer", whiteSpace: "nowrap",
                      }}
                    >
                      <span style={{ fontSize: 10, fontFamily: "monospace" }}>
                        {primaryIdentity === "sku" ? "SKU" : "Name"}
                      </span>
                      <span style={{ fontSize: 10, opacity: 0.7 }}>↕</span>
                      <span style={{ fontSize: 10, fontFamily: "monospace", opacity: 0.6 }}>
                        {primaryIdentity === "sku" ? "Name" : "SKU"}
                      </span>
                    </button>
                  </Tooltip>
                  {selectedProductIds.length > 0 && (
                    <>
                      <button
                        onClick={() => {
                          setPromptDialog({
                            title: "Create group",
                            message: "Group related products together to manage shared COGS and ad cost.",
                            placeholder: "e.g. Hero SKU family",
                            defaultValue: "",
                            onConfirm: (name) => {
                              const updated = createGroup(selectedMkt, name, selectedProductIds);
                              mktOps.update(selectedMkt.id, { productGroups: updated.productGroups });
                              setExpandedGroupIds(prev => [...prev, (updated.productGroups[updated.productGroups.length - 1] || {}).id].filter(Boolean));
                              setSelectedProductIds([]);
                              setPromptDialog(null);
                            },
                          });
                        }}
                        style={{ padding:"5px 12px", borderRadius:RADIUS.md, fontSize:11, fontWeight:700, fontFamily:FONT, background:T.accent+"15", border:`1px solid ${T.accent}40`, color:T.accent, cursor:"pointer" }}>
                        + Create group from selected
                      </button>
                      <button
                        onClick={async () => {
                          const count = selectedProductIds.length;
                          const ok = await showConfirm({
                            icon: "🗑", title: `Delete ${count} product${count===1?"":"s"}?`,
                            message: `This cannot be undone. Removes the selected products from "${selectedMkt.name}" and any groups they belong to.`,
                            confirmLabel: "Yes, Delete", danger: true,
                          });
                          if (!ok) return;
                          // Bulk: remove products + clean group memberships + recompute shares across brand
                          const survivingProducts = selectedMkt.products.filter(p => !selectedProductIds.includes(p.id));
                          const cleanedGroups = (selectedMkt.productGroups || [])
                            .map(g => ({ ...g, productIds: (g.productIds||[]).filter(id => !selectedProductIds.includes(id)) }))
                            .filter(g => (g.productIds||[]).length > 0);
                          const recomputed = recomputeProductShares(survivingProducts);
                          // If no products left, unlock pctOfTotal so recompute can set it to 0
                          const mktPatch = { products: recomputed, productGroups: cleanedGroups };
                          if (recomputed.filter(p=>!p._decoupled).length === 0) {
                            mktPatch._overridden = { ...(selectedMkt._overridden||{}), pctOfTotal: false };
                            mktPatch.pctOfTotal = 0;
                          }
                          const updatedMkts = marketplaces.map(m => m.id === selectedMkt.id
                            ? { ...m, ...mktPatch }
                            : m);
                          const finalMkts = recomputeMarketplaceShares(updatedMkts);
                          mktOps.setMarketplacesAll?.(finalMkts);
                          setSelectedProductIds([]);
                        }}
                        style={{ padding:"5px 12px", borderRadius:RADIUS.md, fontSize:11, fontWeight:700, fontFamily:FONT, background:T.red+"15", border:`1px solid ${T.red}55`, color:T.red, cursor:"pointer" }}>
                        🗑 Delete selected
                      </button>
                      <button
                        onClick={() => setSelectedProductIds([])}
                        style={{ padding:"5px 10px", borderRadius:RADIUS.md, fontSize:11, fontFamily:FONT, background:"transparent", border:`1px solid ${T.border}`, color:T.textSub, cursor:"pointer" }}>
                        Clear
                      </button>
                    </>
                  )}
                  {selectedProductIds.length === 0 && (selectedMkt.productGroups || []).length > 0 && (
                    <span style={{ fontSize:11, color:T.textMuted }}>
                      {(selectedMkt.productGroups || []).length} group{(selectedMkt.productGroups || []).length === 1 ? "" : "s"} — click a parent row to expand
                    </span>
                  )}
                  <Info tip="productGroups"/>
                </div>
                );
              })()}

              {/* Product search */}
              {selectedMkt.products.length>0&&<div style={{ position:"relative",marginBottom:14 }}><span style={{ position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:13,color:T.textSub,pointerEvents:"none" }}>🔍</span><input value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} placeholder="Search products…" style={{ width:"100%",background:"transparent",border:`1.5px solid ${searchQuery?selectedMktColor:T.border}`,borderRadius:RADIUS.md,color:T.text,padding:"9px 12px 9px 36px",fontSize:13,outline:"none",fontFamily:FONT,boxSizing:"border-box",transition:`border-color 0.18s ${SPRING}` }}/>{searchQuery&&<button onClick={()=>setSearchQuery("")} style={{ position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:T.textSub,cursor:"pointer",fontSize:13 }}>✕</button>}</div>}

              {/* Products */}
              {selectedMkt.products.length===0 ? (
                <div style={{ display:"flex", alignItems:"center", gap:12, padding:"16px 20px", background:T.surface, border:`1px dashed ${selectedMktColor}44`, borderRadius:RADIUS.md }}>
                  <span style={{ fontSize:24, opacity:0.5 }}>📦</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:T.text, marginBottom:2 }}>No products yet</div>
                    <div style={{ fontSize:11, color:T.textSub }}>Add your first product to start tracking income</div>
                  </div>
                  <Btn onClick={openLaunchModal} color={selectedMktColor} size="sm">+ Add Product</Btn>
                </div>
              ) :(() => {
                // Group + ungrouped layout is hoisted to a useMemo above so this
                // doesn't recompute on every render.
                const { groupRows, ungroupedProducts } = productLayout;
                const totalShown = groupRows.length + ungroupedProducts.length;

                const handleConfirmDeleteProduct = (prod) => setConfirmDelete({type:"prod",mktId:selectedMkt.id,prodId:prod.id,name:prod.name||"this product"});
                const handleDecoupleProduct = async (prod) => {
                  const isDecoupling = !prod._decoupled;
                  if (isDecoupling) {
                    const ok = await showConfirm({
                      icon: "🔗", title: `Decouple "${prod.name||"this product"}"?`,
                      message: "This product will be EXCLUDED from every calculation: income projections, inventory pools, stockout date, the Dashboard chart, the AI assistant, and the Cash Calendar.\n\nYou can recouple it any time.",
                      confirmLabel: "Decouple", danger: true,
                    });
                    if (!ok) return;
                  }
                  const updatedMkt = decoupleProduct(selectedMkt, prod.id, isDecoupling);
                  const updatedMkts = marketplaces.map(m => m.id === selectedMkt.id ? { ...m, products: updatedMkt.products } : m);
                  const finalMkts = recomputeMarketplaceShares(updatedMkts);
                  mktOps.setMarketplacesAll?.(finalMkts);
                };
                const handleConfirmDeleteGroup = async (g) => {
                  const ok = await showConfirm({
                    icon: "📦", title: `Ungroup "${g.name}"?`,
                    message: `This only removes the grouping — the ${(g.productIds||[]).length} products inside stay.`,
                    confirmLabel: "Ungroup", danger: false,
                  });
                  if (ok) {
                    const updated = deleteGroup(selectedMkt, g.id);
                    mktOps.update(selectedMkt.id, { productGroups: updated.productGroups });
                  }
                };

                return (
                <div data-tour="mkt-product-list" style={{ display:"flex",flexDirection:"column",gap:8 }}>
                  {/* Group parent rows */}
                  {groupRows.map(({ group, children }) => (
                    <GroupRow
                      key={group.id}
                      group={group} mkt={selectedMkt} isGlobal={isGlobal}
                      mktOps={mktOps} monthOps={monthOps}
                      settingsMonth={settingsMonth}
                      setLastEditedId={setLastEditedId} lastEditedId={lastEditedId}
                      saveStatus={saveStatus}
                      T={T} globalSimpleMode={globalSimpleMode}
                      isExpanded={expandedGroupIdsSet.has(group.id)}
                      onToggleExpand={() => toggleGroupExpand(group.id)}
                      childProducts={children}
                      selectedProductIds={selectedProductIds}
                      selectedProductIdsSet={selectedProductIdsSet}
                      onToggleSelectMany={toggleSelectMany}
                      onConfirmDeleteGroup={handleConfirmDeleteGroup}
                      onConfirmDeleteProduct={handleConfirmDeleteProduct}
                      onDecoupleProduct={handleDecoupleProduct}
                      showConfirm={showConfirm}
                      productsWithMonthOverrides={productsWithMonthOverrides}
                      globalMkt={marketplaces.find(m=>m.id===selectedMkt.id)}
                      salesWindow={salesWindow}
                      leadTime={effectiveLeadTime}
                      realizedPayoutMap={realizedPayoutMap}
                      primaryIdentity={primaryIdentity}
                      onEditProduct={(prod) => onNavigate?.("catalog", { highlightProductId: prod.id, highlightMktId: selectedMkt.id, expandOnArrival: true, ts: Date.now() })}
                      onToggleProductSync={(prod, nextSyncOn) => mktOps.updateProduct?.(selectedMkt.id, prod.id, { _decoupled: !nextSyncOn })}
                    />
                  ))}
                  {/* Ungrouped products — Phase 7 slim rows. The heavy
                      inline pricing editor moves to the Catalog page;
                      Markets is a dashboard. */}
                  {ungroupedProducts.map(prod => (
                    <SlimProductRow
                      key={prod.id}
                      prod={prod} mkt={selectedMkt} T={T}
                      isSelected={selectedProductIdsSet.has(prod.id)}
                      onToggleSelect={toggleSelectOne}
                      selectMode={selectedProductIds.length > 0 || true}
                      primaryIdentity={primaryIdentity}
                      onToggleSync={(nextSyncOn) => mktOps.updateProduct?.(selectedMkt.id, prod.id, { _decoupled: !nextSyncOn })}
                      onEdit={() => onNavigate?.("catalog", { highlightProductId: prod.id, highlightMktId: selectedMkt.id, expandOnArrival: true, ts: Date.now() })}
                    />
                  ))}
                  {totalShown===0 && searchQuery && <div style={{ textAlign:"center",padding:"24px",color:T.textSub,fontSize:13 }}>No products matching "{searchQuery}"</div>}
                </div>
                );
              })()}
            </div>
          )}
        </>
        )}
        </>
      )}

      {/* Connect API BottomSheet */}
      <BottomSheet open={!!connectSheet} onClose={()=>setConnectSheet(null)}
        title={connectSheet ? `Connect ${PLATFORMS[connectSheet.platform]?.name||connectSheet.platform}` : "Connect API"}
        subtitle={connectSheet ? PLATFORMS[connectSheet.platform]?.description : ""}
        height="auto" maxWidth={480}>
        {connectSheet && (() => {
          const plat = connectSheet.platform;
          const platInfo = PLATFORMS[plat];
          const needsDomain = plat === "shopify";
          const needsKeys = plat === "walmart";
          const isOAuthOnly = plat === "amazon" || plat === "ebay";

          const canSubmit = needsDomain ? connectInput.shopDomain.trim().length > 0
            : needsKeys ? connectInput.clientId.trim().length > 0 && connectInput.clientSecret.trim().length > 0
            : true;

          const handleConnect = async () => {
            setConnecting(true);
            try {
              const {data:{session:s}} = await supabase.auth.getSession();
              const token = s?.access_token;
              if (needsDomain) {
                let domain = connectInput.shopDomain.trim();
                if (!domain.includes(".")) domain += ".myshopify.com";
                mktOps.update(connectSheet.mktId, { apiConnection: { ...defaultApiConnection(), platform: plat, shopDomain: domain } });
                await initiateAuth(plat, connectSheet.mktId, token, { shopDomain: domain });
              } else if (needsKeys) {
                mktOps.update(connectSheet.mktId, { apiConnection: { ...defaultApiConnection(), platform: plat, connected: true, sellerId: connectInput.clientId.trim() } });
                setConnectSheet(null);
              } else {
                mktOps.update(connectSheet.mktId, { apiConnection: { ...defaultApiConnection(), platform: plat } });
                await initiateAuth(plat, connectSheet.mktId, token);
              }
            } catch (e) {
              console.error("Connect error:", e);
            } finally {
              setConnecting(false);
            }
          };

          const inputStyle = { width:"100%", background:"transparent", border:`1px solid ${T.border}`, borderRadius:RADIUS.md, color:T.text, padding:"10px 14px", fontSize:13, outline:"none", fontFamily:FONT, boxSizing:"border-box" };

          return (
            <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px", background:platInfo?.color+"0e", borderRadius:RADIUS.md, border:`1px solid ${platInfo?.color}22` }}>
                <span style={{ fontSize:24 }}>{platInfo?.icon}</span>
                <div>
                  <div style={{ fontSize:14, fontWeight:600, color:T.text }}>{platInfo?.name}</div>
                  <div style={{ fontSize:12, color:T.textSub }}>Scopes: {platInfo?.scopes?.join(", ")}</div>
                </div>
              </div>

              {needsDomain && (
                <div>
                  <label style={{ fontSize:12, fontWeight:600, color:T.textSub, marginBottom:6, display:"block" }}>Store Domain</label>
                  <input value={connectInput.shopDomain} onChange={e => setConnectInput(p=>({...p, shopDomain:e.target.value}))}
                    placeholder="your-store.myshopify.com" style={inputStyle}
                    onKeyDown={e => e.key === "Enter" && canSubmit && handleConnect()} />
                  <div style={{ fontSize:11, color:T.textSub, marginTop:4 }}>Enter your Shopify store domain (e.g. my-store or my-store.myshopify.com)</div>
                </div>
              )}

              {needsKeys && (
                <>
                  <div>
                    <label style={{ fontSize:12, fontWeight:600, color:T.textSub, marginBottom:6, display:"block" }}>Client ID</label>
                    <input value={connectInput.clientId} onChange={e => setConnectInput(p=>({...p, clientId:e.target.value}))}
                      placeholder="Walmart Client ID" style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ fontSize:12, fontWeight:600, color:T.textSub, marginBottom:6, display:"block" }}>Client Secret</label>
                    <input value={connectInput.clientSecret} onChange={e => setConnectInput(p=>({...p, clientSecret:e.target.value}))}
                      placeholder="Walmart Client Secret" type="password" style={inputStyle} />
                  </div>
                  <div style={{ fontSize:11, color:T.textSub }}>Find these in your Walmart Developer Portal under API Keys.</div>
                </>
              )}

              {isOAuthOnly && (
                <div style={{ fontSize:13, color:T.textSub, lineHeight:1.5 }}>
                  Clicking Connect will redirect you to {platInfo?.name} to authorize CashHorizon. You'll be brought back automatically after granting access.
                </div>
              )}

              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:4 }}>
                <button onClick={()=>setConnectSheet(null)}
                  style={{ padding:"8px 18px", borderRadius:RADIUS.sm, fontSize:13, fontFamily:FONT, cursor:"pointer", background:"transparent", border:`1px solid ${T.border}`, color:T.textSub }}>Cancel</button>
                <button onClick={handleConnect} disabled={!canSubmit || connecting}
                  style={{ padding:"8px 18px", borderRadius:RADIUS.sm, fontSize:13, fontFamily:FONT, cursor:"pointer", background:canSubmit && !connecting ? platInfo?.color : T.border, border:"none", color:"#fff", fontWeight:600, opacity:canSubmit && !connecting ? 1 : 0.5 }}>
                  {connecting ? "Connecting…" : "Connect"}
                </button>
              </div>
            </div>
          );
        })()}
      </BottomSheet>

      {/* Floating back-to-top — ALWAYS visible, bottom-center of viewport.
          Tries to scroll the <main id="main-content"> container first, then
          falls back to window + document.documentElement. Uses z-index 9999
          (BottomSheet modals use 10000+ so this still hides behind them). */}
      <button
        onClick={() => {
          const mainEl = typeof document !== "undefined" ? document.getElementById("main-content") : null;
          if (mainEl) {
            mainEl.scrollTo({ top: 0, behavior: "smooth" });
            return;
          }
          if (typeof window !== "undefined") {
            window.scrollTo({ top: 0, behavior: "smooth" });
            if (document.documentElement) document.documentElement.scrollTop = 0;
          }
        }}
        aria-label="Back to top"
        title="Back to top"
        style={{
          position: "fixed",
          left: "50%", bottom: 20, transform: "translateX(-50%)",
          zIndex: 9999,
          minWidth: 120, height: 40,
          padding: "0 18px", borderRadius: 999,
          background: selectedMktColor || T.accent,
          border: `2px solid ${selectedMktColor || T.accent}`,
          color: "#ffffff",
          cursor: "pointer", fontFamily: FONT, fontSize: 13, fontWeight: 700,
          letterSpacing: 0.3,
          boxShadow: `0 8px 24px ${(selectedMktColor || T.accent) + "66"}, 0 2px 8px rgba(0,0,0,0.35)`,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ fontSize: 16, lineHeight: 1 }}>↑</span>
        <span>Back to top</span>
      </button>

    </div>
  );
}
